/* Crystal Quest quiz engine V3.4 (shared by the well water filter finder and the water softener quiz).
 *
 * One screen at a time, Hims-style: a landing that sells the quiz, chapter openers, one question per screen with
 * stacked answers, full-screen stat, fact and review screens between chapters, an answer playback, a short matching
 * moment and one clean recommendation. Every screen is laid out to fit the viewport without scrolling.
 * Answers stay in this browser tab (sessionStorage). Nothing is sent anywhere except an explicit cart add, the
 * contact form the customer submits, and Shopify customer events (IDs and bands only, never typed numbers or ZIPs).
 *
 * ============================================================================================================
 * PUBLIC API (window.CQQuizV3)
 * ============================================================================================================
 *   CQQuizV3.register(key, definition)   Register a quiz. Mounts every root with data-cqq-quiz="<key>".
 *   CQQuizV3.defineType(name, spec)      Add or replace a screen type (see "Custom screen types" below).
 *   CQQuizV3.init(scope)                 Mount any unmounted roots inside scope (called for you on register and
 *                                        on shopify:section:load).
 *   CQQuizV3.helpers                     {esc, md, plain, number, join, lowerFirst, upperFirst, cleanDashes}
 *   CQQuizV3.instances                   Mounted controllers (harness and debugging): .go(id), .answers(),
 *                                        .path(), .result(), .state(), .reset(), .overflow().
 *
 * ------------------------------------------------------------------------------------------------------------
 * DEFINITION (the object a definition file passes to register)
 * ------------------------------------------------------------------------------------------------------------
 *   {
 *     name:       'Well water filter finder',      used in the contact message and analytics
 *     storageKey: 'cqq:well:v1',                    sessionStorage key (default 'cqq:<key>:v1')
 *     chapters:   [{name:'Your water', line:'What you see, smell and test.',
 *                   image:{src:'cq-v3-h11-tall-windowglass.webp', focus:'50% 55%'}}, ... four in all],
 *     screens:    [ ...screen objects, in flow order... ],
 *     derive(answers, ctx)        optional; returns extra computed values merged into answers before every
 *                                 `when` test and before recommend (e.g. {gpg:15, hardnessInPlay:true}).
 *     recommend(answers, ctx)     returns the RESULT object (below) for the `result` screen.
 *     strings:    {...}           optional overrides for the engine's own words (see STRINGS below).
 *     summary(answers, result, ctx)  optional; plain text added to the contact message (default: every answer).
 *   }
 *
 * Every text field on a screen or result may be a string or a function (answers, ctx) => string.
 * Text is escaped, then this small markup is applied:
 *   *words*        emphasis, the blue second beat of a heading (<em>)
 *   **words**      strong
 *   ==words==      a highlighted phrase (the playback's mark.cqv-q-said)
 *   [label](url)   a link; url must start with /, #, https://, http://, tel: or mailto:. External links open in
 *                  a new tab so the quiz stays put.
 * Em and en dashes are replaced with " to " (red line); keep copy free of them anyway.
 *
 * ------------------------------------------------------------------------------------------------------------
 * SCREEN OBJECTS (common fields)
 * ------------------------------------------------------------------------------------------------------------
 *   id        unique key, also the default answer key and the analytics screen ID
 *   type      landing | chapter | single | multi | input | stat | fact | reaction | testimonial | playback |
 *             loader | result   (or any type added with defineType)
 *   chapter   1 to 4 for chapter, question and interstitial screens (drives the kicker and the progress bar)
 *   when      (answers, ctx) => boolean. The screen is on the customer's path only when this is true.
 *             A conditional follow-up is simply the next screen in the list with a `when`.
 *   variants  interstitials only: [{id:'a', when, ...fields}]. The first variant whose `when` passes is shown
 *             (its fields override the screen's); no match means the screen is skipped.
 *   kicker    optional; question screens default to "01 / 04 · Your water" (+ " · Last question" with last:true)
 *   title     the h1 (two beats: 'Do you have a *water test report?*')
 *   help      the helper line under the question (one line)
 *   image     {src, alt, focus, focusDesktop} or false. Question, input and chapter screens default to their
 *             chapter's image (left half on desktop, hidden on phones for questions, a band on chapter openers).
 *   cta       button label where a screen has one
 *
 * landing     eyebrow, title, lead, checks:[3 strings], cta, note, trust, resumeLabel,
 *             plan:{kicker, items:[{name, line}], position:'top'|'bottom'} (items default to the chapters; the
 *             card shows from 821 px, over the picture's left edge, 32 px from its top or bottom),
 *             image:{src, alt, focus, focusDesktop}
 * chapter     title, lead, cta. The rail (Done / Now / Not needed) is built from the chapters.
 * single      options:[{value, label, help}] (or a function), escape:{label, set, goto} (optional text button).
 *             A tap selects, the row fills, and the next screen arrives 260 ms later.
 * multi       options:[{value, label, help, exclusive:true}], cta ('Continue'), min (1),
 *             emptyLabel + emptyValue: Continue stays enabled with nothing ticked, reads emptyLabel, and saves
 *             [emptyValue] (the "Nothing flagged" pattern). escape as for single.
 *             Stored as an array under `key` (default id).
 * input       fields:[{key, label, unit, units:[{value:'gpg', label:'gpg', factor:1, min:0, max:150},
 *                     {value:'mgl', label:'mg/L', factor:1/17.1, min:0, max:2565}],
 *                     min, max, placeholder, inputmode ('decimal'), autocomplete, maxlength, pattern (RegExp),
 *                     integer:true, text:true (keep as text, e.g. a ZIP)}],
 *             require: 'any' | 'all' (default 'all' for one field, 'any' for a pair),
 *             error: string or (field, answers) => string, fact:{text, source}, cta, skip:{label, set, goto},
 *             lookup(values, answers, ctx) => {ok:true, set:{...}} | {ok:false, message, actions:[{label, set,
 *             goto}]} (may return a Promise; runs on Continue).
 *             Stored per field: answers[key] (number, or text), answers[key+'Raw'] (as typed),
 *             answers[key+'Unit'] and answers[key+'Base'] (converted to the first unit, via factor) with units.
 *             band(answers) optional: an analytics band ('hard', '3-4'); typed values are never sent.
 * stat        kicker, figure, caption, pivot, source, cta           (navy, figure variant)
 * fact        kicker, title, pivot, source, cta                     (navy, sentence as the heading)
 * reaction    kicker, figure, caption, math, pivot, source, cta     (navy, answer reaction with a math line)
 * testimonial kicker ("In a customer's words"), stars (5), quote, pill, name, date:{text, iso},
 *             product:{label, href}, cta                            (sage)
 * playback    kicker, title, reflect (use ==marks==), reflectShort (optional, shown at 560 px of height or
 *             less), because (text after "Because you told us"), becauseLabel, cta, changeLabel
 * loader      kicker, title, steps:[3 strings], proof, skipLabel, stepMs (800). Skipped automatically with
 *             reduced motion or Save-Data. It moves on to the next screen (the result) by itself.
 * result      the definition's recommend() supplies the content (below). `build(answers, ctx)` on the screen
 *             overrides recommend for that screen.
 *
 * Actions that set values: escape, skip and lookup actions take {set:{key:value}, goto:'screenId'}.
 * `set` may change another screen's answer (e.g. {hardKnown:'number'}); `goto` jumps to a screen (default: the
 * next screen on the path). An answer whose screen falls off the path is ignored everywhere (answers are pruned).
 *
 * ------------------------------------------------------------------------------------------------------------
 * RESULT OBJECT (returned by recommend)
 * ------------------------------------------------------------------------------------------------------------
 *   {
 *     id:      'COMPLETE',                       outcome ID (analytics, contact message)
 *     eyebrow: 'Your match',
 *     title:   'Clear and soft, *from one system.*',
 *     media:   {src, alt, focus, focusDesktop, badge}    scene or calm picture (media:{product:true} uses the
 *                                                       selected variant's catalogue photo)
 *     product: {                                 omit for honest outcomes
 *       card:   'complete',                      catalogue card ID (cq-well-v3-catalog + the quiz-only cards)
 *       select: {size:1.5, finish:'Fiberglass', config:'With SMART Filter and Softener', flow:12,
 *                voltage:'110V'},                axis by label or alias; numbers match by leading number, so
 *                                                 "2 Cu ft (60K Grains)" is 2 (bug N1 fix). Other axes keep the
 *                                                 default variant's values. Unavailable falls back to the default.
 *       enable: {uv:true},                       optional components of the card (checkboxes in Configure)
 *       componentSelect: {uv:{voltage:'110V'}},
 *       label:  'Complete system',               breakdown label for the base item
 *       componentLabels: {uv:'UV'},              breakdown labels for enabled components (default: 'UV' for uv)
 *       pairs:  [{card:'uv', select:{flow:12, voltage:'110V'}, label:'UV'}],   extra items in the same cart add
 *       name, nameSuffix, priceLabel             display overrides (priceLabel may be a function of the selected
 *                                                 variant's option values, e.g. o => o[1] + ', ' + o[2] + ' cu ft')
 *     },
 *     nameRow: {title, label, price},            honest outcomes: the name row text (price may be a string)
 *     why:     ['...', '...', '...'],            exactly three
 *     included: 'Included: ...' or {label:'What happens next:', text:'...'},
 *     actions: [{label:'Add to cart', do:'add'}, {label:'Configure', do:'configure'}]
 *              do: add | configure | tel | contact | link (href) | details (tab) | alternative | retake
 *              contact takes {title, subject}; the first action is primary, the second secondary.
 *     note:    '... [One-year limited warranty](/pages/warranty). ...',
 *     noteMore: 'In-stock standard systems ship in less than 3 business days.'  first sentence that hides on
 *              screens 700 px tall or less
 *     sources: 'Sources: ...'                    optional small line (honest outcomes)
 *     details: {
 *       plan:  [{title, text, card, select, enable, optional:true, note}]   Your plan (live price when card)
 *       goodToKnow: ['...'],
 *       alternative: {title:'Prefer zero salt?', text, product:{...as above}},
 *       compare: {label:'Compare with the other systems', href}
 *     },
 *     contactNote: '...'                         optional line at the top of the contact dialog
 *     unavailableDetails: {goodToKnow:[...]}     optional; replaces those Details fields when the configuration turns
 *                                                 out to be unavailable and the engine shows its UNAVAILABLE outcome
 *   }
 *   The engine prices everything live, builds the breakdown line ("RO system $4,724 + UV $569"), fills the
 *   Configure, Details and contact dialogs, and falls back to the UNAVAILABLE outcome when neither the chosen
 *   nor the default configuration can be bought (override with definition.unavailable(result, ctx)).
 *
 * ------------------------------------------------------------------------------------------------------------
 * CTX (second argument to every function above)
 * ------------------------------------------------------------------------------------------------------------
 *   ctx.quiz, ctx.name, ctx.card(id), ctx.config(cardId, {select, enable, componentSelect}) -> {price,
 *   available, items, label, options}, ctx.price(cardId, opts) -> '$1,331', ctx.minPrice(cardId) -> '$159',
 *   ctx.money(n), ctx.label(screenId, value), ctx.labels(screenId, values), ctx.screen(id), ctx.outcome()
 *   (the recommend() result for the current answers), ctx.asset(name), ctx.loadScript(name) -> Promise,
 *   ctx.calm (reduced motion or Save-Data), ctx.join(list), ctx.lowerFirst(s), ctx.upperFirst(s), ctx.path().
 *
 * ------------------------------------------------------------------------------------------------------------
 * CUSTOM SCREEN TYPES (so new kinds of screens can be added without touching this file)
 * ------------------------------------------------------------------------------------------------------------
 *   CQQuizV3.defineType('video', {
 *     screenClass: 'cqq-video',           class added to section.cqv-q
 *     question: false,                    true if it records an answer (counts toward chapter progress)
 *     progress: true,                     show the chapter bar
 *     photo: false,                       use the chapter picture beside it on desktop
 *     render(screen, api) { return '<div class="cqv-q-col">...' + api.foot(api.button(screen.cta)) + '</div>'; },
 *     commit(stage, screen, api) { return {myKey:'value'}; }   optional, runs on Continue
 *   });
 *   api: esc, md, text(v), img(ref, opts), kicker(screen), heading(screen, cls), lead(screen), foot(html),
 *        button(label, do, cls), answers, value(key), strings, chapters, id(suffix).
 *
 * ------------------------------------------------------------------------------------------------------------
 * MARKUP THE SECTION PROVIDES (see sections/cq-well-v3-finder.liquid)
 * ------------------------------------------------------------------------------------------------------------
 *   div#cq-quiz-v3.cqv.cqq[data-cqq-quiz][data-cqq-parent][data-cart-url][data-currency][data-cqq-asset-base]
 *     header.cqv-quiz-header (a.cqv-quiz-back = header Back)      snippets/cq-well-v3-header.liquid
 *     section.cqv-q[data-cqq-screen]                              the engine renders into this
 *     script#cqq-images (JSON name -> {src, w, h}), script#cqq-assets (JSON name -> url)
 *     script#cq-well-v3-catalog, script[data-cqq-catalog] (quiz-only cards)
 *     dialog[data-cqq-contact] with the Shopify contact form        snippets/cq-quiz-v3-dialogs.liquid
 *
 * Debugging: add ?cqq-debug=1 (also on automatically on localhost, in the theme editor and on unpublished theme
 * previews). The fit guard then measures every screen and logs any overflow with console.warn; the last reading
 * is on root.dataset.cqqOverflow and CQQuizV3.instances[0].overflow().
 *
 * ------------------------------------------------------------------------------------------------------------
 * ROUND B ADDITIONS (V3.3, 2026-09-26). Full field list with examples: Brain 50-Staging/Website/Well-Water-V3-
 * Workspace/review-2026-09-20/quiz-build-2026-09-24/round-b-2026-09-26/ENGINE-API.md. In short:
 * ------------------------------------------------------------------------------------------------------------
 *   Background plates: stat, fact and reaction screens sit on the navy plate, testimonials on the light blue plate,
 *     look:'mine' on the royal plate and the matching moment on the ripple plate (cq-v3-q-plate-*). plate:false or
 *     plate:'stat'|'review'|'numbers'|'matching' on a screen overrides it. Save-Data shows the flat colour instead.
 *   look on stat, fact and reaction screens (and their variants); no look keeps today's typographic screen:
 *     'mine'   your number: kicker (inside the card), tag ('Assumed'), figure, unit, caption,
 *              scale:{value, assumed, label} (the USGS scale with a marker) or math:{tiles:[{value, label, assumed,
 *              op}], capacity:{used, total, label, size, unit}}, pivot (inside the card), source (under it)
 *     'photo'  national stat photo card: photo:{src, alt, focus}, kicker, figure, caption (or title), pivot, source
 *     'fact'   good-to-know card: photo:{src, alt, focus}, kicker, title, pivot, source
 *   testimonial: the white review card on light blue; product:{label, href, image} adds the product thumbnail.
 *   chapter: compare:{items:[{name, image, points:[3]}], source} swaps the picture for the compare card.
 *   loader: chips:[...] (or a function) with chipsLabel ('Matched for').
 *   result: media:{scene:'complete'} (4:5 laptop picture, 2:1 phone band, crop computed from the product box),
 *     guarantee:true (Guaranteed Performance in the laptop first view or the mobile card below it), share:false,
 *     emailMatch:false, hubspot:{recommendation, needsTest, source}, details.whatNext (false or overrides),
 *     below:{reviews:[...], included:{...}, report:{...}, closer:false} (no install section: its steps live in
 *     Details > Good to know > What happens next, where details.whatNext.install adds a placement line).
 *   definition: share:{omit:[keys], restore(values)}, hubspot:{quizId, label, source(a)}.
 *   Optional definition.resultMedia(result, {components}, original) can return a configured gallery. Components
 *     include every enabled primary and paired catalogue variant with options, image, title, label, and card ID.
 *     Return the original media to preserve an accurate scene; quizzes without this hook keep existing behavior.
 *   Round C: definition.stainlessScenes lists approved stainless scene twins; all selected tanks must be stainless.
 *     image.fallback is a one-shot image reference for a missing future asset. share.normalize(values) makes
 *     equivalent answers share the same payload without changing the customer's stored choice.
 *     #start and ?start=1 replay the per-quiz session-once logo entrance, then open question 1. It lazy-loads its own
 *     module/layers only at Start; reduced motion, pause, resume and share links bypass it.
 *     Every cart add carries private quiz properties, then best-effort cart attributes.
 *   Motion: preload and decode the next two screens, prepare the new stage beneath a frozen outgoing layer,
 *     wait at most 700 ms for a visible photo card, otherwise 300 ms, then crossfade for 220 ms.
 *     Reduced motion shares the bounded image wait and reveals without a crossfade.
 *   Landing: elements with [data-cqq-more] inside or right after the root show under the landing's first view
 *     (the root scrolls while the landing shows) and hide once the quiz starts.
 *   Share link: #m=<base64url JSON {v:1, q, a[, o, c, skips]}> on the page URL restores the answers and opens the result.
 *   HubSpot: the contact dialog posts to form F1 and "Email me my match" to form F2 when the dialog carries
 *     data-cqq-hs-portal (snippets/cq-quiz-v3-dialogs.liquid); any failure falls back to the Shopify contact form.
 */
(function (host, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) { module.exports = api; return; }
  if (host.CQQuizV3 && host.CQQuizV3.version && host.CQQuizV3.version >= api.version) return;
  host.CQQuizV3 = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const VERSION = '3.6.0';

  /* ============================== helpers ============================== */
  const esc = v => String(v == null ? '' : v).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const cleanDashes = s => String(s == null ? '' : s).replace(/\s+[\u2013\u2014]\s+/g, ', ').replace(/[\u2013\u2014]/g, ' to ');
  const cleanProduct = s => cleanDashes(s).replace(/\s+-\s+/g, ', ');
  const safeUrl = u => /^(\/(?!\/)|#|https?:\/\/|tel:|mailto:)/i.test(String(u || '').trim());
  function md(s) {
    let t = esc(cleanDashes(s));
    t = t.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label, url) => {
      const raw = url.replace(/&amp;/g, '&');
      if (!safeUrl(raw)) return label;
      const ext = /^https?:\/\//i.test(raw);
      return '<a href="' + url + '"' + (ext ? ' target="_blank" rel="noopener"' : '') + '>' + label + '</a>';
    });
    // a highlighted answer of up to 36 characters (it fits one line on a 360 px phone) keeps its words together with
    // no-break spaces; a longer one may wrap on phones (assets/cq-well-v3-quiz.css), so it never runs off the screen
    t = t.replace(/==([^=]+?)==/g, (m, x) => '<mark class="cqv-q-said">' + (/</.test(x) || x.replace(/&[#a-z0-9]+;/gi, '_').length > 36 ? x : x.replace(/ /g, '&nbsp;')) + '</mark>');
    t = t.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    // These reviewed short accents fit at the smallest type tier; other phrases keep their normal wrapping.
    t = t.replace(/\*([^*]+?)\*/g, (m, x) => '<em>' + (/^(hard water|half the detergent|iron and manganese|UV disinfection|had hard water\.?|toilet parts)$/.test(x) ? x.replace(/ /g, '&nbsp;') : x) + '</em>');
    t = t.replace(/\bOne-year\b/g, '<span class="cqq-nw">One-year</span>');
    // Bind only visible text. Product/source URLs can also contain "1-inch" and must remain byte-for-byte intact.
    t = t.split(/(<[^>]*>)/g).map(part => part.charAt(0) === '<' ? part : part
      .replace(/\b(hard water|toilet parts)\b/g, phrase => phrase.replace(/ /g, '\u00a0'))
      .replace(/(\d[\d,.]*) (gpg|mg\/L|GPM|cu ft|gallons)\b/g, (m, n, u) => n + '\u00a0' + u.replace(/ /g, '\u00a0'))
      .replace(/(\d[\d,.]*) to (\d[\d,.]*)\u00a0(gpg|mg\/L|GPM|cu\u00a0ft)\b/g, '$1\u00a0to\u00a0$2\u00a0$3')
      .replace(/Photo illustrative\./g, 'Photo\u00a0illustrative.')
      .replace(/\b(\d[\d./]*)-inch\b/g, '<span class="cqq-nw">$1-inch</span>')).join('');
    return t;
  }
  const plain = s => cleanDashes(s).replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/==|\*\*|\*/g, '');
  const number = v => {
    if (v === null || v === undefined || typeof v === 'boolean') return null;
    const t = String(v).replace(/,/g, '').trim();
    if (t === '' || !/^[-+]?(\d+\.?\d*|\.\d+)$/.test(t)) return null;
    const n = Number(t); return Number.isFinite(n) ? n : null;
  };
  const join = list => { const a = (list || []).filter(Boolean); return a.length < 2 ? (a[0] || '') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]; };
  const lowerFirst = s => { s = String(s || ''); return /^(UV|RO|TDS|ZIP|pH|ISO|USGS|EPA|I\b)/.test(s) ? s : s.charAt(0).toLowerCase() + s.slice(1); };
  const upperFirst = s => { s = String(s || ''); return s.charAt(0).toUpperCase() + s.slice(1); };
  const arr = v => Array.isArray(v) ? v : (v == null ? [] : [v]);
  const helpers = {esc, md, plain, number, join, lowerFirst, upperFirst, cleanDashes};

  const STRINGS = {
    back: 'Back', backPrev: 'Back to the previous screen', continue: 'Continue', start: 'Start', resume: 'Pick up where you left off',
    startMoment: "Let's find *your match*.",
    lastQuestion: 'Last question', chapterWord: 'Chapter', of: 'of',
    progressLabel: 'Quiz progress', railLabel: 'Quiz chapters', done: 'Done', now: 'Now', notNeeded: 'Not needed',
    playbackCta: 'See my match', change: 'Change an answer', because: 'Because you told us',
    loaderSkip: 'Skip to my match', lookingUp: 'Looking it up',
    details: 'Details', addToCart: 'Add to cart', adding: 'Adding to cart', configure: 'Configure',
    configureTitle: 'Configure', done_: 'Done', yourTotal: 'Your total',
    tabPlan: 'Your plan', tabGood: 'Good to know', tabOther: 'Other options', tabAnswers: 'Your answers',
    changeOne: 'Change', retake: 'Retake the quiz', optionalNotAdded: 'Optional, not added to your cart',
    skipped: 'Skipped', notAnswered: 'Not answered', compare: 'Compare with the other systems',
    phone: '1-800-934-0051', tel: 'tel:18009340051', talk: 'Talk to a specialist',
    selectedConfig: 'Selected configuration: ', whyLabel: 'Why it fits you',
    unavailableTitle: "Let's set this one up *with you.*", unavailableEyebrow: 'Your next step',
    unavailableIncluded: 'The configuration for your answers is unavailable right now.',
    sendDetails: 'Send my details',
    cartError: 'We could not add this to your cart. Please try again or call 1-800-934-0051.',
    configUpdated: 'Configuration updated.', invalidDefault: 'Enter a number from {min} to {max}, or skip.',
    close: 'Close', answersTitle: 'Your answers', required: 'Choose an answer to continue.',
    contactSent: 'Thank you. Your note has been sent to Crystal Quest.',
    // round B: in-between cards, result additions, share link, HubSpot
    reviewKicker: "In a customer's words", verified: 'Verified buyer', matchedFor: 'Matched for',
    scaleNames: ['Soft', 'Moderate', 'Hard', 'Very hard'], scaleWide: ['Soft', 'Moderately hard', 'Hard', 'Very hard'],
    scaleTicks: ['0', '3.5', '7', '10.5+ gpg'], scaleYou: '{v} gpg', scaleAssumed: 'About {v} gpg',
    scaleAria: '{v} gpg, {band} on a scale from soft to very hard', scaleBands: ['soft', 'moderately hard', 'hard', 'very hard'],
    shareFirst: 'Share my match', shareShort: 'Share', copyLink: 'Copy link to my match', sendPlumber: 'Share with my installer', emailMatch: 'Email me my match',
    linkCopied: 'Link copied', copyFailed: 'Copy the link below.', shareTitle: 'My Crystal Quest water match',
    shareText: 'This is the system Crystal Quest\'s quiz matched to our water. Can you take a look?',
    shareMyText: 'My Crystal Quest water match.',
    keepTitle: 'A clear plan. *Ready to share.*', keepLead: 'Send your system and configuration to the person installing it.',
    keepPoints: ['Your plumber sees the system and its size, the stages in order, and the drain and power it needs.'],
    yourLink: 'Your link',
    warrantyNote: '[One-year limited warranty](/pages/warranty).',
    guaranteeLine: 'Backed by our [Guaranteed Performance program](/pages/our-guaranteed-performance) and [one-year limited warranty](/pages/warranty).',
    guaranteeTitle: 'Guaranteed Performance',
    guaranteeText: 'Backed by our [Guaranteed Performance program](/pages/our-guaranteed-performance) and [one-year limited warranty](/pages/warranty). The program has conditions, including an independent lab test after install. [See the terms](/pages/our-guaranteed-performance).',
    whatNextTitle: 'What happens next',
    whatNext: [
      ['Today.', 'Add it to your cart. The size and options from your answers are already set.', 'Today: add to cart, size already set.'],
      ['Ships.', 'In-stock standard systems ship in less than 3 business days. Residential orders over $150 ship free in the lower 48.', 'Ships in less than 3 business days; free over $150 in the lower 48.'],
      ['Install day.', 'A licensed plumber or installer connects it. Installation is separate from the equipment price.', 'Install: a licensed plumber or installer connects it.'],
      ['After.', 'Lifetime support by phone for you and your installer: [1-800-934-0051](tel:18009340051).', 'After: lifetime support, [1-800-934-0051](tel:18009340051).']],
    includedTitle: 'What\'s *included.*',
    reviewsTitle: 'Owners of *systems like yours.*',
    trustTitle: 'Built by people who *answer the phone.*',
    // Covered results group warranty with Guaranteed Performance; shipping, installation and support stay in Details.
    trust: [['1994', 'Designed and hand-assembled in the USA since 1994'], ['1,000,000+', 'Over 1,000,000 people served since 1994'], ['ISO 9001', 'Certified quality management']],
    closerTitle: 'Still deciding? *Talk it through.*', closerText: 'A specialist checks the fit with you. Or compare every system side by side.',
    reportTitle: 'Send us your *water report.*', reportText: 'A specialist goes through it with you and plans the stages.', reportLabel: 'Send my water report',
    // HubSpot (round A HUBSPOT-BUILD.md): F1 specialist review, F2 email my match
    sending: 'Sending', send: 'Send', emailInvalid: 'That email does not look complete. Check it and try again.',
    reviewPrivacy: 'We use these details only to reply about your water.',
    matchTitle: 'Email me my match', matchEmail: 'Email', matchFirst: 'First name (optional)', matchTips: 'Also send me water tips and offers now and then.',
    matchSend: 'Send my match', matchPrivacy: 'We\'ll email your match and may follow up about it twice. Unsubscribe anytime.',
    matchSent: 'Sent. Check your inbox in a minute.', matchSaved: 'Saved. We\'ll email your match shortly. Prefer the phone? [1-800-934-0051](tel:18009340051).',
    matchFallbackBody: 'Please email me my match from the quiz.', privacy: 'Privacy Policy', privacyUrl: '/policies/privacy-policy',
    sendFailed: 'We could not send this just now. Please try again, or call [1-800-934-0051](tel:18009340051).',
    hsReviewLabel: 'Your water review', shareArea: 'your area'
  };

  const QUESTION_TYPES = new Set(['single', 'multi', 'input']);
  const INTERSTITIALS = new Set(['stat', 'fact', 'reaction', 'testimonial']);
  const types = new Map();
  const definitions = new Map();
  const mounted = new WeakSet();
  const instances = [];

  function defineType(name, spec) { types.set(name, Object.assign({question:false, progress:true, photo:false}, spec)); }

  /* ============================== mount ============================== */
  function mount(root, def) {
    const doc = root.ownerDocument, win = doc.defaultView;
    const screenEl = root.querySelector('[data-cqq-screen]');
    if (!screenEl || !def || !Array.isArray(def.screens) || !def.screens.length) {
      if (win.console) win.console.error('[cqq] quiz definition missing screens or the section has no [data-cqq-screen]');
      showFallback(root);
      return null;
    }
    const quiz = root.dataset.cqqQuiz;
    const S = Object.assign({}, STRINGS, def.strings || {});
    const chapters = arr(def.chapters);
    const screens = def.screens.filter(s => s && s.id && s.type);
    const byId = new Map(screens.map((s, i) => [s.id, Object.assign({}, s, {_order:i})]));
    const order = id => (byId.get(id) || {_order:-1})._order;
    const storageKey = def.storageKey || ('cqq:' + quiz + ':v1');
    const debug = (() => {
      try {
        const q = new URLSearchParams(win.location.search);
        if (q.has('cqq-debug')) return q.get('cqq-debug') !== '0';
        if (win.CQQ_DEBUG === true) return true;
        if (/^(localhost|127\.0\.0\.1|\[::1\])$/.test(win.location.hostname)) return true;
        if (win.Shopify && (win.Shopify.designMode || (win.Shopify.theme && win.Shopify.theme.role === 'development'))) return true;
      } catch (_) {}
      return false;
    })();
    const reduceMq = win.matchMedia ? win.matchMedia('(prefers-reduced-motion: reduce)') : {matches:false};
    const calm = () => !!(reduceMq.matches || root.closest('[data-cqv-motion-paused]') || (win.navigator && win.navigator.connection && win.navigator.connection.saveData));
    // The editorial finder opts into instant keyboard navigation without changing older quiz definitions.
    let keyboardMotion = false;
    root.addEventListener('keydown', () => { if (def.statMotion) keyboardMotion = true; }, true);
    root.addEventListener('pointerdown', () => { keyboardMotion = false; }, true);
    const instantInteraction = () => !!(def.statMotion && keyboardMotion);

    /* ---------- catalogue, images, assets ---------- */
    const readJSON = el => { try { return el ? JSON.parse(el.textContent) : null; } catch (_) { return null; } };
    const cards = [];
    const addCards = list => arr(list).forEach(c => { if (c && c.id && !cards.some(x => x.id === c.id)) cards.push(c); });
    addCards((readJSON(root.querySelector('#cq-well-v3-catalog')) || {}).cards);
    root.querySelectorAll('script[data-cqq-catalog]').forEach(el => addCards((readJSON(el) || {}).cards));
    const cardById = new Map(cards.map(c => [c.id, c]));
    const imageMap = readJSON(root.querySelector('#cqq-images')) || {};
    const sceneMap = readJSON(root.querySelector('#cqq-scenes')) || {};
    const assetMap = readJSON(root.querySelector('#cqq-assets')) || {};
    const assetBase = root.dataset.cqqAssetBase || '';
    const mediaBase = root.dataset.cqwMediaBase || '';
    // Product composites and room scenes are kept in Shopify Files, not the theme (AJ 2026-10-18: "put it in shopify as a
    // file"): the same file names, served from the Files CDN. A section prints data-cqq-files-base; the fallback turns
    // the theme asset base (/cdn/shop/t/<id>/assets/) into the Files base (/cdn/shop/files/).
    const filesBase = root.dataset.cqqFilesBase || assetBase.replace(/\/t\/\d+\/assets\/$/, '/files/');
    const FILES_RE = /^cq-(?:v3-q-scene-|whq-result-|wh1[0-2]-studio-|v3-(?:refined-|polished-)?studio-|v3-authentic-|v3-part-|v3-q-compare-)|-generated\.jpg$|^cq-whq-tile-(?:tank|cartridge|compact)\.webp$/;
    let currency = root.dataset.currency || 'USD';
    try { new Intl.NumberFormat('en-US', {style:'currency', currency}); } catch (_) { currency = 'USD'; }
    const money = v => v == null || !Number.isFinite(Number(v)) ? '' : new Intl.NumberFormat('en-US', {style:'currency', currency, minimumFractionDigits:Number.isInteger(Number(v)) ? 0 : 2, maximumFractionDigits:2}).format(Number(v));
    const commerce = () => win.CQWellV3Commerce || null;
    const asset = name => assetMap[name] || (imageMap[name] && imageMap[name].src) || (filesBase && FILES_RE.test(name) ? filesBase + name : mediaBase && /^cq-wiz(?:ard)?-[a-z0-9-]+\.(?:webp|jpg)$/.test(name) ? mediaBase + name : (assetBase ? assetBase + name : name));

    /* ---------- state ---------- */
    const fresh = () => ({values:{}, owner:{}, done:{}, fill:{}, config:null, current:null, editing:null, started:false, seq:0});
    let st = fresh();
    let saved = null, effCache = null, navToken = 0, timers = [], cartBusy = false, lastResult = null, resultViewed = false;
    const selections = new Map();   // result product selections (commerce state) keyed by 'primary' / 'alt'
    let leadPending = null, startMoment = null, ghostEl = null, countFrame = 0, warmIdle = 0;
    let cartButton = null, cartLabel = '';
    let finishCounts = null;
    const invalidate = () => { effCache = null; };
    const resolve = (v, a) => typeof v === 'function' ? v(a || answers(), ctx) : v;

    function derive(values) {
      const a = Object.assign({}, values);
      if (typeof def.derive === 'function') { try { Object.assign(a, def.derive(a, ctx) || {}); } catch (e) { warn('derive failed', e); } }
      return a;
    }
    function pickVariant(s, a) {
      if (!Array.isArray(s.variants)) return s;
      const v = s.variants.find(x => !x.when || safeWhen(x.when, a));
      // a variant may switch between interstitial kinds (stat, fact, reaction, testimonial), never into a question
      if (!v) return null;
      const type = v.type && INTERSTITIALS.has(v.type) && INTERSTITIALS.has(s.type) ? v.type : s.type;
      return Object.assign({}, s, v, {id:s.id, type, variants:undefined, variantId:v.id || null});
    }
    function safeWhen(fn, a) { try { return !!fn(a, ctx); } catch (e) { warn('when failed', e); return false; } }
    function visibleWith(s, a) {
      if (s.type === 'loader' && calm()) return false;
      if (s.when && !safeWhen(s.when, a)) return false;
      if (Array.isArray(s.variants) && !pickVariant(s, a)) return false;
      return true;
    }
    function answers() {
      if (effCache) return effCache;
      let keep = Object.keys(st.values);
      let a = derive(st.values);
      for (let i = 0; i < 6; i++) {
        const onPath = new Set(screens.filter(s => visibleWith(s, a)).map(s => s.id));
        const next = keep.filter(k => !st.owner[k] || onPath.has(st.owner[k]));
        const pruned = {}; next.forEach(k => { pruned[k] = st.values[k]; });
        const b = derive(pruned);
        const same = next.length === keep.length;
        keep = next; a = b;
        if (same) break;
      }
      effCache = a;
      return a;
    }
    const pathScreens = () => { const a = answers(); return screens.filter(s => visibleWith(s, a)); };
    const pathIds = () => pathScreens().map(s => s.id);
    const materialize = id => { const s = byId.get(id); return s ? pickVariant(s, answers()) || s : null; };
    const typeOf = s => types.get(s && s.type) || null;
    const isQuestion = s => !!(s && (QUESTION_TYPES.has(s.type) || (typeOf(s) && typeOf(s).question)));
    const firstId = () => pathIds()[0];

    const ctx = {
      quiz, get name() { return def.name || quiz; }, S,
      card: id => cardById.get(id) || null,
      config: (cardId, opts) => { const b = buildSelection({card:cardId, select:(opts || {}).select, enable:(opts || {}).enable, componentSelect:(opts || {}).componentSelect}); return b ? pricing(b) : null; },
      price: (cardId, opts) => { const p = ctx.config(cardId, opts); return p && p.price != null ? money(p.price) : ''; },
      minPrice: cardId => { const c = cardById.get(cardId); if (!c) return ''; const ps = c.components[0].variants.filter(v => v.available !== false).map(v => Number(v.price)).filter(Number.isFinite); return ps.length ? money(Math.min(...ps)) : ''; },
      money, join, lowerFirst, upperFirst, esc, md, plain,
      label: (screenId, value) => { const s = byId.get(screenId); const o = s ? optionsOf(s).find(x => x.value === value) : null; return o ? plain(resolve(o.label)) : String(value == null ? '' : value); },
      labels: (screenId, values) => arr(values).map(v => ctx.label(screenId, v)),
      screen: id => byId.get(id) || null,
      outcome: () => computeResult(true),
      asset, loadScript,
      get calm() { return calm(); },
      path: () => pathIds(),
      answers: () => answers()
    };

    /* ---------- persistence ---------- */
    function save() {
      try { win.sessionStorage.setItem(storageKey, JSON.stringify({v:1, ts:Date.now(), values:st.values, owner:st.owner, done:st.done, fill:st.fill, config:st.config, current:st.current, editing:st.editing, started:st.started})); } catch (_) {}
    }
    function load() {
      try { const raw = win.sessionStorage.getItem(storageKey); const d = raw ? JSON.parse(raw) : null; return d && d.v === 1 && d.values ? d : null; } catch (_) { return null; }
    }
    function clearSaved() { try { win.sessionStorage.removeItem(storageKey); } catch (_) {} }
    function adopt(d) { st = Object.assign(fresh(), {values:d.values || {}, owner:d.owner || {}, done:d.done || {}, fill:d.fill || {}, config:d.config || null, current:d.current, editing:d.editing || null, started:!!d.started}); invalidate(); }

    /* ---------- analytics (Shopify customer events only) ---------- */
    function track(name, data) {
      const s = byId.get(st.current);
      const payload = Object.assign({quiz, screen:st.current || null, chapter:s && s.chapter || null}, data || {});
      try { if (win.Shopify && win.Shopify.analytics && typeof win.Shopify.analytics.publish === 'function') win.Shopify.analytics.publish(name, payload); } catch (_) {}
      try { root.dispatchEvent(new win.CustomEvent('cqq:event', {detail:{name, payload}, bubbles:true})); } catch (_) {}
      if (debug) { (root.__cqqEvents = root.__cqqEvents || []).push({name, payload}); }
    }
    function warn() { if (debug && win.console) win.console.warn.apply(win.console, ['[cqq]'].concat([].slice.call(arguments))); }

    /* ---------- scripts on demand (e.g. the hardness data) ---------- */
    const scriptCache = {};
    function loadScript(name) {
      if (scriptCache[name]) return scriptCache[name];
      scriptCache[name] = new Promise((res, rej) => {
        const el = doc.createElement('script'); el.src = asset(name); el.async = true;
        el.onload = () => res(); el.onerror = () => { delete scriptCache[name]; rej(new Error('load ' + name)); };
        doc.head.appendChild(el);
      });
      return scriptCache[name];
    }

    /* ---------- images ---------- */
    // Shopify's CDN resizes theme assets and product images with &width=; the ladder runs to 1920 (round B brief 4.4)
    // and never past the file's own width. Exactly one width per URL: a width already on the URL (catalogue cards
    // print width=700) is replaced, never doubled.
    const LADDER = [360, 480, 720, 960, 1200, 1600, 1920];
    const MEDIA_WIDTHS = [480, 720, 1080];
    // Stacked phone and tablet results use a complete 2:1 photo; desktop keeps the 4:5 scene.
    // Portrait tablets wider than 820 px (an iPad Pro 11 is 834 x 1062) stack like phones too (AJ 2026-10-09).
    const BAND_MQ = '(max-width: 820px), (orientation: portrait)';
    const SPLIT_SIZES = '(min-width: 821px) and (orientation: landscape) 50vw, 100vw';
    // The split (two-column) layout: a container 821 px or wider in landscape, matching the CSS container queries.
    const isSplit = el => !!el && (el.matches('.cqv-q-landing, .cqv-q-result') ? win.matchMedia('(min-width: 821px) and (orientation: landscape)').matches : el.clientWidth >= 821 && el.clientWidth > el.clientHeight);
    const onMedia = url => !!mediaBase && String(url).indexOf(mediaBase) === 0;
    function sized(url, w, own) {
      if (!url) return '';
      if (onMedia(url)) {
        const width = MEDIA_WIDTHS.find(value => value >= w && value < (Number(own) || 1600));
        return width ? mediaBase + 'w' + width + '/' + String(url).slice(mediaBase.length).replace(/\.[^/.]+$/, '.webp') : url;
      }
      const u = String(url).replace(/([?&])width=\d+(&|$)/g, (m, a, b) => b ? a : '').replace(/[?&]$/, '');
      return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
    }
    function imgRef(ref) {
      if (!ref) return null;
      if (typeof ref === 'string') ref = {src:ref};
      const name = ref.src || '';
      const known = imageMap[name];
      // Each regenerated scene and band owns its crop box and wall colour, including stainless twins.
      const geometry = known;
      const url = known ? known.src : (/^(https?:)?\/\//.test(name) || name.charAt(0) === '/' ? name : asset(name));
      return Object.assign({}, ref, {url, name, w:ref.w || (geometry && geometry.w) || 0, h:ref.h || (geometry && geometry.h) || 0,
        safe:ref.safe || (geometry && geometry.safe) || '', wall:ref.wall || (geometry && geometry.wall) || '', avoid:ref.avoid || (geometry && geometry.avoid) || ''});
    }
    function srcsetOf(r, ladder) {
      if (onMedia(r.url)) {
        const own = Number(r.w) || 1600;
        return MEDIA_WIDTHS.filter(w => w < own).map(w => esc(sized(r.url, w, own)) + ' ' + w + 'w').concat(esc(r.url) + ' ' + own + 'w').join(', ');
      }
      if (r.srcset) return esc(r.srcset);
      const own = r.w || 0;
      const widths = (ladder || LADDER).filter(w => !own || w < own).concat(own ? [own] : []);
      return widths.map(w => esc(w === own ? r.url : sized(r.url, w)) + ' ' + w + 'w').join(', ');
    }
    function imgHTML(ref, o) {
      const r = imgRef(ref); if (!r || !r.url) return '';
      o = o || {};
      const own = r.w || 0;
      const srcset = r.noSrcset ? '' : srcsetOf(r, o.ladder);
      const alt = r.alt != null ? r.alt : (o.alt != null ? o.alt : '');
      const fallback = r.fallback ? ' data-cqq-image-fallback="' + esc(JSON.stringify(r.fallback)) + '"' : '';
      return '<img src="' + esc(r.srcset || (own && own <= 1200) ? r.url : sized(r.url, own && own < 960 ? own : 960, own)) + '"' + (srcset ? ' srcset="' + srcset + '" sizes="' + esc(o.sizes || SPLIT_SIZES) + '"' : '') +
        fallback + ' alt="' + esc(plain(alt)) + '"' + (r.w ? ' width="' + r.w + '" height="' + r.h + '"' : '') + (o.cls ? ' class="' + esc(o.cls) + '"' : '') +
        ((o.eager || o.now) ? ' loading="eager"' : ' loading="lazy"') +
        ((o.priority || o.eager || o.low) ? ' fetchpriority="' + (o.priority === 'high' || (!o.priority && o.eager) ? 'high' : 'low') + '"' : '') + ' decoding="async">';
    }
    // Missing future scene/setup files fall back once, without another request for the failed picture source.
    root.addEventListener('error', e => {
      const im = e.target;
      if (!im || !im.dataset || !im.dataset.cqqImageFallback) return;
      let ref; try { ref = JSON.parse(im.dataset.cqqImageFallback); } catch (_) { return; }
      const fig = im.closest('figure, .cqv-sheet-media'), fallback = imgRef(ref);
      if (fig) {
        Array.from(fig.attributes).forEach(a => { if (/^data-cqq-(safe|ar|wall|avoid|fit)/.test(a.name)) fig.removeAttribute(a.name); });
        fig.removeAttribute('style'); fig.classList.remove('is-contained', 'is-fixed', 'cqq-img-wait', 'cqq-whole-image');
        if (fallback.product) fig.classList.add('is-product');
        else if (fallback.safe) {
          // A chapter fallback gets its own crop data, including the room photo's complete product box.
          fig.dataset.cqqSafe = fallback.safe; fig.dataset.cqqFit = 'all';
          if (fallback.w && fallback.h) fig.dataset.cqqAr = (fallback.w / fallback.h).toFixed(4);
          if (fallback.wall) fig.dataset.cqqWall = fallback.wall;
          if (fallback.avoid) fig.dataset.cqqAvoid = fallback.avoid;
        }
        if (fig.hasAttribute('data-cqq-photo')) fig.dataset.cqqPhoto = fallback.url;
      }
      const picture = im.closest('picture');
      const wrap = doc.createElement('div'); wrap.innerHTML = imgHTML(fallback, {now:true, sizes:im.sizes || '100vw', cls:!fallback.product && fallback.safe ? 'cqq-fit-img' : ''});
      if (wrap.firstChild) (picture || im).replaceWith(wrap.firstChild);
      fitAll();
    }, true);
    const photoSlotObservers = new Set();
    function clearPhotoSlots() { photoSlotObservers.forEach(observer => observer.disconnect()); photoSlotObservers.clear(); }
    function preparePhotoSlots(scope) {
      scope.querySelectorAll('img[data-cqq-slot-src]').forEach(im => {
        if (im.dataset.cqqSlotReady) return;
        im.dataset.cqqSlotReady = 'true'; im.dataset.cqqSlotState = 'observing';
        const upgrade = () => {
          const url = im.dataset.cqqSlotSrc;
          if (!url || !safeUrl(url)) return;
          im.dataset.cqqSlotState = 'loading';
          const probe = new win.Image();
          probe.decoding = 'async'; probe.fetchPriority = 'low';
          probe.onload = () => {
            im.dataset.cqqSlotState = 'loaded';
            if (!im.isConnected || !probe.naturalWidth) return;
            const fallback = {src:im.getAttribute('src'), srcset:im.getAttribute('srcset')};
            im.addEventListener('error', () => {
              im.removeAttribute('srcset');
              if (fallback.srcset) im.setAttribute('srcset', fallback.srcset);
              im.src = fallback.src;
            }, {once:true});
            im.width = probe.naturalWidth; im.height = probe.naturalHeight;
            im.srcset = srcsetOf({url, w:probe.naturalWidth});
            im.src = url;
          };
          // A missing slot never replaces the visible fallback or retries in this page session.
          probe.onerror = () => { im.dataset.cqqSlotState = 'failed'; };
          probe.src = url;
        };
        if ('IntersectionObserver' in win) {
          const observer = new win.IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); photoSlotObservers.delete(observer); upgrade(); }
          }, {rootMargin:'200px'});
          photoSlotObservers.add(observer); observer.observe(im);
        }
        // Older browsers keep the approved, responsive fallback without an extra request.
      });
    }
    // Image geometry stays on the figure for source proportions and floating-card clearance.
    const fitAttrs = (r, band) => r && r.safe ? ' data-cqq-safe' + (band ? '-band' : '') + '="' + esc(r.safe) + '"' + (r.wall ? ' data-cqq-wall' + (band ? '-band' : '') + '="' + esc(r.wall) + '"' : '') +
      (r.avoid && !band ? ' data-cqq-avoid="' + esc(r.avoid) + '"' : '') + (r.w && r.h ? ' data-cqq-ar' + (band ? '-band' : '') + '="' + (r.w / r.h).toFixed(4) + '"' : '') : '';
    // A result scene: media:{scene:'complete'} names the 4:5 laptop picture and the 2:1 phone band of that outcome
    function sceneMedia(m) {
      if (!m || !m.scene) return m;
      const key = String(m.scene), sc = sceneMap[key] || {};
      return Object.assign({src:'cq-v3-q-scene-' + key + '.webp', band:'cq-v3-q-scene-' + key + '-band.webp', alt:sc.alt || ''}, m);
    }
    // All stacked results use the closer 2:1 band; split desktop views use the portrait scene.
    function pictureHTML(m, o) {
      o = o || {};
      const d = imgRef(m), b = m.band ? imgRef(Object.assign({}, m, {src:m.band, w:0, h:0, safe:'', wall:'', avoid:''})) : null;
      if (!d || !d.url) return '';
      const img = imgHTML(Object.assign({}, m, {alt:m.alt || ''}), {sizes:o.sizes || SPLIT_SIZES, eager:o.eager, cls:'cqq-fit-img'});
      return b ? '<picture><source media="' + BAND_MQ + '" srcset="' + (srcsetOf(b) || esc(b.url)) + '" sizes="100vw">' + img + '</picture>' : img;
    }
    // Background plates for the in-between screens (AJ picks 2026-09-25: stat and fact 1b, review 2c, numbers 3b,
    // matching 4b). A landscape screen gets the desktop plate, a portrait one the phone plate. Save-Data keeps the
    // flat colour.
    const saveData = () => !!(win.navigator && win.navigator.connection && win.navigator.connection.saveData);
    function plateKind(s) {
      if (!s || s.plate === false) return null;
      if (typeof s.plate === 'string') return s.plate;
      if (s.type === 'testimonial') return 'review';
      if (s.type === 'loader') return 'matching';
      if (s.look === 'mine') return 'numbers';
      if (s.type === 'stat' || s.type === 'fact' || s.type === 'reaction') return 'stat';
      return null;
    }
    const plateRefs = kind => ({d:imgRef('cq-v3-q-plate-' + kind + '-desktop.webp'), p:imgRef('cq-v3-q-plate-' + kind + '-phone.webp')});
    function plateHTML(kind) {
      if (!kind || saveData()) return '';
      const {d, p} = plateRefs(kind);
      if (!d.url || !p.url) return '';
      const phoneSet = srcsetOf(p, [540, 720]);
      return '<picture class="cqq-plate" data-cqq-plate="' + esc(kind) + '" aria-hidden="true"><source media="(min-aspect-ratio: 1/1)" srcset="' + (srcsetOf(d, [1200, 1600]) || esc(d.url)) + '" sizes="100vw">' +
        '<img src="' + esc(sized(p.url, 720, p.w)) + '"' + (phoneSet ? ' srcset="' + phoneSet + '" sizes="100vw"' : '') + ' alt="" loading="eager" fetchpriority="low" decoding="async"></picture>';
    }
    // Use the renderer's srcset and sizes, in browser selection order, and retain its decode promise.
    const warmed = new Map();
    function preload(ref, sizes, ladder) {
      const r = imgRef(ref); if (!r || !r.url) return Promise.resolve();
      const set = r.noSrcset ? '' : srcsetOf(r, ladder).replace(/&amp;/g, '&');
      const src = r.srcset || (r.w && r.w <= 1200) ? r.url : sized(r.url, r.w && r.w < 960 ? r.w : 960, r.w);
      if (onMedia(r.url)) sizes = sizes || SPLIT_SIZES;
      const key = (set || src) + '|' + sizes;
      if (warmed.has(key)) return warmed.get(key);
      const ready = new Promise(done => {
        try {
          const im = new win.Image(); im.decoding = 'async'; im.fetchPriority = 'low';
          if (!im.decode) im.onload = im.onerror = done;
          im.sizes = sizes;
          if (set) im.srcset = set;
          im.src = src;
          if (im.decode) im.decode().then(done, done);
          else if (im.complete) done();
        } catch (_) { done(); }
      });
      warmed.set(key, ready); return ready;
    }
    const photoVisible = s => s.type === 'landing' || s.type === 'chapter' ||
      (quiz === 'wizard' && typeOf(s) && typeOf(s).photo && !isQuestion(s)) ||
      ((QUESTION_TYPES.has(s.type) || (quiz === 'wizard' && typeOf(s) && typeOf(s).photo)) &&
        (isSplit(screenEl) || (screenEl.clientWidth >= 601 && screenEl.clientHeight >= 880)));
    function snappedSizes(renderW, own) {
      const dpr = win.devicePixelRatio || 1, width = Number(own) || 1920;
      const candidate = LADDER.filter(x => x < width).concat([width]).find(x => x >= renderW * dpr) || width;
      return Math.ceil(candidate / dpr) + 'px';
    }
    function screenImages(s, a) {
      if (!s) return [];
      const list = [], add = (ref, sizes, ladder) => { if (ref) list.push({ref, sizes, ladder}); };
      const kind = plateKind(s);
      if (kind && !saveData()) {
        const refs = plateRefs(kind), landscape = win.matchMedia ? win.matchMedia('(min-aspect-ratio: 1/1)').matches : win.innerWidth >= win.innerHeight;
        add(landscape ? refs.d : refs.p, '100vw', landscape ? [1200, 1600] : [540, 720]);
      }
      if (photoVisible(s) && !s.compare) {
        const main = photoRef(s, a), phoneSrc = main && main.phone && win.matchMedia && win.matchMedia(BAND_MQ).matches;
        add(phoneSrc ? main.phone : main, s.editorial || phoneSrc ? '100vw' : SPLIT_SIZES);
      }
      const photo = resolve(s.photo, a);
      if (photo) add(photo, s.look === 'photo' ? '(min-width: 821px) 500px, calc(100vw - 32px)' : '(min-width: 821px) 568px, calc(100vw - 72px)');
      const compare = resolve(s.compare, a);
      if (compare) arr(compare.items).forEach(item => add(item.image, '(min-width: 821px) 300px, 45vw'));
      if (s.type === 'testimonial') { const product = resolve(s.product, a); if (product) add(product.image, '48px', [120]); }
      if (s.type === 'result') {
        const result = computeResult(true);
        // Rebuild locally: an edited answer clears st.config while the last result's selections still exist.
        let b = result && result.product ? buildSelection(result.product) : null;
        if (b && st.config && !restoreChoices(b, st.config)) b = buildSelection(result.product);
        const pr = b && pricing(b);
        // prepareResult owns unavailable substitutions; do not warm a sale scene for an unavailable selection.
        if (result && result.product && (!pr || pr.available === false)) return list;
        const media = selectedMedia(result, pr, b);
        if (media && Array.isArray(media.gallery)) {
          media.gallery.filter(item => item.src).forEach(item => add(item, '(min-width: 821px) 260px, 160px'));
        } else if (media) {
          const band = media.band && win.matchMedia && win.matchMedia(BAND_MQ).matches;
          // The closer 2:1 bands preserve the full photograph and use matching proportions for both finishes.
          const ref = imgRef(band ? media.band : media);
          const estimate = band ? win.innerWidth : win.innerWidth / 2 - 64;
          const sizes = ref && onMedia(ref.url) ? (band ? '100vw' : media.band ? '(min-width: 821px) and (orientation: landscape) 50vw, 520px' : SPLIT_SIZES) : snappedSizes(estimate, ref && ref.w);
          add(ref, media.product ? SPLIT_SIZES : sizes);
        }
      }
      return list;
    }
    function warmScreen(s, a) { screenImages(typeof s === 'string' ? materialize(s) : s, a).forEach(x => preload(x.ref, x.sizes, x.ladder)); }
    function warmAhead(fromId, count, draft) {
      const connection = win.navigator && win.navigator.connection;
      const limit = saveData() || (connection && /(^|-)2g$|^3g$/.test(connection.effectiveType || '')) ? 1 : (count || 2);
      const path = draft ? screens.filter(s => visibleWith(s, draft)) : pathScreens();
      path.filter(s => order(s.id) > order(fromId)).slice(0, limit).forEach(s => {
        // Preview only the selected draft variant; answers and result selection stay uncommitted.
        if (draft && s.type === 'result') return;
        warmScreen(pickVariant(s, draft || answers()), draft);
      });
    }
    const focusStyle = r => r ? ' style="' + esc('--cqv-q-focus:' + (r.focus || '50% 50%') + ';--cqv-q-focus-lg:' + (r.focusDesktop || r.focus || '50% 50%')) + '"' : '';

    /* ---------- options ---------- */
    function optionsOf(s) {
      const list = resolve(s.options);
      return arr(list).map(o => typeof o === 'string' ? {value:o, label:o} : o).filter(o => o && o.value != null);
    }

    /* ---------- progress ---------- */
    function chapterAt(id) {
      const p = pathScreens(); const i = p.findIndex(s => s.id === id);
      for (let j = i; j >= 0; j--) if (p[j].chapter) return p[j].chapter;
      return 0;
    }
    function progressInfo(id, dir) {
      const s = byId.get(id), t = typeOf(s);
      if (!s || !t || t.progress === false) return null;
      const n = chapters.length || 4;
      if (s.type === 'playback') return {chapter:n, fills:Array(n).fill(100)};
      const c = chapterAt(id) || 1;
      const p = pathScreens(), idx = p.findIndex(x => x.id === id);
      const qs = p.filter(x => x.chapter === c && isQuestion(x));
      const doneQs = qs.filter(x => p.indexOf(x) < idx).length;
      let fill = qs.length ? Math.round(doneQs / qs.length * 100) : 0;
      const prev = st.fill[c] || 0;
      if (dir !== -1) fill = Math.max(fill, prev);
      st.fill[c] = fill;
      const fills = []; for (let k = 1; k <= n; k++) fills.push(k < c ? 100 : k === c ? fill : 0);
      return {chapter:c, fills};
    }
    function paintProgress(id, dir) {
      const bar = screenEl.querySelector('.cqv-q-progress'); if (!bar) return;
      const info = progressInfo(id, dir);
      bar.hidden = !info;
      if (!info) return;
      const segs = bar.children;
      info.fills.forEach((f, i) => { const seg = segs[i]; if (!seg) return; seg.classList.toggle('is-done', f >= 100); seg.classList.toggle('is-current', i + 1 === info.chapter && f < 100); seg.style.setProperty('--cqv-q-fill', f + '%'); });
      const ch = chapters[info.chapter - 1] || {};
      const text = S.chapterWord + ' ' + info.chapter + ' ' + S.of + ' ' + (chapters.length || 4) + ', ' + (ch.name || '');
      bar.setAttribute('aria-valuenow', String(Math.round(((info.chapter - 1) + (info.fills[info.chapter - 1] || 0) / 100) / (chapters.length || 4) * 100)));
      bar.setAttribute('aria-valuetext', text);
      if (st._lastChapter !== info.chapter) { st._lastChapter = info.chapter; announce(text); }
    }
    function announce(text) { const live = screenEl.querySelector('[data-cqq-announce]'); if (live) { live.textContent = ''; win.setTimeout(() => { live.textContent = text; }, 30); } }

    /* ---------- render api for types ---------- */
    const tAPI = {
      esc, md, plain, S, chapters,
      text: (v, a) => md(resolve(v, a)),
      img: imgHTML, imgRef, focusStyle,
      get answers() { return answers(); },
      // Raw choice preserves the selected row when a definition aliases two answers for recommendation rules.
      value: k => st.values[k] != null ? st.values[k] : answers()[k],
      id: suffix => 'cqq-' + quiz + '-' + (st.current || 'x') + (suffix ? '-' + suffix : ''),
      kicker(s) {
        if (s.kicker != null) return s.kicker === false ? '' : '<p class="cqv-q-kicker">' + md(resolve(s.kicker)) + '</p>';
        return tAPI.chapterKicker(s);
      },
      // "01 / 04 \u00b7 Your water": the chapter kicker, above a card whose own kicker sits inside it
      chapterKicker(s) {
        if (s.chapterKicker === false) return '';
        if (typeof s.chapterKicker === 'string') return '<p class="cqv-q-kicker">' + md(s.chapterKicker) + '</p>';
        const c = s.chapter; if (!c) return '';
        const n = chapters.length || 4, ch = chapters[c - 1] || {};
        const pad = x => (x < 10 ? '0' : '') + x;
        return '<p class="cqv-q-kicker">' + esc(pad(c) + ' / ' + pad(n) + ' \u00b7 ' + (ch.name || '')) + (s.last ? esc(' \u00b7 ' + S.lastQuestion) : '') + '</p>';
      },
      pictureHTML, sceneMedia, srcsetOf, fitAttrs,
      review: (r, o) => reviewCardHTML(r, o),
      heading(s, cls, field) { return '<h1 class="' + (cls || 'cqv-q-title') + '" id="' + tAPI.id('title') + '" tabindex="-1">' + md(resolve(s[field || 'title'])) + '</h1>'; },
      lead(s, extraCls) { const h = resolve(s.help != null ? s.help : s.lead); return h ? '<p class="cqv-q-lead' + (extraCls ? ' ' + extraCls : '') + '" id="' + tAPI.id('help') + '">' + md(h) + '</p>' : ''; },
      foot: html => html ? '<div class="cqv-q-foot">' + html + '</div>' : '',
      button: (label, act, cls, attrs) => '<button type="button" class="' + (cls || 'cqv-btn') + '" data-cqq-do="' + esc(act || 'next') + '"' + (attrs || '') + '>' + md(label) + '</button>',
      photo(s, opts) {
        const r = imgRef(photoRef(s)); if (!r) return '';
        // Registered photos retain their complete source image. Phone frames follow the source's proportions;
        // desktop photos fit their existing split column without cropping or mirrored fill.
        const fit = r.safe ? fitAttrs(r) + ' data-cqq-fit="' + esc((opts && opts.fit) || 'all') + '"' : '';
        const visible = photoVisible(s);
        let img = imgHTML(r, {alt:r.alt || '', now:visible, priority:visible ? 'high' : undefined, sizes:(opts && opts.sizes) || SPLIT_SIZES, cls:r.safe ? 'cqq-fit-img' : ''});
        // image.phone {src, w, h}: a wide picture for phones and tablets up to 820 px (the section prints it once the
        // imagery lane's file exists); the main picture stays the source from 821 px and the fallback everywhere.
        // Round D (A3, 2026-09-28): the entry pictures cq-v3-q-entry-<quiz>-wide.webp (2400 x 1021, registered with their
        // product box) are the phone sources; their box and shape ride on the figure (data-cqq-safe-phone,
        // data-cqq-ar-phone) so the frame follows the full phone image proportions.
        const phone = r.phone ? imgRef(r.phone) : null;
        if (phone && phone.url) img = '<picture><source media="' + BAND_MQ + '" srcset="' + (srcsetOf(phone) || esc(phone.url)) + '" sizes="100vw">' + img + '</picture>';
        const phoneFit = phone && phone.url && phone.safe && phone.w && phone.h ? ' data-cqq-safe-phone="' + esc(phone.safe) + '" data-cqq-ar-phone="' + (phone.w / phone.h).toFixed(4) + '"' + (phone.wall ? ' data-cqq-wall-phone="' + esc(phone.wall) + '"' : '') : '';
        return '<figure class="cqv-q-photo' + (opts && opts.cls ? ' ' + opts.cls : '') + '"' + focusStyle(phone && phone.url && phone.focus ? Object.assign({}, r, {focus:phone.focus}) : r) + fit + phoneFit + ' data-cqq-photo="' + esc(r.url) + '">' + img + (opts && opts.inner || '') + '</figure>';
      },
      hasResume: () => !!(saved && saved.started && saved.current && Object.keys(saved.values || {}).length),
      chapterSkipped: k => !pathScreens().some(x => x.chapter === k && isQuestion(x)),
      resultHTML: s => resultHTML(s),
      resultBind: () => fillResultBelow()
    };
    function photoRef(s, a) {
      if (s.image === false) return null;
      if (s.image) return resolve(s.image, a);
      const t = typeOf(s);
      if (t && t.photo && s.chapter && chapters[s.chapter - 1]) return chapters[s.chapter - 1].image || null;
      return null;
    }

    /* ---------- screen chrome ---------- */
    screenEl.classList.add('cqv-q');
    // Round D (D2): the section's own entry picture (already fetched through the head preload, often decoded). The
    // first landing render keeps this element instead of a new one, so the picture is never fetched or decoded twice.
    const serverMedia = (() => { const f = screenEl.querySelector('.cqv-q-photo'); return f ? f.querySelector(':scope > picture, :scope > img') : null; })();
    doc.documentElement.classList.add('cqq-page');   // chrome-hiding fallback for browsers without :has() (see cq-quiz-v3-head)
    screenEl.innerHTML = '<div class="cqv-q-progress" role="progressbar" aria-label="' + esc(S.progressLabel) + '" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" hidden>' +
      (chapters.length ? chapters : [1, 2, 3, 4]).map(() => '<span></span>').join('') + '</div><div class="cqv-q-stage" data-cqq-stage></div><p class="cqv-sr-only" aria-live="polite" data-cqq-announce></p>';
    root.classList.add('is-ready');
    // Do not reveal fallback metrics and then reflow the screen when the quiz font arrives.
    // If loading fails or takes too long, retain the system stack for this mount instead of swapping later.
    // Round D (D2): the section prints the root with cqq-fonts-pending, so the quiz is hidden from the very first paint
    // (the root's white shows) and appears once, finished: the first view's weights (400 and 500, preloaded in the head
    // by cq-quiz-v3-head) and its pictures, each wait bounded, then one reveal. No quiz, blank, quiz (A-B-A) any more.
    // 600 and 700 load in the background; later screens that use them come long after.
    const firstViewGate = (() => {
      const bound = ms => new Promise(done => win.setTimeout(() => done(false), ms));
      let fonts = Promise.resolve(true);
      if (doc.fonts && doc.fonts.load) {
        const family = win.getComputedStyle(root).fontFamily;
        const faces = [400, 500].map(weight => weight + ' 16px ' + family);
        [600, 700].forEach(weight => { try { doc.fonts.load(weight + ' 16px ' + family).catch(() => {}); } catch (_) {} });
        if (faces.some(face => !doc.fonts.check(face))) {
          root.classList.add('cqq-fonts-pending');
          fonts = Promise.race([
            Promise.all(faces.map(face => doc.fonts.load(face))).then(() => faces.every(face => doc.fonts.check(face)), () => false),
            bound(1500)
          ]);
        }
      }
      return fonts;
    })();
    function adoptServerMedia() {
      const fresh = screenEl.querySelector('[data-cqq-stage] .cqv-q-photo > picture, [data-cqq-stage] .cqv-q-photo > img');
      if (!serverMedia || !fresh || serverMedia.tagName !== fresh.tagName) return;
      const imgOf = el => el.tagName === 'IMG' ? el : el.querySelector('img');
      const key = el => { const im = imgOf(el), so = el.querySelector && el.querySelector('source'); return [im && im.getAttribute('src'), im && im.getAttribute('srcset'), so && so.getAttribute('srcset'), so && so.getAttribute('media')].join('|'); };
      if (key(fresh) !== key(serverMedia)) return;
      const next = imgOf(fresh), kept = imgOf(serverMedia);
      // the rendered element's attributes (class, sizes, the computed crop in style) win; unchanged values are not reset
      Array.from(kept.attributes).forEach(a => { if (!next.hasAttribute(a.name)) kept.removeAttribute(a.name); });
      Array.from(next.attributes).forEach(a => { if (kept.getAttribute(a.name) !== a.value) kept.setAttribute(a.name, a.value); });
      if (kept.complete && kept.naturalWidth) kept.classList.remove('cqq-img-wait', 'cqq-img-late');
      else {
        // the listeners the render put on the new element (late fade, crop refit) move to the kept one
        // (before the first view is shown the whole quiz is hidden, so a picture that arrives then shows with it, not
        // with the late-picture fade; round D, the finder's laptop photo faded in just after the quiz appeared)
        const loaded = () => { if (!root.classList.contains('cqq-fonts-pending')) kept.classList.add('cqq-img-late'); kept.classList.remove('cqq-img-wait'); const fig = kept.closest('[data-cqq-safe]'); if (fig && fig.isConnected) fitMedia(fig); };
        kept.addEventListener('load', loaded, {once:true}); kept.addEventListener('error', loaded, {once:true});
      }
      fresh.replaceWith(serverMedia);
    }
    function revealFirstView() {
      // runs after mount has drawn the first screen (a microtask later), so every name below is initialised
      const stage = screenEl.querySelector('[data-cqq-stage]');
      const pictures = stage ? Array.from(stage.querySelectorAll('img')).filter(im => im.loading !== 'lazy' && im.getClientRects().length) : [];
      const decoded = Promise.race([
        Promise.all(pictures.map(im => (im.decode ? im.decode() : Promise.resolve()).catch(() => {}))),
        new Promise(done => win.setTimeout(done, 1200))
      ]);
      Promise.all([firstViewGate, decoded]).then(([ready]) => {
        const fallback = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif';
        if (!ready) { root.style.setProperty('--cqv-font', fallback); root.style.setProperty('--cqv-font-stack', fallback); }
        try { fitAll(); } catch (e) { warn('font fit failed', e); }
        // a picture that is ready shows with the first frame, not with the late-picture fade the render gave it
        pictures.forEach(im => { if (im.complete && im.naturalWidth) im.classList.remove('cqq-img-wait', 'cqq-img-late'); });
        root.classList.remove('cqq-fonts-pending');
        // These entry routes request focus while the first frame is hidden; the ordinary landing does not.
        if (deepStart || contactState) {
          const target = root.querySelector('dialog[open] h2, .cqq-start-moment') || screenEl.querySelector('h1');
          if (target) { try { target.focus({preventScroll:true}); } catch (_) {} }
        }
      });
    }

    /* ---------- below the first view (round B brief 3.1 and 4.3) ----------
       The landing's selling sections are server-rendered by the section ([data-cqq-more], inside the root or right
       after it) so search engines read them; they are moved under the screen and show only while the landing does.
       The result's own sections (keep or send your match, owner reviews, trust) are built here, under the
       result's first view. Both use normal document scrolling (.is-scroll); the quiz header stays on top. */
    const moreEls = [];
    root.querySelectorAll('[data-cqq-more]').forEach(el => { if (!screenEl.contains(el)) moreEls.push(el); });
    for (let n = root.nextElementSibling; n; n = n.nextElementSibling) if (n.matches && n.matches('[data-cqq-more]')) moreEls.push(n);
    { let anchor = screenEl; moreEls.forEach(el => { if (anchor.nextElementSibling !== el) anchor.after(el); anchor = el; el.hidden = true; }); }
    const resultMore = doc.createElement('div');
    resultMore.className = 'cqq-more cqq-result-more'; resultMore.hidden = true; resultMore.setAttribute('data-cqq-result-more', '');
    (moreEls.length ? moreEls[moreEls.length - 1] : screenEl).after(resultMore);
    function syncScroll(s) {
      const land = !!(s && s.type === 'landing'), res = !!(s && s.type === 'result');
      moreEls.forEach(el => { el.hidden = !land; });
      if (!res) { resultMore.hidden = true; resultMore.innerHTML = ''; }
      if (land) moreEls.forEach(el => el.querySelectorAll('img').forEach(im => { if (!im.complete) im.addEventListener('load', () => fitAll(), {once:true}); }));
      const editorial = land && s.editorial === true;
      root.classList.toggle('is-editorial-landing', editorial);
      const headerBack = root.querySelector('.cqv-quiz-back');
      if (headerBack) headerBack.hidden = editorial;
      root.classList.toggle('is-landing', land);
      root.classList.toggle('is-result', res);
      // Browse screens use document scrolling, even when an outcome has no extra blocks.
      root.classList.toggle('is-scroll', land || res);
      doc.documentElement.classList.toggle('cqq-page-scroll', land || res);
      root.scrollTop = 0;
      win.scrollTo({top:0, left:0, behavior:'instant'});
      if (land) moreEls.forEach(preparePhotoSlots);
    }

    /* ---------- render ---------- */
    function screenClass(s) {
      const t = typeOf(s);
      return ['cqv-q'].concat(arr(t && t.screenClass)).concat(s.screenClass ? arr(s.screenClass) : []).join(' ');
    }
    // Shopify may serialize 320ms as .32s. All CSS-driven timers share this conversion.
    function cssTimeMs(value, fallback) {
      const match = String(value == null ? '' : value).trim().match(/^(\d*\.?\d+)(ms|s)?$/i);
      if (!match) return fallback;
      const ms = Number(match[1]) * (match[2] && match[2].toLowerCase() === 's' ? 1000 : 1);
      return Number.isFinite(ms) ? ms : fallback;
    }
    const swapMs = () => cssTimeMs(win.getComputedStyle(root).getPropertyValue('--cqq-swap-ms'), 220);
    function dropGhost() { if (ghostEl) { ghostEl.remove(); ghostEl = null; } }
    function freeze(stage) {
      dropGhost();
      const ghost = doc.createElement('section'), rect = screenEl.getBoundingClientRect(), rootRect = root.getBoundingClientRect();
      ghost.className = screenEl.className + ' cqq-ghost'; ghost.setAttribute('aria-hidden', 'true'); ghost.inert = true;
      ghost.style.top = (rect.top - rootRect.top + root.scrollTop) + 'px'; ghost.style.height = rect.height + 'px';
      const progress = screenEl.querySelector('.cqv-q-progress'); if (progress) ghost.appendChild(progress.cloneNode(true));
      const scroll = stage.scrollTop; ghost.appendChild(stage);
      ghost.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
      root.appendChild(ghost); stage.scrollTop = scroll; ghostEl = ghost; return ghost;
    }
    const CARD_PHOTO = '.cqq-pstat-photo img, .cqq-factcard-photo img, .cqw-narrative-photo img';
    function settle(stage, ms) {
      const seen = new WeakMap(), cleanup = [];
      let stopped = false, timeout;
      const inspect = () => {
        if (stopped) return Promise.resolve();
        const images = Array.from(stage.querySelectorAll('img')).filter(im => {
          if (!im.getClientRects().length || (im.loading === 'lazy' && !im.matches(CARD_PHOTO))) return false;
          im.loading = 'eager';
          const source = [im.currentSrc, im.src, im.srcset, im.sizes].join('|');
          let sources = seen.get(im);
          if (!sources) { sources = new Set(); seen.set(im, sources); }
          if (sources.has(source)) return false;
          sources.add(source); return true;
        });
        if (!images.length) return Promise.resolve();
        return Promise.allSettled(images.map(im => {
          const loaded = im.complete ? Promise.resolve() : new Promise(done => {
            const finish = () => { remove(); done(); };
            const remove = () => { im.removeEventListener('load', finish); im.removeEventListener('error', finish); };
            cleanup.push(remove);
            im.addEventListener('load', finish, {once:true}); im.addEventListener('error', finish, {once:true});
          });
          // Cached files still need decoded pixels; an early decode rejection alone is not readiness.
          let decoded = Promise.resolve();
          if (im.decode) { try { decoded = im.decode(); } catch (_) {} }
          return Promise.allSettled([loaded, decoded]);
        })).then(inspect);
      };
      // Fallback nodes and changed sources share one deadline. Navigation must not cancel its cleanup.
      return Promise.race([
        inspect(), new Promise(done => { timeout = win.setTimeout(done, ms); })
      ]).finally(() => { stopped = true; win.clearTimeout(timeout); cleanup.forEach(remove => remove()); });
    }
    function renderScreen(id, opts) {
      opts = opts || {};
      const s = materialize(id), t = typeOf(s);
      if (!s || !t) { warn('unknown screen or type', id, s && s.type); return; }
      const token = ++navToken, oldStage = screenEl.querySelector('[data-cqq-stage]');
      const animate = !calm() && !instantInteraction() && !opts.instant && (!!opts.outgoing || (oldStage && oldStage.childElementCount > 0));
      const outgoing = opts.outgoing || (oldStage && oldStage.childElementCount > 0 ? freeze(oldStage) : null);
      const stage = doc.createElement('div'); stage.className = 'cqv-q-stage'; stage.setAttribute('data-cqq-stage', '');
      const kind = plateKind(s); stage.innerHTML = plateHTML(kind) + t.render(s, tAPI);
      if (kind) stage.classList.add('has-plate');
      let revealed = false;
      const plate = stage.querySelector(':scope > .cqq-plate');
      if (plate) {
        const im = plate.querySelector('img');
        if (!(im.complete && im.naturalWidth)) {
          plate.classList.add('is-pending');
          im.addEventListener('load', () => { if (revealed) plate.classList.add('is-late'); plate.classList.remove('is-pending'); }, {once:true});
          im.addEventListener('error', () => plate.remove(), {once:true});
        }
      }
      screenEl.className = screenClass(s) + (s.type ? ' cqq-' + s.type : '') + (s.look ? ' cqq-look-' + s.look : '') + (s.type === 'chapter' && s.compare ? ' cqq-look-compare' : '') + (kind ? ' cqq-plate-' + kind : '');
      screenEl.setAttribute('data-cqq-screen', s.id); screenEl.setAttribute('aria-labelledby', tAPI.id('title'));
      if (s.type === 'loader') screenEl.setAttribute('aria-busy', 'true'); else screenEl.removeAttribute('aria-busy');
      if (oldStage && oldStage.parentNode === screenEl) oldStage.replaceWith(stage);
      else screenEl.insertBefore(stage, screenEl.querySelector('[data-cqq-announce]'));
      paintProgress(s.id, opts.dir);
      if (s.type !== 'result') syncScroll(s);
      if (t.bind) { try { t.bind(stage, s, tAPI, controller); } catch (e) { warn('bind failed', e); } }
      if (s.type === 'result') syncScroll(s);
      // Fitting and focus happen under the unchanged outgoing layer, before images are settled and revealed.
      after(s, stage, opts);
      const removeOutgoing = () => {
        if (outgoing) outgoing.remove();
        if (ghostEl === outgoing) ghostEl = null;
        if (startMoment && startMoment.element === outgoing) startMoment.cancel();
      };
      const reveal = () => {
        if (token !== navToken || !stage.isConnected) return;
        if (s.type === 'loader') runLoader(s, stage);
        if (calm()) { if (def.statMotion) runScreenMotion(s, stage, false); removeOutgoing(); return; }
        revealed = true;
        if (plate) { const im = plate.querySelector('img'); if (im && im.complete && im.naturalWidth) plate.classList.remove('is-pending', 'is-late'); }
        stage.querySelectorAll('img').forEach(im => {
          if (im.loading === 'lazy' || !im.getClientRects().length || (im.complete && im.naturalWidth)) return;
          im.classList.add('cqq-img-wait');
          // a picture that arrives while the first view is still hidden (cqq-fonts-pending) shows with it, no fade
          const loaded = () => { if (!root.classList.contains('cqq-fonts-pending')) im.classList.add('cqq-img-late'); im.classList.remove('cqq-img-wait'); };
          im.addEventListener('load', loaded, {once:true}); im.addEventListener('error', loaded, {once:true});
        });
        if (outgoing && animate) {
          // Cached pictures can settle in the same task; establish opacity 1 before starting its transition.
          win.getComputedStyle(outgoing).opacity;
          outgoing.classList.add('is-out'); later(removeOutgoing, swapMs() + 60);
        } else removeOutgoing();
        const col = stage.querySelector('.cqv-q-col');
        if (animate && col) {
          col.classList.add('is-entering');
          const enterMs = cssTimeMs(win.getComputedStyle(root).getPropertyValue('--cqq-enter-ms'), 320);
          later(() => col.classList.remove('is-entering'), enterMs + 60);
        }
        runScreenMotion(s, stage, animate);
      };
      // Keep the previous frame during the bounded image wait, including with reduced motion.
      const cardPhoto = Array.from(stage.querySelectorAll(CARD_PHOTO)).some(im => im.getClientRects().length);
      if (outgoing) settle(stage, cardPhoto ? 700 : 300).then(reveal); else reveal();
    }
    function after(s, stage, opts) {
      if (opts.focus !== false) {
        const h = stage.querySelector('h1[tabindex], h1');
        if (h) { if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1'); try { h.focus({preventScroll:true}); } catch (_) { h.focus(); } }
      }
      stage.scrollTop = 0;
      if (s.type === 'result') resultViewed = true;
      fitAll();
      // Every image already has a frame. A late decode can update its crop, never refit surrounding text or panels.
      stage.querySelectorAll('img').forEach(im => { if (!im.complete) im.addEventListener('load', () => {
        const fig = im.closest('[data-cqq-safe]'); if (fig && stage.isConnected) fitMedia(fig);
      }, {once:true}); });
      // A pure path lookup preserves Change an answer mode while warming both likely next screens.
      if (win.requestIdleCallback) warmIdle = win.requestIdleCallback(() => { warmIdle = 0; warmAhead(s.id); }, {timeout:100});
      else later(() => warmAhead(s.id), 0);
      fitCheck();
    }

    // Headings reveal word by word with the shared CSS timing tokens (AJ's slower pacing, 2026-10-01) instead of
    // the old left-to-right sweep, on every screen change: question titles, chapter and in-between moments, the
    // matching screen, the result heading and the entry headline when it is shown again. The first view on page load
    // stays still (it appears finished, D2). Figures and capacity bars share one count-up clock.
    // Word wrappers stay in place for this screen's lifetime. Unwrapping them after the reveal changed kerning and
    // made the heading match the generic entrance selector again, causing a second fade from zero. A hidden copy
    // carries the complete accessible heading; visual pieces are aria-hidden. No-break groups stay together.
    const WORD_UNIT = '.cqq-fig, .cqv-q-figure, .cqq-count-value, .cqq-nw, img, svg, br';
    let wordUndo = [];
    function finishWords() { const list = wordUndo; wordUndo = []; list.forEach(fn => { try { fn(); } catch (_) {} }); }
    function revealWords(targets, opts) {
      opts = opts || {};
      targets = targets.filter(el => el && el.isConnected && !el.querySelector('a, button, input, select, textarea, .cqq-words-sr'));
      if (!targets.length || calm()) return 0;
      const units = [];
      targets.forEach(h => {
        const sr = doc.createElement('span'); sr.className = 'cqv-sr-only cqq-words-sr';
        // the heading's own words (not innerText, which would carry CSS uppercase), a space where a block part ends
        const textOf = el => Array.from(el.childNodes).map(n => {
          if (n.nodeType === 3) return n.textContent;
          if (n.nodeType !== 1) return '';
          if (n.matches('br')) return ' ';
          const disp = win.getComputedStyle(n).display;
          if (disp === 'none') return '';
          return /^(inline|inline-block|contents)$/.test(disp) ? textOf(n) : ' ' + textOf(n) + ' ';
        }).join('');
        sr.textContent = textOf(h).replace(/\s+/g, ' ').trim();
        const walk = node => Array.from(node.childNodes).forEach(n => {
          if (n.nodeType === 3) {
            const parts = n.textContent.split(/([ \t\n\r\f]+)/);
            if (!parts.some(p => p && !/^[ \t\n\r\f]+$/.test(p))) return;
            const frag = doc.createDocumentFragment();
            parts.forEach(p => {
              if (!p) return;
              if (/^[ \t\n\r\f]+$/.test(p)) { frag.appendChild(doc.createTextNode(p)); return; }
              const w = doc.createElement('span'); w.className = 'cqq-w'; w.setAttribute('aria-hidden', 'true'); w.textContent = p;
              frag.appendChild(w); units.push(w);
            });
            n.replaceWith(frag);
          } else if (n.nodeType === 1) {
            if (n.matches(WORD_UNIT)) {
              if (n.matches('br')) return;
              const inline = win.getComputedStyle(n).display === 'inline';
              n.classList.add('cqq-wu'); n.setAttribute('aria-hidden', 'true'); if (inline) n.style.display = 'inline-block';
              units.push(n);
            } else walk(n);
          }
        });
        walk(h);
        h.insertBefore(sr, h.firstChild);
        h.classList.add('cqq-words');
      });
      // Read the heading's inherited screen timings so CSS and completion share the same bounded sequence.
      const motion = win.getComputedStyle(targets[0]);
      const preferredStep = cssTimeMs(motion.getPropertyValue('--cqq-word-step'), 110);
      const maxSpan = cssTimeMs(motion.getPropertyValue('--cqq-word-span'), 700);
      const step = Math.min(preferredStep, Math.floor(maxSpan / Math.max(1, units.length - 1)));
      units.forEach((u, i) => { u.style.setProperty('--cqq-wi', String(i + (opts.offset || 0))); });
      targets.forEach(h => h.style.setProperty('--cqq-word-step', step + 'ms'));
      const wordMs = cssTimeMs(motion.getPropertyValue('--cqq-word-ms'), 650);
      const total = wordMs + step * Math.max(0, units.length - 1 + (opts.offset || 0));
      let done = false;
      const finish = () => { if (done) return; done = true; targets.forEach(h => h.classList.add('cqq-words-done')); };
      wordUndo.push(finish);
      (opts.timer || later)(() => { wordUndo = wordUndo.filter(fn => fn !== finish); finish(); }, total + 40);
      return total;
    }
    function runScreenMotion(s, stage, animate) {
      // A proportion rail explains percentage/ratio figures while their sourced wording remains untouched.
      // It is present in the static state too; no count-up is applied to bounds such as "more than".
      if (def.statMotion && (s.type === 'stat' || s.type === 'reaction') && s.figure) {
        const raw = plain(resolve(s.figure)).trim();
        const percent = /^(?:about\s+|nearly\s+)?(\d+(?:\.\d+)?)\s*(?:%|percent)$/i.exec(raw);
        const ratio = /^(\d+)\s+in\s+(\d+)$/i.exec(raw);
        const half = /^about half$/i.test(raw);
        const fraction = percent ? Number(percent[1]) / 100 : ratio && Number(ratio[2]) ? Number(ratio[1]) / Number(ratio[2]) : half ? .5 : null;
        const figure = stage.querySelector('.cqq-fig, .cqv-q-figure');
        if (figure && fraction != null && fraction >= 0 && fraction <= 1) {
          const visual = doc.createElement('span'); visual.className = 'cqq-stat-visual'; visual.setAttribute('aria-hidden', 'true');
          const fill = doc.createElement('span'); fill.className = 'cqq-stat-fill'; fill.style.width = (fraction * 100) + '%';
          visual.appendChild(fill); figure.after(visual);
        }
      }
      if (calm() || instantInteraction()) return;
      // Explicitly selected inline statistics count in place; never infer a target from arbitrary prose.
      // Keep the whole title visible so a word reveal cannot conceal the short numeric transition.
      let inlineMotion = false;
      if (def.statMotion && s.count !== false && typeof s.motionNumber === 'number' && Number.isFinite(s.motionNumber) && s.motionNumber > 0) {
        for (const emphasis of stage.querySelectorAll('.cqv-q-col h1 em')) {
          const node = Array.from(emphasis.childNodes).find(n => n.nodeType === 3 && Array.from(n.textContent.matchAll(/\d[\d,]*(?:\.\d+)?/g)).some(m => Number(m[0].replace(/,/g, '')) === s.motionNumber));
          if (!node) continue;
          const raw = node.textContent, match = Array.from(raw.matchAll(/\d[\d,]*(?:\.\d+)?/g)).find(m => Number(m[0].replace(/,/g, '')) === s.motionNumber);
          const number = doc.createElement('span'); number.setAttribute('data-cqq-motion-number', ''); number.textContent = match[0];
          const fragment = doc.createDocumentFragment();
          fragment.appendChild(doc.createTextNode(raw.slice(0, match.index))); fragment.appendChild(number);
          fragment.appendChild(doc.createTextNode(raw.slice(match.index + match[0].length))); node.replaceWith(fragment);
          inlineMotion = true; break;
        }
      }
      if (animate && !inlineMotion) {
        const col = stage.querySelector('.cqv-q-col');
        const title = stage.querySelector('.cqv-q-col p.cqv-q-title') || stage.querySelector('.cqv-q-col h1');
        if (title) {
          const lead = stage.querySelector('.cqq-card-lead');
          // the card lead reads before the figure on the "your number" card, so its words go first
          revealWords(lead && (lead.compareDocumentPosition(title) & 4) ? [lead, title] : [title]);
          if (col) col.classList.add('cqq-has-reveal');
        }
      }
      const counts = [];
      stage.querySelectorAll('.cqq-fig, .cqv-q-figure, [data-cqq-motion-number]').forEach(el => {
        if (s.count === false || el.closest('[data-cqq-nocount]')) return;
        const text = el.textContent.trim();
        // Ratios, bounds and prose retain their exact meaning throughout the transition.
        const countText = el.hasAttribute('data-cqq-motion-number') ? text : def.statMotion && s.figure ? plain(resolve(s.figure)).trim() : text;
        if (!/^\d[\d,]*(?:\.\d+)?(?:\s*(?:%|percent|gpg|grains?))?$/i.test(countText)) return;
        const node = Array.from(el.childNodes).find(n => n.nodeType === 3 && /\d/.test(n.textContent));
        if (!node) return;
        const raw = node.textContent, m = /\d[\d,]*(?:\.\d+)?/.exec(raw); if (!m) return;
        const value = Number(m[0].replace(/,/g, '')); if (!Number.isFinite(value) || value <= 0) return;
        const box = doc.createElement('span'), final = doc.createElement('span'), display = doc.createElement('span');
        box.className = 'cqq-count-value is-counting'; final.className = 'cqq-count-final'; final.textContent = m[0];
        display.className = 'cqq-count-display'; display.setAttribute('aria-hidden', 'true'); display.textContent = '0';
        box.appendChild(final); box.appendChild(display);
        const fragment = doc.createDocumentFragment();
        fragment.appendChild(doc.createTextNode(raw.slice(0, m.index))); fragment.appendChild(box);
        fragment.appendChild(doc.createTextNode(raw.slice(m.index + m[0].length))); node.replaceWith(fragment);
        counts.push({box, display, match:m[0], value, decimals:(m[0].split('.')[1] || '').length});
      });
      const bars = Array.from(stage.querySelectorAll('.cqq-cap-fill, .cqq-stat-fill'));
      const finish = () => { counts.forEach(c => { c.box.classList.remove('is-counting'); c.display.remove(); }); bars.forEach(bar => { bar.style.transform = ''; }); finishCounts = null; countFrame = 0; };
      if (!counts.length && !bars.length) return;
      finishCounts = finish; const started = win.performance.now() + (def.statMotion ? 0 : 120);
      const tick = now => {
        if (calm() || instantInteraction() || doc.hidden || !stage.isConnected) { finish(); return; }
        const t = Math.max(0, Math.min(1, (now - started) / (def.statMotion ? 500 : 1200))), eased = def.statMotion ? 1 - Math.pow(1 - t, 3) : t * t * (3 - 2 * t);
        counts.forEach(c => { c.display.textContent = (c.value * eased).toLocaleString('en-US', {minimumFractionDigits:c.decimals, maximumFractionDigits:c.decimals, useGrouping:c.match.indexOf(',') >= 0}); });
        bars.forEach(bar => { bar.style.transform = 'scaleX(' + eased + ')'; });
        if (t < 1) countFrame = win.requestAnimationFrame(tick); else finish();
      };
      tick(started - (def.statMotion ? 0 : 120));
    }

    /* ---------- fitting: whole photographs, one-line figures, label badges and matched-for chips ---------- */
    const box4 = v => { const a = String(v || '').trim().split(/[\s,]+/).map(Number); return a.length === 4 && a.every(Number.isFinite) ? a.map(x => x / 100) : null; };
    // AJ's no-crop correction: show the entire selected photograph. Phones use the existing wide
    // source in a stable frame; desktop photos fit inside their split column. Source choice follows
    // the same container breakpoint as the layout, never the height produced by fitting it.
    function fitMedia(fig) {
      if (fig.classList.contains('cqq-media-outgoing')) return;
      const img = fig.querySelector(':scope > img.cqq-fit-img, :scope > picture > img.cqq-fit-img'); if (!img) return;
      const d = fig.dataset, source = fig.querySelector(':scope > picture > source');
      // Classic scrollbars reduce the quiz container width without changing viewport media queries.
      // Keep its responsive picture in step with the container-based stacked/split layout.
      const mediaScreen = fig.closest('.cqv-q');
      if (source && mediaScreen) {
        const media = isSplit(mediaScreen) ? 'not all' : 'all';
        if (source.media !== media) source.media = media;
      }
      const phone = source && win.matchMedia(source.media).matches;
      const suffix = phone ? (d.cqqArPhone ? 'Phone' : 'Band') : '';
      const ar = Number(d['cqqAr' + suffix]) || (img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0);
      if (!ar) return;
      fig.style.setProperty('--cqq-image-ar', ar);
      fig.classList.add('cqq-whole-image');
      fig.classList.remove('is-contained');
      fig.style.backgroundColor = '';
      fig.style.removeProperty('--cqq-fill');
      img.style.objectFit = 'contain'; img.style.objectPosition = '50% 50%';
      const css = win.getComputedStyle(fig);
      const fw = fig.clientWidth, fh = Math.max(0, fig.clientHeight - parseFloat(css.paddingBottom || 0));
      // Stacked layouts fill the frame (CSS object-fit: cover, AJ 2026-10-10): size the request for the covered width.
      const covers = win.getComputedStyle(img).objectFit === 'cover';
      // The card overlaps the frame's bottom strip (--cqq-card-lap); the picture fills only the band above it, placed so
      // the product box (data-cqq-safe) and a 4% margin under it (tank bases, the floor) stay in view, then centred
      // across on the box. Outside stacked layouts nothing here runs.
      const lap = covers ? parseFloat(css.getPropertyValue('--cqq-card-lap')) || 0 : 0;
      const band = Math.max(1, fh - lap);
      const renderW = covers ? Math.max(fw, band * ar) : Math.min(fw, fh * ar);
      if (covers) {
        const sb = box4(d['cqqSafe' + suffix]), rh = renderW / ar, ox = renderW - fw, oy = rh - band;
        if (sb) {
          const ty = oy > 0.5 ? Math.min(0, Math.max(-oy, band - Math.min(1, sb[3] + 0.04) * rh)) : 0;
          const tx = ox > 0.5 ? Math.min(0, Math.max(-ox, fw / 2 - (sb[0] + sb[2]) / 2 * renderW)) : 0;
          fig.style.setProperty('--cqq-cover-pos', (ox > 0.5 ? -tx / ox * 100 : 50).toFixed(2) + '% ' + (oy > 0.5 ? -ty / oy * 100 : 50).toFixed(2) + '%');
        } else fig.style.removeProperty('--cqq-cover-pos');
        const src = img.currentSrc || img.src;
        if (src) fig.style.setProperty('--cqq-fill-src', 'url("' + src.replace(/"/g, '%22') + '")');
      } else { fig.style.removeProperty('--cqq-cover-pos'); fig.style.removeProperty('--cqq-fill-src'); }
      // Reserve the decoded image's actual box, rather than letting its old intrinsic dimensions
      // participate for a frame during a responsive source or finish change.
      img.style.width = renderW + 'px'; img.style.height = renderW / ar + 'px';
      img.style.bottom = css.paddingBottom;
      // The picture spans its frame's full width: portrait tablets draw it edge to edge with square corners.
      fig.classList.toggle('cqq-bleed', renderW >= fw - 1);
      const widths = phone ? (source.getAttribute('srcset') || '').match(/\d+(?=w(?:,|$))/g) : null;
      const sz = snappedSizes(renderW, widths && widths.length ? Math.max(...widths.map(Number)) : img.getAttribute('width'));
      if (img.sizes !== sz) img.sizes = sz;
      if (source && source.sizes !== sz) source.sizes = sz;
      if (img.dataset.cqqSrcset) { img.srcset = img.dataset.cqqSrcset; delete img.dataset.cqqSrcset; }
      if (img.dataset.cqqSrc) { img.src = img.dataset.cqqSrc; delete img.dataset.cqqSrc; }
      // Keep the entry's floating card clear of the actual product inside the uncropped image.
      const card = fig.querySelector('.cqv-q-profile'), box = box4(d['cqqSafe' + suffix]);
      if (card && box) {
        const r = fig.getBoundingClientRect(), cr = card.getBoundingClientRect();
        const w = renderW, h = w / ar, x = r.left + (fw - w) / 2, y = r.top + (fh - h) / 2;
        card.classList.toggle('is-hidden', cr.width > 0 && cr.left < x + box[2] * w && cr.right > x + box[0] * w && cr.top < y + box[3] * h && cr.bottom > y + box[1] * h);
      }
    }
    // Big figures stay on one line: the type steps down until the figure fits its column (never an accidental
    // "About / 25,000" wrap); only a figure too long for the smallest step may wrap, between words.
    function fitLines(scope) {
      scope.querySelectorAll('[data-cqq-fitline]').forEach(el => {
        el.style.fontSize = ''; el.classList.remove('is-wrapping');
        const min = Number(el.dataset.cqqFitline) || 30;
        let fs = parseFloat(win.getComputedStyle(el).fontSize) || 48, guard = 0;
        while (el.scrollWidth > el.clientWidth + 1 && fs > min && guard++ < 40) { fs = Math.max(min, fs - 2); el.style.fontSize = fs + 'px'; }
        if (el.scrollWidth > el.clientWidth + 1) el.classList.add('is-wrapping');
      });
    }
    // The badge text ("Complete bundle") joins the configuration line; if that line would need a third line, it goes
    function fitBadges(scope) {
      scope.querySelectorAll('.cqq-label-badge').forEach(b => {
        b.hidden = false;
        const lab = b.parentElement, cs = win.getComputedStyle(lab);
        const lh = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) || 13) * 1.45;
        if (lab.getBoundingClientRect().height > lh * 2.4) b.hidden = true;
      });
    }
    // The scale's "You: 12 gpg" chip stays inside the card; the dot marks the value exactly
    function fitScales(scope) {
      scope.querySelectorAll('.cqq-scale').forEach(sc => {
        const bar = sc.querySelector('.cqq-scale-bar'), chip = sc.querySelector('.cqq-scale-chip'); if (!bar || !chip) return;
        const W = bar.clientWidth, x = Number(sc.dataset.x) / 100 * W, half = chip.offsetWidth / 2;
        chip.style.left = Math.min(W - half, Math.max(half, x)) + 'px';
      });
    }
    // A short screen may shed the optional note. Keep the full picture and let the existing stage
    // scroll when necessary, so neither the image nor the controls is sacrificed to a fixed height.
    function fitResult(stage) {
      if (!screenEl.classList.contains('cqv-q-result')) return;
      screenEl.classList.remove('cqq-tight', 'cqq-tighter');
      if (root.classList.contains('is-scroll') || isSplit(screenEl)) return;
      const over = () => stage.scrollHeight - stage.clientHeight > 1;
      if (over()) screenEl.classList.add('cqq-tight');
    }
    // "Share my match" adds no height: when the eyebrow row would wrap (a long eyebrow such as "Your match, from your
    // report" on a phone), the link shortens to "Share", and if that still wraps it waits below the fold
    function fitEyebrow(stage) {
      const row = stage.querySelector('.cqq-result-eyebrow'), sh = row && row.querySelector('.cqq-share-btn');
      if (!sh) return;
      sh.textContent = S.shareFirst; sh.hidden = false; sh.removeAttribute('aria-label');
      const one = () => { const p = row.querySelector('.cqv-eyebrow'); if (!p || win.getComputedStyle(p).display === 'none') return true; const lh = parseFloat(win.getComputedStyle(p).lineHeight) || 21; return p.getBoundingClientRect().height <= lh * 1.5; };
      if (one()) return;
      sh.textContent = S.shareShort; sh.setAttribute('aria-label', S.shareFirst);
      if (!one()) sh.hidden = true;
    }
    function fitAll() {
      const stage = screenEl.querySelector('[data-cqq-stage]'); if (!stage) return;
      fitEyebrow(stage);
      stage.querySelectorAll('[data-cqq-safe]').forEach(fitMedia);
      fitResult(stage);
      // product pictures in the sections under the first view (the landing's example match) follow the same rule
      moreEls.concat([resultMore]).forEach(el => { if (!el.hidden) el.querySelectorAll('[data-cqq-safe]').forEach(fitMedia); });
      fitLines(stage); fitBadges(stage); fitScales(stage);
      root.querySelectorAll('dialog[open] [data-cqq-safe]').forEach(fitMedia);
      root.querySelectorAll('dialog[open] .cqq-good-next').forEach(compactNext);
    }
    let fitRaf = 0;
    win.addEventListener('resize', () => { win.cancelAnimationFrame(fitRaf); fitRaf = win.requestAnimationFrame(fitAll); });
    function later(fn, ms) { const id = win.setTimeout(fn, ms); timers.push(id); return id; }
    function clearTimers() {
      clearPhotoSlots();
      root.querySelectorAll('img[data-cqq-slot-state="observing"]').forEach(im => { delete im.dataset.cqqSlotReady; delete im.dataset.cqqSlotState; });
      navToken++; // Decode promises are not cancellable; invalidate any reveal already waiting on them.
      timers.forEach(id => win.clearTimeout(id)); timers = [];
      if (countFrame) win.cancelAnimationFrame(countFrame);
      if (finishCounts) finishCounts();
      // the outgoing stage becomes the frozen layer as it is; its split heading is discarded with it
      wordUndo = [];
      if (startMoment) startMoment.cancel();
      if (warmIdle && win.cancelIdleCallback) win.cancelIdleCallback(warmIdle); warmIdle = 0;
      dropGhost();
    }

    /* ---------- fit guard (development only logs; production scrolls instead of clipping) ---------- */
    let lastOverflow = null;
    function fitCheck() {
      if (!debug) return;
      win.requestAnimationFrame(() => win.requestAnimationFrame(() => {
        const stage = screenEl.querySelector('[data-cqq-stage]'); if (!stage) return;
        const over = Math.max(0, stage.scrollHeight - stage.clientHeight);
        const overX = Math.max(0, doc.documentElement.scrollWidth - doc.documentElement.clientWidth);
        lastOverflow = {screen:st.current, y:over, x:overX, w:screenEl.clientWidth, h:screenEl.clientHeight};
        root.dataset.cqqOverflow = over > 1 || overX > 1 ? (st.current + ':' + over + 'x' + overX) : '';
        if (over > 1 || overX > 1) win.console.warn('[cqq] overflow on "' + st.current + '": ' + over + ' px down, ' + overX + ' px across at ' + win.innerWidth + 'x' + win.innerHeight);
      }));
    }
    if (debug && 'ResizeObserver' in win) { let rt = 0; new win.ResizeObserver(() => { win.clearTimeout(rt); rt = win.setTimeout(fitCheck, 150); }).observe(screenEl); }

    /* ---------- navigation ---------- */
    function firstGap(id) {
      // never show a screen whose earlier questions are unanswered (deep links, stale history after a retake)
      const p = pathScreens(), i = p.findIndex(s => s.id === id);
      if (i < 0) return id;
      const gap = p.slice(0, i).find(s => isQuestion(s) && !st.done[s.id]);
      return gap ? gap.id : id;
    }
    function nearestOnPath(id) {
      const p = pathIds(); if (p.includes(id)) return id;
      const o = order(id); const before = p.filter(x => order(x) < o);
      return before.length ? before[before.length - 1] : p[0];
    }
    function go(id, opts) {
      opts = opts || {};
      if (!byId.has(id)) { warn('no screen', id); return; }
      id = firstGap(nearestOnPath(id));
      const initial = navToken === 0;
      clearTimers(); closeDialogs();
      const from = st.current;
      const dir = opts.dir || (from && order(id) < order(from) ? -1 : 1);
      // "Change an answer" mode only lives while moving between questions; leaving it (Back, playback) ends it
      if (st.editing && !isQuestion(byId.get(id))) st.editing = null;
      st.current = id;
      if (id !== firstId()) st.started = true;
      invalidate();
      // leaving a scrolled landing or result: the next screen starts at the top, without a visible jump
      const scrolled = root.scrollTop > 0 || win.scrollY > 0;
      if (scrolled) { root.scrollTop = 0; win.scrollTo({top:0, left:0, behavior:'instant'}); }
      renderScreen(id, {dir, focus:opts.focus, instant:!!opts.instant && initial, outgoing:opts.outgoing});
      const hist = opts.history || 'push';
      try {
        const base = win.history.state && typeof win.history.state === 'object' ? win.history.state : {};
        const n = hist === 'push' ? ((base.cqq && base.cqq.n) || 0) + 1 : ((base.cqq && base.cqq.n) || 0);
        const entry = Object.assign({}, base, {cqq:{id, n, quiz}});
        if (hist === 'push') win.history.pushState(entry, ''); else if (hist === 'replace') win.history.replaceState(entry, '');
        if (hist !== 'none') histN = n;
      } catch (_) {}
      save();
      const s = materialize(id);
      const backLink = root.querySelector('.cqv-quiz-back');
      if (backLink) backLink.setAttribute('aria-label', (s && s.type === 'landing') || id === firstId() ? (backLink.dataset.cqqBackLabel || S.back) : S.backPrev);
      track('cq_quiz_screen_view', {variant:s && s.variantId || null});
      if (s && s.type === 'landing') track('cq_quiz_landing_view');
      if (s && s.type === 'chapter') track('cq_quiz_chapter_view');
      if (s && INTERSTITIALS.has(s.type)) track('cq_quiz_interstitial_view', {variant:s.variantId || s.id});
      if (s && s.type === 'playback') track('cq_quiz_playback_view');
      if (s && s.type === 'loader') track('cq_quiz_matching_view');
    }
    function nextAfter(id) {
      const p = pathIds(), o = order(id);
      if (st.editing) {
        const follow = p.find(x => order(x) > o && isQuestion(byId.get(x)) && !st.done[x]);
        if (follow) return follow;
        const back = st.editing.returnTo; st.editing = null;
        if (back && p.includes(back)) return back;
        const pb = p.find(x => byId.get(x).type === 'playback');
        return pb || p[p.length - 1];
      }
      return p.find(x => order(x) > o) || null;
    }
    function advance(opts) {
      opts = opts || {};
      const target = opts.goto && byId.has(opts.goto) ? opts.goto : nextAfter(st.current);
      if (!target) return;
      if (opts.goto && st.editing) st.editing = null;
      go(target, {history:opts.history || 'push'});
    }
    function back() {
      if (cartBusy) return;
      const s = byId.get(st.current);
      if (!s || s.type === 'landing' || st.current === firstId()) { leave(); return; }
      track('cq_quiz_back');
      const hs = win.history.state;
      if (hs && hs.cqq && hs.cqq.n > 0 && hs.cqq.quiz === quiz) { win.history.back(); return; }
      const p = pathIds(), o = order(st.current);
      const prev = p.filter(x => order(x) < o && byId.get(x).type !== 'loader').pop();
      if (prev) go(prev, {history:'replace', dir:-1});
      else leave();
    }
    function leave() {
      const link = root.querySelector('.cqv-quiz-back');
      const href = root.dataset.cqqParent || (link && link.getAttribute('href')) || '/';
      if (st.started && !resultViewed) { track('cq_quiz_exit', {last:st.current}); exitSent = true; }
      win.location.assign(href);
    }
    let histN = 0;   // sequence number of the history entry on screen, so Back and Forward are told apart
    win.addEventListener('popstate', e => {
      const c = e.state && e.state.cqq;
      if (!c || c.quiz !== quiz) return;
      const dir = typeof c.n === 'number' && c.n !== histN ? (c.n < histN ? -1 : 1) : (order(c.id) < order(st.current) ? -1 : 1);
      histN = typeof c.n === 'number' ? c.n : histN;
      if (dir === -1) track('cq_quiz_back');
      let target = c.id;
      if (byId.get(target) && byId.get(target).type === 'loader') target = nextAfter(target) || target;
      // a Back onto a screen that has since left the path (e.g. the ZIP step after "Enter a number instead") would
      // land on the current screen again; skip that dead entry instead of making the customer press Back twice
      if (dir === -1 && target !== st.current && firstGap(nearestOnPath(target)) === st.current && c.n > 0) { win.history.back(); return; }
      go(target, {history:'none', dir});
    });

    /* ---------- committing answers ---------- */
    function clearOwned(screenId) { Object.keys(st.owner).forEach(k => { if (st.owner[k] === screenId) { delete st.owner[k]; delete st.values[k]; } }); }
    function setValues(obj, screenId) {
      Object.keys(obj || {}).forEach(k => {
        const v = obj[k];
        if (v === undefined || v === null) { delete st.values[k]; delete st.owner[k]; return; }
        st.values[k] = v;
        if (!st.owner[k] || !byId.has(st.owner[k]) || st.owner[k] === screenId || !isQuestion(byId.get(st.owner[k]))) st.owner[k] = screenId;
      });
    }
    function commit(screenId, values, extra) {
      st.config = null;
      clearOwned(screenId);
      setValues(values, screenId);
      if (extra) setValues(extra, screenId);
      st.done[screenId] = true;
      invalidate(); save();
    }
    function runAction(a, screenId) {
      if (!a) return;
      st.config = null;
      if (a.set) { setValues(resolve(a.set), screenId); invalidate(); }
      st.done[screenId] = true; save();
      advance({goto:a.goto});
    }

    /* ---------- dialogs ---------- */
    function closeDialogs() { root.querySelectorAll('dialog[open]').forEach(d => { try { d.close(); } catch (_) {} }); }
    function makeDialog(cls, title) {
      let d = root.querySelector('dialog.' + cls);
      if (!d) {
        d = doc.createElement('dialog'); d.className = 'cqv-dialog cqq-dialog ' + cls;
        d.setAttribute('aria-labelledby', 'cqq-' + cls + '-title');
        d.innerHTML = '<div class="cqv-dialog-head"><h2 id="cqq-' + cls + '-title" tabindex="-1"></h2><button type="button" class="cqv-dialog-close" data-cqq-close aria-label="' + esc(S.close) + '">' + esc(S.close) + '</button></div><div class="cqv-dialog-body" data-cqq-dialog-body></div><p class="cqv-sr-only" role="status" aria-live="polite" data-cqq-dialog-status></p>';
        d.addEventListener('click', e => { if (e.target === d) d.close(); });
        root.appendChild(d);
      }
      d.querySelector('h2').innerHTML = md(title || '');
      return d;
    }
    let dialogOpener = null;
    function openDialog(d) {
      dialogOpener = doc.activeElement;
      if (!d.open) { if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', ''); }
      const h = d.querySelector('h2'); if (h) { try { h.focus({preventScroll:true}); } catch (_) {} }
      d.addEventListener('close', () => { if (dialogOpener && dialogOpener.isConnected) { try { dialogOpener.focus({preventScroll:true}); } catch (_) {} } dialogOpener = null; }, {once:true});
    }

    /* ---------- answers list (Details: Your answers, playback: Change an answer) ---------- */
    function answerText(s, a) {
      if (s.answerText) return resolve(s.answerText, a);
      const key = s.key || s.id;
      if (s.type === 'single') { const v = a[key]; return v == null ? S.skipped : ctx.label(s.id, v); }
      if (s.type === 'multi') {
        const v = arr(a[key]);
        if (!v.length) return S.skipped;
        if (s.emptyValue != null && v.length === 1 && v[0] === s.emptyValue && !optionsOf(s).some(o => o.value === s.emptyValue)) return plain(resolve(s.emptyLabel)) || S.skipped;
        return ctx.labels(s.id, v).map((x, i) => i ? lowerFirst(x) : x).join(', ');
      }
      if (s.type === 'input') {
        const parts = arr(resolve(s.fields)).map(f => {
          const raw = a[f.key + 'Raw']; if (raw == null || raw === '') return null;
          const unitVal = a[f.key + 'Unit']; const u = f.units ? (f.units.find(x => x.value === unitVal) || f.units[0]).label : (f.unit || '');
          return (arr(resolve(s.fields)).length > 1 ? plain(f.label) + ' ' : '') + raw + (u ? ' ' + u : '');
        }).filter(Boolean);
        return parts.length ? parts.join(', ') : S.skipped;
      }
      return '';
    }
    function answersListHTML() {
      const a = answers();
      const rows = pathScreens().filter(isQuestion).map(s => '<li><div><span class="cqq-ans-q">' + esc(plain(resolve(s.shortTitle || s.title, a))) + '</span><span class="cqq-ans-a">' + esc(cleanDashes(answerText(s, a))) + '</span></div><button type="button" class="cqv-text-btn" data-cqq-do="edit" data-cqq-edit="' + esc(s.id) + '" aria-label="' + esc(S.changeOne + ': ' + plain(resolve(s.shortTitle || s.title, a))) + '">' + esc(S.changeOne) + '</button></li>').join('');
      return '<ol class="cqq-answers">' + rows + '</ol><div class="cqq-answers-foot"><button type="button" class="cqv-text-btn" data-cqq-do="retake">' + esc(S.retake) + '</button></div>';
    }
    function openAnswers() {
      const d = makeDialog('cqq-answers-dialog', S.answersTitle);
      d.querySelector('[data-cqq-dialog-body]').innerHTML = answersListHTML();
      openDialog(d);
      track('cq_quiz_change_answer', {from:st.current});
    }
    function edit(id) {
      const s = byId.get(id); if (!s) return;
      const returnTo = pathIds().find(x => byId.get(x).type === 'playback') || st.current;
      st.editing = {returnTo};
      closeDialogs();
      go(id, {history:'push', dir:-1});
    }
    function retake() {
      track('cq_quiz_retake');
      clearSaved(); selections.clear(); lastResult = null; resultViewed = false;
      st = fresh(); invalidate();
      go(firstId(), {history:'push', dir:-1});
    }

    /* ---------- commerce: selections and pricing ---------- */
    function axisIndex(part, key) {
      const labels = arr(part.labels).map(l => String(l).toLowerCase());
      const k = String(key).toLowerCase();
      if (/^\d+$/.test(k)) return Number(k);
      let i = labels.indexOf(k); if (i >= 0) return i;
      const alias = {size:/size/, finish:/structure|finish/, config:/configuration/, configuration:/configuration/, voltage:/voltage/, flow:/flow|gpm/, storage:/storage/, pump:/pump/, stand:/stand/, port:/port/, production:/ro size|production|gpd/, output:/output/};
      const re = alias[k]; if (!re) return -1;
      return labels.findIndex(l => re.test(l));
    }
    function matchValue(option, want) {
      if (typeof want === 'number') return parseFloat(String(option).replace(/,/g, '')) === want;
      const o = String(option).toLowerCase().trim(), w = String(want).toLowerCase().trim();
      return o === w || o.indexOf(w) === 0;
    }
    function applySelect(part, s, index, select) {
      if (!select) return true;
      const cur = part.variants.find(v => String(v.id) === String(s.selectedIds[index])) || part.variants[0];
      const wants = Object.keys(select).map(k => [axisIndex(part, k), select[k]]).filter(x => x[0] >= 0 && x[1] != null);
      if (!wants.length) return true;
      const cands = part.variants.filter(v => wants.every(([i, w]) => matchValue(v.options[i], w)));
      if (!cands.length) { warn('no variant matches', part.name, select); return false; }
      const wanted = new Set(wants.map(x => x[0]));
      const score = v => v.options.reduce((sum, val, j) => sum + (!wanted.has(j) && cur && val === cur.options[j] ? 10 : 0), 0) + (v.available ? 5 : 0);
      cands.sort((x, y) => score(y) - score(x));
      s.selectedIds[index] = cands[0].id;
      return cands[0].available !== false;
    }
    function buildSelection(p) {
      const C = commerce(); if (!p || !C) return null;
      const card = cardById.get(p.card); if (!card) { warn('unknown card', p.card); return null; }
      const make = () => {
        const s = C.state(card);
        Object.keys(p.enable || {}).forEach(k => { if (k in s.enabled) s.enabled[k] = !!p.enable[k]; });
        return s;
      };
      let s = make(), ok = applySelect(card.components[0], s, 0, p.select);
      Object.keys(p.componentSelect || {}).forEach(k => { const i = card.components.findIndex(c => (c.key || '') === k); if (i > 0) applySelect(card.components[i], s, i, p.componentSelect[k]); });
      let fellBack = false;
      if (!ok || !C.configuration(card, s).available) { const d = make(); if (C.configuration(card, d).available || !ok) { s = d; fellBack = true; } }
      const pairs = arr(p.pairs).map(q => buildSelection(Object.assign({}, q, {pairs:null}))).filter(Boolean);
      return {def:p, card, state:s, pairs, fellBack};
    }
    function pricing(b) {
      const C = commerce(); if (!b || !C) return null;
      const conf = C.configuration(b.card, b.state);
      const base = conf.components[0] || {};
      const labelFor = v => {
        const map = b.def.componentLabels || {};
        if (map[v.key]) return map[v.key];
        if (v.key === 'uv') return 'UV';
        if (v.key === 'ozone') return 'ozone stage';
        return cleanProduct(v.name);
      };
      const parts = [{label:b.def.label || cleanProduct(b.card.name), price:base.price * (base.quantity || 1)}]
        .concat(conf.components.filter(v => v.index > 0).map(v => ({label:labelFor(v), price:v.price * (v.quantity || 1)})));
      let total = conf.price, available = conf.available, items = conf.items.slice();
      b.pairs.forEach(q => {
        const pc = pricing(q);
        if (!pc) { available = false; return; }
        total = total == null || pc.price == null ? null : Math.round((total + pc.price) * 100) / 100;
        available = available && pc.available;
        items = items.concat(pc.items);
        pc.parts.forEach((x, i) => parts.push({label:i === 0 ? (q.def.label || x.label) : x.label, price:x.price}));
      });
      const title = /^default title$/i.test(String(base.title || '').trim()) ? '' : String(base.title || '');
      // priceLabel: a fixed string, or a function of the selected variant's option values (so Configure keeps it live)
      const label = typeof b.def.priceLabel === 'function' ? String(b.def.priceLabel(base.options || []) || '') : b.def.priceLabel != null ? b.def.priceLabel : cleanProduct(title.replace(/\s*\/\s*/g, ', '));
      return {price:total, available, items, parts, label, options:base.options || [], image:base.image, url:base.url, conf, card:b.card};
    }
    // Share catalog identities and choices only. Prices and cart lines always come from the current catalog.
    function selectionChoices(b) {
      return {card:b.card.id, ids:b.state.selectedIds.slice(), enabled:Object.assign({}, b.state.enabled), properties:Object.assign({}, b.state.properties), pairs:b.pairs.map(selectionChoices)};
    }
    function restoreChoices(b, choice) {
      if (!choice || choice.card !== b.card.id || !Array.isArray(choice.ids) || choice.ids.length !== b.card.components.length || !Array.isArray(choice.pairs) || choice.pairs.length !== b.pairs.length) return false;
      const state = commerce().state(b.card);
      for (let i = 0; i < b.card.components.length; i++) {
        const id = choice.ids[i];
        if (!((typeof id === 'number' || (typeof id === 'string' && /^\d{1,16}$/.test(id))) && Number.isSafeInteger(Number(id)) && Number(id) > 0)) return false;
        const part = b.card.components[i], v = part.variants.find(x => String(x.id) === String(id));
        if (!v) return false;
        state.selectedIds[i] = v.id;
      }
      Object.keys(state.enabled).forEach(k => { if (choice.enabled && typeof choice.enabled[k] === 'boolean') state.enabled[k] = choice.enabled[k]; });
      Object.keys(state.properties).forEach(k => { if (choice.properties && typeof choice.properties[k] === 'string') state.properties[k] = choice.properties[k]; });
      if (!commerce().configuration(b.card, state).available || !b.pairs.every((p, i) => restoreChoices(p, choice.pairs[i]))) return false;
      b.state = state;
      return true;
    }
    function breakdownText(parts) {
      if (!parts || parts.length < 2) return '';
      return parts.map((x, i) => (i === 0 ? upperFirst(x.label) : lowerFirst(x.label)) + ' ' + money(x.price)).join(' + ');
    }

    /* ---------- result ---------- */
    function computeResult(quiet) {
      const a = answers();
      const rs = pathScreens().find(s => s.type === 'result');
      let r = null;
      try { r = rs && rs.build ? rs.build(a, ctx) : (typeof def.recommend === 'function' ? def.recommend(a, ctx) : null); }
      catch (e) { if (!quiet) warn('recommend failed', e); r = null; }
      return r;
    }
    function prepareResult() {
      let r = computeResult() || {id:'EMPTY', title:'', why:[]};
      selections.clear();
      if (r.product) {
        let b = buildSelection(r.product);
        if (b && st.config && !restoreChoices(b, st.config)) { b = buildSelection(r.product); st.config = null; }
        const pr = b && pricing(b);
        if (!b || !pr || !pr.available) {
          const un = typeof def.unavailable === 'function' ? def.unavailable(r, ctx) : null;
          r = un || Object.assign({}, r, {
            id:'UNAVAILABLE', eyebrow:S.unavailableEyebrow, title:S.unavailableTitle, product:null,
            nameRow:{title:(r.product && r.product.name) || (b && b.card.name) || '', label:'', price:''},
            included:S.unavailableIncluded,
            actions:[{label:S.talk, do:'tel'}, {label:S.sendDetails, do:'contact', title:S.sendDetails}],
            details:Object.assign({}, r.details || {}, {alternative:null}, r.unavailableDetails || {}), unavailableFrom:r.id
          });
        } else {
          selections.set('primary', b);
          if (b.fellBack) warn('preselect fell back to the default configuration', r.product);
        }
      }
      if (r.details && r.details.alternative && r.details.alternative.product) {
        const ab = buildSelection(r.details.alternative.product);
        if (ab && pricing(ab) && pricing(ab).available) selections.set('alt', ab);
      }
      lastResult = r;
      return r;
    }
    function priceBlockHTML(pr) {
      return '<span class="cqv-price" data-cqq-price>' + esc(money(pr.price)) + '</span>';
    }
    // "Included: a, b" or "What happens next: ..." keeps its lead-in in bold, as in the design
    function leadIn(text) {
      const m = /^([A-Z][^:.]{0,40}:)\s+([\s\S]*)$/.exec(String(text || ''));
      return m ? '<b>' + md(m[1]) + '</b> ' + md(m[2]) : md(text);
    }
    function actionHTML(a, i) {
      const cls = i === 0 ? 'cqv-btn' : 'cqv-btn cqv-btn-ghost';
      const label = md(resolve(a.label));
      const trackAttr = a.track ? ' data-cqq-track="' + esc(a.track) + '"' : '';
      switch (a.do) {
        case 'tel': return '<a class="' + cls + '" href="' + esc(safeUrl(a.href) ? a.href : S.tel) + '">' + label + '</a>';
        case 'link': return '<a class="' + cls + '" href="' + esc(safeUrl(a.href) ? a.href : '#') + '"' + trackAttr + '>' + label + '</a>';
        case 'contact': return '<button type="button" class="' + cls + '" data-cqq-do="contact" data-cqq-index="' + i + '">' + label + '</button>';
        case 'details': return '<button type="button" class="' + cls + '" data-cqq-do="details"' + (a.tab ? ' data-cqq-tab="' + esc(a.tab) + '"' : '') + '>' + label + '</button>';
        default: return '<button type="button" class="' + cls + '" data-cqq-do="' + esc(a.do || 'add') + '">' + label + '</button>';
      }
    }
    // Share and "Email me my match" are offered on results that recommend a system (and on any result that sets
    // share:true, such as a test-first outcome); review outcomes show the report form instead (brief 4.3)
    const shareOn = r => !!r && (r.share === true || (r.share !== false && !!r.product && r.id !== 'UNAVAILABLE'));
    const emailMatchOn = r => !!r && !!hs() && r.emailMatch !== false && !!r.product && r.id !== 'UNAVAILABLE';
    // The badge text sits in front of the configuration line, never on the picture (brief 4.1)
    const labelHTML = (pr, badge) => (badge ? '<span class="cqq-label-badge">' + md(badge) + ' · </span>' : '') + '<span class="cqv-sr-only">' + esc(S.selectedConfig) + '</span>' + esc(pr.label);
    function selectedMedia(r, pr, b) {
      const media = r && sceneMedia(resolve(r.media)) || null;
      if (!media || !pr || !b) return media;
      // Optional definition-only media override. Resolve the current enabled components, including pairs,
      // after Configure or share restoration; this does not change prices, variants or cart lines.
      if (typeof def.resultMedia === 'function') {
        const components = [];
        const collect = selection => {
          commerce().configuration(selection.card, selection.state).components.forEach(c => components.push(Object.assign({}, c, {
            options:(c.options || []).slice(), card:selection.card.id,
            label:c.index === 0 ? (selection.def.label || cleanProduct(c.name)) : cleanProduct(c.name)
          })));
          (selection.pairs || []).forEach(collect);
        };
        collect(b);
        const custom = def.resultMedia(r, {components}, media);
        if (custom) return custom;
      }
      const tanks = [];
      [b].concat(b.pairs || []).forEach(selection => {
        const configured = commerce().configuration(selection.card, selection.state);
        configured.components.forEach(component => {
          const part = selection.card.components[component.index], axis = part && axisIndex(part, 'finish');
          if (axis >= 0) tanks.push(/stainless/i.test((component.options || [])[axis] || ''));
        });
      });
      if (!tanks.length || !tanks.every(Boolean)) return media;
      const fallback = {src:pr.image, alt:cleanProduct(b.card.name) + ', Stainless Steel product photo', product:true};
      if (!media.scene || arr(def.stainlessScenes).indexOf(media.scene) < 0) return fallback;
      const band = 'cq-v3-q-scene-' + media.scene + '-ss-band.webp';
      // Only registered bands are requested; new stainless bands carry their own crop metadata.
      const hasBand = !!(imageMap[band] || assetMap[band]);
      return Object.assign({}, media, {src:'cq-v3-q-scene-' + media.scene + '-ss.webp', band:hasBand ? band : null, containPhone:!hasBand, fallback});
    }
    function equipmentMediaHTML(media, cls) {
      return '<div class="' + cls + ' is-equipment" data-cqq-equipment-count="' + media.gallery.length + '"><div class="cqq-result-equipment">' + media.gallery.map(item =>
        '<figure class="cqq-equipment-item" data-cqq-component-id="' + esc(item.id) + '">' + (item.unavailable ? '<div class="cqq-equipment-unavailable"><strong>' + esc(item.label) + '</strong><span>Photo unavailable for this configuration</span></div>' : imgHTML(item, {eager:true, sizes:'(min-width: 821px) 260px, 160px'})) +
        '<figcaption><strong>' + esc(item.label) + (item.quantity > 1 ? ' × ' + esc(item.quantity) : '') + '</strong><span>' + esc(item.configuration) + '</span></figcaption></figure>'
      ).join('') + '</div></div>';
    }
    function resultMediaHTML(r, pr, b) {
      const media = selectedMedia(r, pr, b);
      if (!media) return '';
      if (Array.isArray(media.gallery) && media.gallery.length) return equipmentMediaHTML(media, 'cqv-q-result-media');
      if (media.product) {
        // a catalogue cut-out sits whole on the studio blue, never small on white (brief 4.4.5)
        if (!pr) return '';
        const ref = imgRef({src:pr.image, alt:media.alt || cleanProduct((r.product && r.product.name) || b.card.name) + ', product photo'});
        return '<figure class="cqv-q-result-media is-product">' + imgHTML(ref, {alt:ref.alt, eager:true, sizes:SPLIT_SIZES}) + '</figure>';
      }
      const d = imgRef(media), bd = media.band ? imgRef(media.band) : null;
      if (!d || !d.url) return '';
      // Advice-only outcomes follow the same whole-photo fit as the product results.
      d.safe = d.safe || '0 0 100 100';
      return '<figure class="cqv-q-result-media' + (d.safe ? '' : ' is-fixed') + '"' + focusStyle(d) + fitAttrs(d) + (media.containPhone ? ' data-cqq-contain-phone="true"' : '') + (bd ? fitAttrs(bd, true) : '') + '>' + pictureHTML(Object.assign({}, media, {w:d.w, h:d.h}), {eager:true, sizes:bd ? '(min-width: 821px) and (orientation: landscape) 50vw, 520px' : SPLIT_SIZES}) + '</figure>';
    }
    function resultHTML(s) {
      const r = prepareResult();
      const b = selections.get('primary'), pr = b && pricing(b);
      const media = sceneMedia(resolve(r.media)) || {};
      const badge = media.badge ? plain(resolve(media.badge)) : '';
      const mediaHTML = resultMediaHTML(r, pr, b);
      const links = (shareOn(r) ? '<button type="button" class="cqv-text-btn cqq-share-btn" data-cqq-do="share" data-cqq-share="first">' + esc(S.shareFirst) + '</button>' : '') +
        '<button type="button" class="cqv-text-btn cqq-details-btn" data-cqq-do="details" aria-haspopup="dialog">' + esc(S.details) + '</button>';
      // AJ 2026-10-11: as on the landing, the eyebrow ("Your match") is the screen's h1 and the headline a styled p.cqv-q-title
      const ebText = r.eyebrow ? md(resolve(r.eyebrow)) : '';
      const top = '<div class="cqv-q-result-top"><div class="cqq-result-eyebrow">' + (ebText ? '<h1 class="cqv-eyebrow" id="' + tAPI.id('title') + '" tabindex="-1">' + ebText + '</h1>' : '<span></span>') +
        '<span class="cqq-result-links">' + links + '</span></div>' +
        (ebText ? '<p class="cqv-q-title">' + md(resolve(r.title)) + '</p></div>' : '<h1 class="cqv-q-title" id="' + tAPI.id('title') + '" tabindex="-1">' + md(resolve(r.title)) + '</h1></div>');
      let name = '';
      if (pr) {
        const nm = cleanProduct(r.product.name || b.card.name) + (r.product.nameSuffix ? ' ' + r.product.nameSuffix : '');
        const bd = breakdownText(pr.parts);
        name = '<div class="cqv-q-result-name"><div><h2>' + md(nm) + '</h2></div>' +
          '<div class="cqq-result-price">' + priceBlockHTML(pr) + '</div><span class="cqv-price-label" data-cqq-price-label' + (badge ? ' data-cqq-badge="' + esc(badge) + '"' : '') + '>' + labelHTML(pr, badge) + '</span>' +
          '<span class="cqv-price-breakdown" data-cqq-breakdown' + (bd ? '' : ' hidden') + '>' + esc(bd) + '</span></div>';
      } else if (r.nameRow) {
        const nr = resolve(r.nameRow);
        name = '<div class="cqv-q-result-name"><div><h2>' + md(resolve(nr.title)) + '</h2>' + (nr.label ? '<span class="cqv-price-label">' + md(resolve(nr.label)) + '</span>' : '') + '</div>' +
          (nr.price ? '<div class="cqq-result-price"><span class="cqv-price">' + md(resolve(nr.price)) + '</span></div>' : '') + '</div>';
      }
      const why = arr(resolve(r.why)).filter(Boolean).slice(0, 3);
      const whyHTML = why.length ? '<ul class="cqv-checks cqv-q-why" aria-label="' + esc(S.whyLabel) + '">' + why.map(w => '<li>' + md(resolve(w)) + '</li>').join('') + '</ul>' : '';
      const inc = resolve(r.included);
      const incHTML = inc ? '<p class="cqv-q-included">' + (typeof inc === 'object' ? '<b>' + md(resolve(inc.label)) + '</b> ' + md(resolve(inc.text)) : leadIn(inc)) + '</p>' : '';
      // Keep definition actions and contact indices intact; only change their presentation.
      const actions = arr(resolve(r.actions)).slice(0, 2);
      const purchasePair = actions.some(a => a.do === 'add');
      const chatIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 3v-3H3V6a2 2 0 0 1 2-2Z"/></svg>';
      let acts, tools = '';
      if (purchasePair) {
        const addIndex = actions.findIndex(a => a.do === 'add');
        acts = actionHTML(actions[addIndex], 0);
        const specialistIndex = actions.findIndex(a => a.do === 'contact' || a.do === 'tel');
        const specialist = specialistIndex >= 0 ? actions[specialistIndex] : null;
        const help = chatIcon + '<span>Not sure? Ask a specialist to check it</span>';
        acts += specialist && specialist.do === 'contact'
          ? '<button type="button" class="cqv-text-btn cqq-specialist-action" data-cqq-do="contact" data-cqq-index="' + specialistIndex + '" aria-haspopup="dialog">' + help + '</button>'
          : '<a class="cqq-specialist-action" href="' + esc(specialist && safeUrl(specialist.href) ? specialist.href : S.tel) + '">' + help + '</a>';
        tools = actions.map((a, i) => a.do === 'configure' ? actionHTML(a, i) : '').join('');
        if (tools) tools = '<div class="cqq-result-tools">' + tools + '</div>';
      } else acts = actions.map(actionHTML).reverse().join('');
      const hasGuarantee = r.guarantee && r.id !== 'UNAVAILABLE';
      const more = resolve(r.noteMore), rawNote = resolve(r.note);
      // Both quiz definitions use this warranty sentence. On covered results it lives with the
      // program link instead; keep any other note text and every warranty-only result intact.
      const note = hasGuarantee && typeof rawNote === 'string' ? rawNote.replace(S.warrantyNote, '').trim() : rawNote;
      const noteHTML = (more || note) ? '<p class="cqv-q-note">' + (more ? '<span class="cqv-q-note-more">' + md(more) + ' </span>' : '') + (note ? md(note) : '') + '</p>' : '';
      const src = resolve(r.sources);
      // laptops only: one Guaranteed Performance line under the note (AJ ruling 2 wording, a property of the system)
      // one span, so the line's flex row holds two items (the check disc and the sentence) and the link stays inline
      const gp = hasGuarantee ? '<p class="cqq-gp-line"><span>' + md(S.guaranteeLine) + '</span></p>' : '';
      track('cq_quiz_result_view', {outcome:r.id, products:pr ? pr.items.map(i => i.id) : []});
      return '<div class="cqv-q-col">' + top + mediaHTML + '<div class="cqv-q-result-body">' + name + whyHTML + incHTML + (src ? '<p class="cqv-q-source cqq-result-sources">' + md(src) + '</p>' : '') +
        (acts ? '<div class="cqv-q-actions' + (purchasePair ? ' cqq-purchase-actions' : '') + '">' + acts + '</div>' : '') + tools + '<p class="cqv-cart-error" role="alert" data-cqq-cart-error hidden></p>' + noteHTML + gp + '</div></div>';
    }

    /* ---------- result: below the first view (brief 4.3; nothing here repeats the first view) ---------- */
    function sectionHead(title, lead, eyebrow) {
      return '<div class="cqq-sh">' + (eyebrow ? '<p class="cqq-eyebrow">' + md(eyebrow) + '</p>' : '') + '<h2 class="cqq-h2">' + md(title) + '</h2>' + (lead ? '<p class="cqq-intro">' + md(lead) + '</p>' : '') + '</div>';
    }
    // A review card (the in-quiz review screen, the result's owner reviews and the landing reviews row share it)
    function reviewCardHTML(rv, o) {
      o = o || {};
      if (!rv || !rv.quote) return '';
      const stars = Math.max(0, Math.min(5, Number(rv.stars == null ? 5 : rv.stars)));
      const date = rv.date ? (typeof rv.date === 'object' ? '<time datetime="' + esc(rv.date.iso || '') + '">' + esc(rv.date.text || '') + '</time>' : '<time>' + esc(rv.date) + '</time>') : '';
      const p = rv.product || null;
      const prod = p ? (p.href && safeUrl(p.href) ? '<a href="' + esc(p.href) + '">' + md(resolve(p.label)) + '</a>' : md(resolve(p.label))) : '';
      const thumbRef = p && p.image ? imgRef(typeof p.image === 'string' ? {src:p.image} : p.image) : null;
      // every card keeps the same footer: the reviewed product's studio picture where the theme has one of that exact
      // product, otherwise a plain water mark on the same studio tile (never a picture of a different product)
      const thumb = o.editorial ? '' : thumbRef && thumbRef.url ? '<span class="cqq-rev-thumb">' + imgHTML(Object.assign({}, thumbRef, {alt:''}), {sizes:'48px', ladder:[120], low:true}) + '</span>'
        : p ? '<span class="cqq-rev-thumb is-mark" aria-hidden="true"></span>' : '';
      const name = rv.name && !/^anonymous$/i.test(String(rv.name).trim()) ? '<b>' + md(resolve(rv.name)) + '</b>' : '';
      const meta = [date, prod].filter(Boolean).join(' · ');
      const quote = String(resolve(rv.quote) || '').trim();
      const q = /^[“"]/.test(quote) ? quote : '“' + quote + '”';
      return '<figure class="cqq-rev' + (o.editorial ? ' cqq-editorial-review' : '') + (o.cls ? ' ' + o.cls : '') + '"><div class="cqq-rev-top"><span class="cqv-stars cqq-stars" role="img" aria-label="Rated ' + stars + ' out of 5">' + '★'.repeat(stars) + '</span>' +
        '<span class="cqq-vpill">' + md(resolve(rv.pill) || S.verified) + '</span></div><blockquote>' + md(q) + '</blockquote>' +
        (rv.context ? '<p class="cqq-rev-context">' + md(resolve(rv.context)) + '</p>' : '') +
        ((name || meta || thumb) ? '<figcaption class="cqq-rev-who">' + thumb + '<span>' + name + (meta ? '<span class="cqq-rev-meta">' + meta + '</span>' : '') + '</span></figcaption>' : '') + '</figure>';
    }
    // the review shown inside the quiz this session is never repeated on the result (brief 2.5)
    function shownReviewKeys() {
      const keys = new Set();
      pathScreens().filter(s => s.type === 'testimonial').forEach(s => { const m = pickVariant(s, answers()) || s; [m.reviewId, m.variantId && s.id + ':' + m.variantId, plain(resolve(m.quote) || '').replace(/[“”"]/g, '').trim().slice(0, 60)].filter(Boolean).forEach(k => keys.add(k)); });
      return keys;
    }
    function belowReviews(list) {
      const seen = shownReviewKeys();
      return arr(resolve(list)).filter(rv => rv && rv.quote && !seen.has(rv.id) && !seen.has(rv.reviewId) && !seen.has(plain(resolve(rv.quote)).replace(/[“”"]/g, '').trim().slice(0, 60))).slice(0, 2);
    }
    function resultBelowHTML(r) {
      const rawBelow = resolve(r.below);
      if (rawBelow === false || r.id === 'EMPTY') return '';
      const below = rawBelow || {};
      const parts = [];
      // 1. Keep your match, or send it on (with the Guaranteed Performance card beside it on laptops)
      const gpCard = r.guarantee && r.id !== 'UNAVAILABLE' ? '<aside class="cqq-gp-card"><span class="cqq-gp-icon" aria-hidden="true"></span><div><p class="cqq-gp-title">' + esc(S.guaranteeTitle) + '</p><p>' + md(S.guaranteeText) + '</p></div></aside>' : '';
      if (shareOn(r)) {
        const url = shareURL();
        parts.push('<section class="cqq-sec is-paper cqq-keep-sec" aria-labelledby="cqq-keep-title"><div class="cqq-wrap cqq-keep-grid"><div class="cqq-keep"><p class="cqq-eyebrow">For your installer</p><h2 class="cqq-h2 is-card" id="cqq-keep-title">' + md(S.keepTitle) + '</h2><p class="cqq-intro">' + md(S.keepLead) + '</p>' +
          '<div class="cqq-keep-btns"><button type="button" class="cqv-btn" data-cqq-do="share" data-cqq-share="plumber">' + esc(S.sendPlumber) + '</button><button type="button" class="cqv-text-btn" data-cqq-do="share" data-cqq-share="copy">' + esc(S.copyLink) + '</button>' +
          (emailMatchOn(r) ? '<button type="button" class="cqv-text-btn cqq-email-match" data-cqq-do="email-match" aria-haspopup="dialog">' + esc(S.emailMatch) + '</button>' : '') + '</div>' +
          '<p class="cqq-share-status" role="status" aria-live="polite" data-cqq-share-status></p>' +
          '<label class="cqq-share-link" data-cqq-share-link hidden><span>' + esc(S.yourLink) + '</span><input type="text" readonly value="' + esc(url) + '" data-cqq-share-input></label>' +
          '<ul class="cqq-dots">' + S.keepPoints.map(t => '<li>' + md(t) + '</li>').join('') + '</ul></div>' + gpCard + '</div></section>');
      } else {
        const rep = resolve(below.report) || (arr(resolve(r.actions)).some(a => a && a.do === 'contact') ? {} : null);
        if (rep) {
          const i = arr(resolve(r.actions)).findIndex(a => a && a.do === 'contact');
          parts.push('<section class="cqq-sec is-paper cqq-keep-sec"><div class="cqq-wrap cqq-keep-grid"><div class="cqq-keep"><p class="cqq-eyebrow">Your next step</p><h2 class="cqq-h2 is-card">' + md(rep.title || S.reportTitle) + '</h2><p class="cqq-intro">' + md(rep.text || S.reportText) + '</p>' +
            '<div class="cqq-keep-btns"><button type="button" class="cqv-btn" data-cqq-do="contact" data-cqq-index="' + Math.max(0, i) + '"' + (i < 0 ? ' data-cqq-title="' + esc(rep.label || S.reportLabel) + '"' : '') + '>' + md(rep.label || S.reportLabel) + '</button><a class="cqv-btn cqv-btn-ghost" href="' + esc(S.tel) + '">' + esc(S.talk) + '</a></div></div>' + gpCard + '</div></section>');
        } else if (gpCard) parts.push('<section class="cqq-sec is-paper cqq-gp-only"><div class="cqq-wrap">' + gpCard + '</div></section>');
      }
      // Practical follow-through uses the selected result and existing Details, without making
      // delivery-time or maintenance-interval promises that vary by product.
      if (r.product && r.id !== 'UNAVAILABLE') {
        parts.push('<section class="cqq-sec"><div class="cqq-wrap">' + sectionHead('From your order *to your everyday.*', '', 'After you order') +
          '<ol class="cqq-timeline"><li><div class="cqq-step-copy"><h3>Your system ships</h3><p>Your selected equipment is prepared for delivery. Review shipping details for your order.</p></div></li>' +
          '<li><div class="cqq-step-copy"><h3>Your installer connects it</h3><p>A licensed plumber or installer connects your system. Installation is separate from the equipment price.</p></div></li>' +
          '<li class="is-navy"><div class="cqq-step-copy"><h3>Support stays with you</h3><p>Lifetime support for you and your installer, for questions about your system.</p></div></li></ol></div></section>');
        const included = resolve(r.included);
        const includedText = included && (typeof included === 'object' ? md(resolve(included.text)) : leadIn(included));
        parts.push('<section class="cqq-sec is-paper"><div class="cqq-wrap cqq-editorial-split"><figure class="cqq-editorial-photo is-detail">' +
          imgHTML({src:'cq-v3-h11-real-head.webp', w:1000, h:1000, alt:'Close-up of a Crystal Quest control valve'}, {low:true, sizes:'(min-width: 821px) 50vw, 100vw'}) +
          '</figure><div class="cqq-editorial-copy">' + sectionHead('Know your system. *Keep it at its best.*', '', 'Equipment and care') +
          (includedText ? '<p class="cqq-intro">' + includedText + '</p>' : '') +
          '<p class="cqq-intro">Your plan lists the selected equipment. Review the setup and care information with your installer before installation.</p>' +
          '<div class="cqq-care-links"><button type="button" class="cqv-text-btn" data-cqq-do="details" data-cqq-tab="plan" aria-haspopup="dialog"><span>See what is included</span><span aria-hidden="true">↗</span></button>' +
          '<button type="button" class="cqv-text-btn" data-cqq-do="details" data-cqq-tab="good" aria-haspopup="dialog"><span>Setup and care details</span><span aria-hidden="true">↗</span></button></div></div></div></section>');
      }
      // Preserve any outcome-specific included module supplied by a definition.
      const inc = resolve(below.included);
      if (inc && arr(inc.items).length) {
        const html = '<div class="cqq-block">' + sectionHead(inc.title || S.includedTitle, inc.lead, 'Your equipment') + '<ul class="cqq-inc">' + arr(inc.items).map(it => { const ir = it.image ? imgRef(typeof it.image === 'string' ? {src:it.image} : it.image) : null; return '<li>' + (ir && ir.url ? '<figure>' + imgHTML(Object.assign({}, ir, {alt:ir.alt || ''}), {sizes:'(min-width: 821px) 280px, 40vw', low:true}) + '</figure>' : '') + '<div><b>' + md(resolve(it.title)) + '</b>' + (it.text ? '<span>' + md(resolve(it.text)) + '</span>' : '') + '</div></li>'; }).join('') + '</ul></div>';
        parts.push('<section class="cqq-sec"><div class="cqq-wrap">' + html + '</div></section>');
      }
      // 4. Owners of systems like yours (never the review already shown in the quiz)
      const revs = belowReviews(below.reviews);
      if (revs.length) parts.push('<section class="cqq-sec"><div class="cqq-wrap">' + sectionHead(below.reviewsTitle || S.reviewsTitle, '', 'Owner stories') + '<div class="cqq-revs">' + revs.map(rv => reviewCardHTML(rv, {editorial:true})).join('') + '</div></div></section>');
      // Final photographic band: one action, matching the result's purchasable state.
      if (below.closer !== false) {
        const actions = arr(resolve(r.actions));
        const add = actions.find(a => a.do === 'add');
        const contactIndex = actions.findIndex(a => a.do === 'contact');
        const finalAction = add ? '<button type="button" class="cqv-btn cqv-btn-light" data-cqq-do="add">' + esc(S.addToCart) + '</button>'
          : contactIndex >= 0 ? '<button type="button" class="cqv-btn cqv-btn-light" data-cqq-do="contact" data-cqq-index="' + contactIndex + '" aria-haspopup="dialog">Ask a specialist</button>'
          : '<a class="cqv-btn cqv-btn-light" href="' + esc(S.tel) + '">Ask a specialist</a>';
        const slot = root.dataset.cqqFinalImage || asset('cq-v3-q-final-cta.webp');
        const photo = imgHTML({src:'cq-v3-q-final-cta.webp', w:2400, h:1350, alt:''}, {low:true, sizes:'100vw'});
        parts.push('<section class="cqq-photo-cta">' + photo + '<div class="cqq-wrap"><div class="cqq-photo-copy">' +
          sectionHead(add ? 'Your next chapter. *Better water.*' : 'A clearer plan *starts here.*', '', 'Your home. Your water.') +
          '<p>' + (add ? 'Your match is ready. Take the next step for your home.' : 'Talk through your water and your next step with a specialist.') + '</p>' + finalAction + '<p class="cqv-cart-error" role="alert" data-cqq-cart-error hidden></p></div></div></section>');
      }
      return parts.join('');
    }
    function fillResultBelow() {
      const r = lastResult; const html = r ? resultBelowHTML(r) : '';
      clearPhotoSlots();
      resultMore.innerHTML = html; resultMore.hidden = !html;
      preparePhotoSlots(resultMore);
      resultMore.querySelectorAll('img').forEach(im => { if (!im.complete) im.addEventListener('load', () => fitAll(), {once:true}); });
    }

    /* ---------- cart ---------- */
    async function addToCart(which, button) {
      if (cartBusy) return;
      const b = selections.get(which); const pr = b && pricing(b);
      const errBox = button && (button.closest('dialog, .cqq-photo-cta') || screenEl).querySelector('[data-cqq-cart-error]');
      if (!pr || !pr.available || !pr.items.length) { if (errBox) { errBox.textContent = S.cartError; errBox.hidden = false; } track('cq_quiz_add_to_cart_error', {outcome:lastResult && lastResult.id}); return; }
      cartBusy = true;
      const label = button ? button.innerHTML : '';
      cartButton = button; cartLabel = label; let navigating = false;
      if (button) { button.setAttribute('aria-disabled', 'true'); button.textContent = S.adding; }
      root.setAttribute('aria-busy', 'true');
      try {
        const base = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
        const attribution = {Quiz:def.name || quiz, 'Quiz match':lastResult && lastResult.id || ''};
        const items = pr.items.map(item => Object.assign({}, item, {properties:Object.assign({}, item.properties || {}, {'_Quiz':attribution.Quiz, '_Quiz match':attribution['Quiz match']})}));
        const res = await win.fetch(base + 'cart/add.js', {method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json', 'Accept':'application/json'}, body:JSON.stringify({items})});
        let data = {}; try { data = await res.json(); } catch (_) {}
        if (!res.ok) throw new Error(typeof data.description === 'string' ? data.description : S.cartError);
        // Best effort: line properties travel atomically with the add; cart attributes never delay the redirect.
        try { win.fetch(base + 'cart/update.js', {method:'POST', credentials:'same-origin', keepalive:true, headers:{'Content-Type':'application/json', 'Accept':'application/json'}, body:JSON.stringify({attributes:attribution})}).catch(() => {}); } catch (_) {}
        track(which === 'alt' ? 'cq_quiz_alternative_add_to_cart' : 'cq_quiz_add_to_cart', {outcome:lastResult && lastResult.id, variants:pr.items.map(i => i.id)});
        navigating = true; win.location.assign(root.dataset.cartUrl || base + 'cart');
      } catch (e) {
        navigating = false;
        if (errBox) { errBox.textContent = e.message || S.cartError; errBox.hidden = false; }
        track('cq_quiz_add_to_cart_error', {outcome:lastResult && lastResult.id});
      } finally {
        if (!navigating) resetCartButton();
      }
    }

    function resetCartButton() {
      cartBusy = false; root.removeAttribute('aria-busy');
      if (cartButton) { cartButton.removeAttribute('aria-disabled'); cartButton.innerHTML = cartLabel; }
      cartButton = null; cartLabel = '';
    }
    win.addEventListener('pageshow', e => { if (e.persisted) resetCartButton(); });

    /* ---------- result dialogs ---------- */
    // The old frame stays opaque until the actual responsive replacement has decoded. Rapid changes
    // retain just one last-visible frame, and detached requests cannot reveal an obsolete selection.
    function swapMedia(current, next) {
      if (!current || !next) return;
      const previous = current.querySelector(':scope > .cqq-media-outgoing') || current;
      const image = next.querySelector('img');
      if (!image) { if (next.classList.contains('is-equipment')) current.replaceWith(next); return; }
      current.replaceWith(next);
      previous.classList.remove('is-fading'); previous.classList.add('cqq-media-outgoing');
      previous.setAttribute('aria-hidden', 'true'); previous.inert = true;
      next.appendChild(previous);
      image.loading = 'eager'; fitMedia(next);
      let attempt = 0;
      const decode = im => {
        if (im.decode) return im.decode();
        if (im.complete) return im.naturalWidth ? Promise.resolve() : Promise.reject(new Error('image unavailable'));
        return new Promise((resolve, reject) => { im.addEventListener('load', resolve, {once:true}); im.addEventListener('error', reject, {once:true}); });
      };
      const reveal = () => {
        if (!next.isConnected || previous.parentNode !== next) return;
        next.removeEventListener('load', loaded, true); next.removeEventListener('error', failed, true);
        if (calm()) { previous.remove(); return; }
        win.getComputedStyle(previous).opacity;
        previous.classList.add('is-fading');
        const mediaMs = cssTimeMs(win.getComputedStyle(previous).getPropertyValue('--cqq-media-swap-ms'), 250);
        win.setTimeout(() => { if (previous.parentNode === next && previous.classList.contains('is-fading')) previous.remove(); }, mediaMs);
      };
      const currentImage = () => Array.from(next.querySelectorAll('img')).find(im => !previous.contains(im));
      const prepare = im => {
        if (!im || !next.isConnected || previous.parentNode !== next) return;
        const token = ++attempt, source = im.currentSrc || im.src;
        Promise.resolve().then(() => next.classList.contains('is-equipment') ? Promise.all(Array.from(next.querySelectorAll('img')).filter(img => !previous.contains(img)).map(img => decode(img).catch(() => {}))) : decode(im)).then(() => {
          if (token !== attempt || !next.isConnected || previous.parentNode !== next) return;
          if (im !== currentImage() || source !== (im.currentSrc || im.src)) { prepare(currentImage()); return; }
          reveal();
        }, () => {
          if (token !== attempt || !next.isConnected) return;
          const current = currentImage();
          // A responsive source change aborts decode; its load event retries. A fallback replaces the node.
          if (current && (current !== im || (current.complete && current.naturalWidth && source !== (current.currentSrc || current.src)))) {
            fitMedia(next); prepare(current);
          }
        });
      };
      const loaded = e => { if (!previous.contains(e.target) && e.target.tagName === 'IMG') prepare(e.target); };
      const failed = () => { Promise.resolve().then(() => { const current = currentImage(); if (current && current !== image) prepare(current); }); };
      next.addEventListener('load', loaded, true); next.addEventListener('error', failed, true);
      prepare(image);
    }
    function warmOtherFinish(which) {
      if (which !== 'primary' || !lastResult) return;
      const b = selections.get(which), pr = b && pricing(b);
      const base = sceneMedia(resolve(lastResult.media)), current = selectedMedia(lastResult, pr, b);
      if (!base || !base.scene || arr(def.stainlessScenes).indexOf(base.scene) < 0) return;
      const opposite = 'cq-v3-q-scene-' + base.scene + (current && /-ss\.webp$/.test(current.src || '') ? '' : '-ss');
      [opposite + '.webp'].concat(win.matchMedia && win.matchMedia(BAND_MQ).matches ? [opposite + '-band.webp'] : []).forEach(name => {
        if (!imageMap[name]) return;
        const ref = imgRef(name), phone = /-(band|strip)\.webp$/.test(name);
        // Help center pictures warm with the result renderer's own sizes, so the browser picks the same copy.
        preload(ref, onMedia(ref.url) ? (phone ? '100vw' : '(min-width: 821px) and (orientation: landscape) 50vw, 520px') : snappedSizes(phone ? win.innerWidth : Math.min(win.innerWidth, 520), ref.w));
      });
    }
    function refreshResultPrice() {
      const b = selections.get('primary'); const pr = b && pricing(b); if (!pr) return;
      screenEl.querySelectorAll('[data-cqq-price]').forEach(el => { el.textContent = money(pr.price); });
      screenEl.querySelectorAll('[data-cqq-price-label]').forEach(el => { el.innerHTML = labelHTML(pr, el.dataset.cqqBadge || ''); });
      const media = screenEl.querySelector('.cqv-q-result-media');
      if (media && lastResult) {
        const wrap = doc.createElement('div'); wrap.innerHTML = resultMediaHTML(lastResult, pr, b);
        if (wrap.firstChild) swapMedia(media, wrap.firstChild);
      }
      fitAll();
      screenEl.querySelectorAll('[data-cqq-breakdown]').forEach(el => { const t = breakdownText(pr.parts); el.textContent = t; el.hidden = !t; });
    }
    function configureHTML(which) {
      const C = commerce(); const b = selections.get(which); const pr = pricing(b);
      const blocks = [b].concat(b.pairs).map((x, i) => {
        const head = b.pairs.length ? '<p class="cqq-config-head">' + esc(i === 0 ? (x.def.label || cleanProduct(x.card.name)) : upperFirst(x.def.label || cleanProduct(x.card.name))) + '</p>' : '';
        return '<div class="cqq-config-block" data-cqq-config-index="' + i + '">' + head + C.optionsHTML(x.card, x.state, {labelPrefix:cleanProduct(x.card.name), currency}) + '</div>';
      }).join('');
      const r = lastResult || {};
      const media = selectedMedia(which === 'primary' ? r : {media:{product:true}}, pr, b) || {product:true};
      const ref = media.product ? {src:pr.image, alt:media.alt || cleanProduct(b.card.name) + ', product photo'} : media;
      const image = imgRef(ref), band = media.band ? imgRef(media.band) : null;
      const gallery = Array.isArray(media.gallery) && media.gallery.length;
      const mediaHTML = gallery ? equipmentMediaHTML(media, 'cqv-sheet-media') : '<div class="cqv-sheet-media' + (media.product ? ' is-product' : '') + '"' + (media.product ? '' : fitAttrs(image) + (media.containPhone ? ' data-cqq-contain-phone="true"' : '') + (band ? fitAttrs(band, true) : '')) + '>' + (media.product ? imgHTML(ref, {sizes:'(max-width: 820px) 100vw, 520px'}) : pictureHTML(ref, {sizes:'(max-width: 820px) 100vw, 520px'})) + '</div>';
      return '<div class="cqv-sheet" data-sheet="config">' + mediaHTML + '<div class="cqv-sheet-main">' + blocks +
        '<p class="cqv-inline-notice" data-cqq-config-notice hidden></p><p class="cqv-cart-error" role="alert" data-cqq-cart-error hidden></p>' +
        '<div class="cqv-panel-buy"><p class="cqv-panel-total"><span>' + esc(S.yourTotal) + '</span><strong data-cqq-config-total>' + esc(money(pr.price)) + '</strong><span class="cqq-config-breakdown" data-cqq-config-breakdown>' + esc(breakdownText(pr.parts)) + '</span></p><div class="cqv-panel-actions"><button type="button" class="cqv-btn" data-cqq-do="add" data-cqq-which="' + esc(which) + '">' + esc(S.addToCart) + '</button><button type="button" class="cqv-btn cqv-btn-ghost" data-cqq-close>' + esc(S.done_) + '</button></div></div></div>';
    }
    function openConfigure(which) {
      which = which || 'primary';
      const b = selections.get(which); if (!b || !commerce()) return;
      // the title names what the result names, including a "with ..." suffix (one listing's configuration, for example
      // "SMART Whole House Water Filter with Softener"); a "+ ..." suffix names a paired second product, which already
      // has its own heading inside the dialog, so it stays out of the title
      const own = which === 'primary' && lastResult && lastResult.product;
      const suffix = own && own.nameSuffix && !/^\s*\+/.test(own.nameSuffix) ? ' ' + own.nameSuffix : '';
      const d = makeDialog('cqq-configure', S.configureTitle + ' ' + cleanProduct((own && own.name) || b.card.name) + suffix);
      d.dataset.cqqWhich = which;
      d.querySelector('[data-cqq-dialog-body]').innerHTML = configureHTML(which);
      openDialog(d);
      d.querySelectorAll('[data-cqq-safe]').forEach(fitMedia);
      warmOtherFinish(which);
      track('cq_quiz_configure_open', {outcome:lastResult && lastResult.id});
    }
    function onConfigureChange(d, control) {
      const C = commerce(); const which = d.dataset.cqqWhich || 'primary'; const b = selections.get(which); if (!b || !C) return;
      const block = control.closest('[data-cqq-config-index]'); const idx = block ? Number(block.dataset.cqqConfigIndex) : 0;
      const target = idx === 0 ? b : b.pairs[idx - 1]; if (!target) return;
      const res = C.handleChange(target.card, target.state, control);
      if (!res || !res.handled) return;
      const pos = [...d.querySelectorAll('select, input')].indexOf(control);
      const previousMedia = d.querySelector('.cqv-sheet-media');
      d.querySelector('[data-cqq-dialog-body]').innerHTML = configureHTML(which);
      const nextMedia = d.querySelector('.cqv-sheet-media');
      if (previousMedia && nextMedia) { nextMedia.replaceWith(previousMedia); swapMedia(previousMedia, nextMedia); }
      d.querySelectorAll('[data-cqq-safe]').forEach(fitMedia);
      warmOtherFinish(which);
      const again = d.querySelectorAll('select, input')[pos]; if (again) { try { again.focus({preventScroll:true}); } catch (_) {} }
      const notice = d.querySelector('[data-cqq-config-notice]');
      if (notice && (res.notice || res.error)) { notice.textContent = res.error || res.notice; notice.hidden = false; }
      const pr = pricing(b);
      const status = d.querySelector('[data-cqq-dialog-status]'); if (status) status.textContent = (res.error || res.notice || '') + ' ' + money(pr.price) + '. ' + S.configUpdated;
      if (which === 'primary') { st.config = selectionChoices(b); save(); refreshResultPrice(); } else refreshAlt();
    }
    function planItemHTML(it) {
      let price = '';
      if (it.card) { const p = ctx.config(it.card, it); if (p && p.price != null) price = '<span class="cqq-plan-price">' + esc(money(p.price)) + '</span>'; }
      const note = it.optional ? S.optionalNotAdded : resolve(it.note);
      return '<li><div class="cqq-plan-text"><strong>' + md(resolve(it.title)) + '</strong>' + (it.text ? '<span>' + md(resolve(it.text)) + '</span>' : '') + (note ? '<em>' + md(note) + '</em>' : '') + '</div>' + price + '</li>';
    }
    function altCardHTML(r) {
      const alt = r.details && r.details.alternative; if (!alt) return '';
      const b = selections.get('alt'); const pr = b && pricing(b);
      const name = alt.product && (alt.product.name || (b && cleanProduct(b.card.name))) || '';
      const bd = pr ? breakdownText(pr.parts) : '';
      // Range-limited alternatives use the same sizing contact as the primary match.
      const second = alt.sizingContact ? '<button type="button" class="cqv-btn cqv-btn-ghost" data-cqq-do="contact" data-cqq-which="alt">' + esc(alt.sizingContact.label) + '</button>'
        : commerce() ? '<button type="button" class="cqv-btn cqv-btn-ghost" data-cqq-do="configure" data-cqq-which="alt">' + esc(S.configure) + '</button>' : '';
      return '<article class="cqq-alt"><p class="cqq-alt-kicker">' + md(resolve(alt.title)) + '</p><div class="cqq-alt-row"><div><h3>' + md(name + (alt.product && alt.product.nameSuffix ? ' ' + alt.product.nameSuffix : '')) + '</h3>' + (pr ? '<p class="cqv-price-label" data-cqq-alt-label>' + esc(pr.label) + '</p>' : '') + '</div>' + (pr ? '<div class="cqq-alt-price"><span class="cqv-price" data-cqq-alt-price>' + esc(money(pr.price)) + '</span>' + (bd ? '<span class="cqv-price-breakdown" data-cqq-alt-breakdown>' + esc(bd) + '</span>' : '') + '</div>' : '') + '</div>' +
        (alt.text ? '<p class="cqq-alt-text">' + md(resolve(alt.text)) + '</p>' : '') +
        (pr ? '<div class="cqq-alt-actions"><button type="button" class="cqv-btn" data-cqq-do="add" data-cqq-which="alt">' + esc(S.addToCart) + '</button>' + second + '</div><p class="cqv-cart-error" role="alert" data-cqq-cart-error hidden></p>' : '') + '</article>';
    }
    function refreshAlt() {
      const b = selections.get('alt'); const pr = b && pricing(b); if (!pr) return;
      root.querySelectorAll('[data-cqq-alt-price]').forEach(el => { el.textContent = money(pr.price); });
      root.querySelectorAll('[data-cqq-alt-label]').forEach(el => { el.textContent = pr.label; });
      root.querySelectorAll('[data-cqq-alt-breakdown]').forEach(el => { el.textContent = breakdownText(pr.parts); });
    }
    function detailsHTML(r, tab) {
      const d = r.details || {};
      const tabs = [];
      const incl = r.included ? (typeof r.included === 'object' ? '<b>' + md(resolve(r.included.label)) + '</b> ' + md(resolve(r.included.text)) : md(resolve(r.included))) : '';
      const plan = arr(resolve(d.plan));
      // the Included line shows here on short phones (where the result hides it), and always when there are no plan items
      if (plan.length || incl) tabs.push(['plan', S.tabPlan, (incl ? '<p class="cqq-plan-included' + (plan.length ? '' : ' is-only') + '">' + incl + '</p>' : '') + (plan.length ? '<ol class="cqq-plan">' + plan.map(planItemHTML).join('') + '</ol>' : '')]);
      const good = arr(resolve(d.goodToKnow)).filter(Boolean);
      // AJ 2026-09-25, decision 3: "What happens next" opens Good to know on results that recommend a system; the
      // one-line versions take over when the full list would not fit the tab (375 x 548)
      const next = whatNextHTML(r);
      // never repeat a "What happens next" line (a definition's own shipping line, for example) or the GP line
      if (next) { const nextText = plain(next.replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' '); for (let i = good.length - 1; i >= 0; i--) { const t = plain(String(resolve(good[i]) || '')).replace(/\s+/g, ' ').trim(); if (t && nextText.indexOf(t) >= 0) good.splice(i, 1); } }
      // Guaranteed Performance appears once: the desktop first view or the mobile card below it.
      if (good.length || next) tabs.push(['good', S.tabGood, next + (good.length ? '<ul class="cqv-checks cqq-good">' + good.map(g => '<li>' + md(resolve(g)) + '</li>').join('') + '</ul>' : '')]);
      const cmp = resolve(d.compare);
      const alt = altCardHTML(r);
      if (alt || cmp) tabs.push(['other', S.tabOther, alt + (cmp ? '<p class="cqq-compare"><a class="cqv-link" href="' + esc(safeUrl(cmp.href) ? cmp.href : '#') + '" data-cqq-track="cq_quiz_compare_click">' + md(resolve(cmp.label) || S.compare) + '</a></p>' : '')]);
      tabs.push(['answers', S.tabAnswers, answersListHTML()]);
      const active = tabs.some(t => t[0] === tab) ? tab : tabs[0][0];
      return '<div class="cqq-tabs" role="tablist" aria-label="' + esc(S.details) + '">' + tabs.map(t => '<button type="button" role="tab" id="cqq-tab-' + t[0] + '" aria-controls="cqq-panel-' + t[0] + '" aria-selected="' + (t[0] === active) + '" tabindex="' + (t[0] === active ? 0 : -1) + '" data-cqq-do="tab" data-cqq-tab="' + t[0] + '">' + esc(t[1]) + '</button>').join('') + '</div>' +
        tabs.map(t => '<div class="cqq-panel" role="tabpanel" id="cqq-panel-' + t[0] + '" aria-labelledby="cqq-tab-' + t[0] + '" tabindex="0"' + (t[0] === active ? '' : ' hidden') + '>' + t[2] + '</div>').join('');
    }
    function whatNextHTML(r) {
      const d = r.details || {};
      const spec = resolve(d.whatNext);
      if (spec === false || r.id === 'UNAVAILABLE') return '';
      if (!r.product && spec == null) return '';
      // spec: an array of [lead, text, short] rows, or {today, ships, install, after} replacing the text of a row
      // (for example a large home's shipping wording); keys given as arrays replace the whole row
      const keys = ['today', 'ships', 'install', 'after'];
      const rows = Array.isArray(spec) ? spec : S.whatNext.map((row, i) => { const o = spec && spec[keys[i]]; return o == null ? row : Array.isArray(o) ? o : [row[0], o, o]; });
      const list = rows.filter(x => x && x !== false).map(x => arr(x));
      if (!list.length) return '';
      return '<div class="cqq-good-next"><p class="cqq-good-next-title">' + esc(S.whatNextTitle) + '</p><ol>' + list.map(x => '<li><span class="cqq-next-full"><b>' + md(x[0]) + '</b> ' + md(x[1]) + '</span><span class="cqq-next-short">' + md(x[2] || (x[0] + ' ' + x[1])) + '</span></li>').join('') + '</ol></div>';
    }
    // the Details sheet never scrolls on phones: when the full steps do not fit the tab, the one-line steps show
    function compactNext(box) {
      const panel = box.closest('.cqq-panel'); if (!panel || panel.hidden) return;
      box.classList.remove('is-compact'); panel.classList.remove('is-dense');
      if (panel.scrollHeight > panel.clientHeight + 1) box.classList.add('is-compact');
      if (panel.scrollHeight > panel.clientHeight + 1) panel.classList.add('is-dense');
    }
    function openDetails(tab) {
      if (!lastResult) return;
      const d = makeDialog('cqq-details', S.details);
      d.querySelector('[data-cqq-dialog-body]').innerHTML = detailsHTML(lastResult, tab);
      openDialog(d);
      d.querySelectorAll('.cqq-good-next').forEach(compactNext);
      track('cq_quiz_details_open', {tab:tab || 'plan', outcome:lastResult.id});
    }
    function selectTab(d, name) {
      d.querySelectorAll('[role=tab]').forEach(b => { const on = b.dataset.cqqTab === name; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; if (on) b.focus(); });
      d.querySelectorAll('[role=tabpanel]').forEach(p => { p.hidden = p.id !== 'cqq-panel-' + name; });
      d.querySelectorAll('.cqq-good-next').forEach(compactNext);
      track('cq_quiz_details_open', {tab:name, outcome:lastResult && lastResult.id});
    }
    function contactSummary() {
      const a = answers();
      const r = lastResult;
      const lines = [];
      lines.push((def.name || quiz));
      if (r) {
        const b = selections.get('primary'); const pr = b && pricing(b);
        lines.push('Outcome: ' + r.id + (pr ? ' (' + cleanProduct(r.product && r.product.name || b.card.name) + ', ' + pr.label + ', ' + money(pr.price) + ')' : ''));
      }
      if (typeof def.summary === 'function') { try { const t = def.summary(a, r, ctx); if (t) lines.push(t); } catch (_) {} }
      else pathScreens().filter(isQuestion).forEach(s => lines.push(plain(resolve(s.shortTitle || s.title, a)) + ': ' + cleanDashes(answerText(s, a))));
      return lines.join('\n');
    }
    function openContact(opts) {
      opts = opts || {};
      const d = root.querySelector('dialog[data-cqq-contact]'); if (!d) { win.location.assign(S.tel); return; }
      if (leadPending) { openDialog(leadPending.dialog); return; }
      // a previous inline "sent" state is cleared first, so the dialog opens ready for a new note
      { const f = d.querySelector('form'), sn = d.querySelector('[data-cqq-hs-sent]'); if (sn && !sn.hidden) { sn.hidden = true; if (f) { f.hidden = false; f.reset(); } } }
      const h = d.querySelector('h2'); if (h && opts.title) h.innerHTML = md(opts.title);
      const set = (name, v) => { const el = d.querySelector('[name="' + name + '"]'); if (el) el.value = v; };
      set('contact[subject]', opts.subject || ((def.name || quiz) + ': ' + plain(opts.title || 'contact')));
      set('contact[Quiz answers]', contactSummary());
      set('contact[Quiz outcome]', lastResult ? lastResult.id : '');
      const note = d.querySelector('[data-cqq-contact-note]'); if (note) { const t = resolve(opts.note || (lastResult && lastResult.contactNote)); note.innerHTML = t ? md(t) : ''; note.hidden = !t; }
      d.dataset.cqqKind = /report/i.test(plain(opts.title || '')) ? 'report' : 'review';
      bindContact(d);
      openDialog(d);
      track('cq_quiz_send_report_open', {outcome:lastResult && lastResult.id});
    }
    function bindContact(d) {
      if (!d || d.dataset.cqqBound) return;
      d.dataset.cqqBound = '1';
      d.addEventListener('click', e => { if (e.target === d) d.close(); });
      const form = d.querySelector('form');
      // capture phase on the dialog: the engine sees the submit before the store's own form scripts (its spam check
      // re-submits contact forms natively), so a HubSpot send is never doubled by a Shopify post
      if (form) d.addEventListener('submit', e => { if (e.target === form) onContactSubmit(e, d, form); }, true);
    }

    /* ---------- HubSpot (round A HUBSPOT-BUILD.md; public form endpoint, no token in the theme) ----------
       F1 "Web: Quiz - Specialist review" takes every contact action (Send my water report, Send my details); F2
       "Web: Quiz - Email my match" takes "Email me my match". IDs come from the contact dialog's data attributes
       (snippets/cq-quiz-v3-dialogs.liquid), so switching HubSpot off is one line there. A 200 shows the sent state
       in place; a bad email shows a field error; anything else (other 4xx, 429, 5xx, network, 8 s timeout, a blocked
       request) falls back to the Shopify contact form, so no lead is lost. */
    function hs() {
      const d = root.querySelector('dialog[data-cqq-contact]');
      const c = d && d.dataset;
      return c && c.cqqHsPortal && c.cqqHsReview ? {portal:c.cqqHsPortal, review:c.cqqHsReview, match:c.cqqHsMatch || '', host:c.cqqHsHost || 'api-na2.hsforms.com'} : null;
    }
    const hsQuizId = () => (def.hubspot && def.hubspot.quizId) || (quiz === 'well' ? 'well_finder' : quiz === 'softener' ? 'softener_quiz' : quiz);
    const hsQuizLabel = () => (def.hubspot && def.hubspot.label) || (quiz === 'well' ? 'Well finder' : quiz === 'softener' ? 'Softener quiz' : (def.name || quiz));
    function hsSource(a) {
      if (def.hubspot && typeof def.hubspot.source === 'function') { try { return def.hubspot.source(a, ctx) || null; } catch (_) {} }
      if (a.source === 'well' || quiz === 'well') return 'well';
      if (a.source === 'city') return 'city';
      return a.source ? 'not_sure' : null;
    }
    function pageUri() {
      const loc = win.location, q = new URLSearchParams(loc.search), keep = new URLSearchParams();
      q.forEach((v, k) => { if (k === 'view' || /^utm_/.test(k)) keep.append(k, v); });
      const qs = keep.toString();
      return loc.origin + loc.pathname + (qs ? '?' + qs : '');
    }
    function hsContext() {
      const m = /(?:^|;\s*)hubspotutk=([^;]+)/.exec(doc.cookie || '');
      const c = {pageUri:pageUri(), pageName:doc.title || ''};
      if (m) c.hutk = m[1];
      return c;
    }
    function recommendationText(r) {
      if (!r) return S.hsReviewLabel;
      if (r.hubspot && r.hubspot.recommendation) return String(resolve(r.hubspot.recommendation)).slice(0, 150);
      const b = selections.get('primary'), pr = b && pricing(b);
      if (r.product && pr) return plain(cleanProduct(r.product.name || b.card.name) + (pr.label ? ', ' + pr.label : '')).slice(0, 150);
      return plain(resolve(r.nameRow && r.nameRow.title) || '') || S.hsReviewLabel;
    }
    async function hsPost(guid, body) {
      const c = hs(); if (!c || !guid) return {ok:false, status:0, reason:'off'};
      const url = 'https://' + c.host + '/submissions/v3/integration/submit/' + encodeURIComponent(c.portal) + '/' + encodeURIComponent(guid);
      const ctrl = typeof win.AbortController === 'function' ? new win.AbortController() : null;
      const timer = win.setTimeout(() => { if (ctrl) ctrl.abort(); }, 8000);
      try {
        const res = await win.fetch(url, {method:'POST', mode:'cors', credentials:'omit', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body), signal:ctrl ? ctrl.signal : undefined});
        let data = null; try { data = await res.json(); } catch (_) {}
        return {ok:res.ok, status:res.status, data, reason:res.ok ? '' : 'http-' + res.status};
      } catch (e) { return {ok:false, status:0, data:null, reason:e && e.name === 'AbortError' ? 'timeout' : 'network'}; }
      finally { win.clearTimeout(timer); }
    }
    const hsErrors = r => arr(r && r.data && r.data.errors).map(e => String((e && e.errorType) || '') + ' ' + String((e && e.message) || ''));
    async function hsSubmit(guid, body) {
      let r = await hsPost(guid, body);
      const emailErr = x => !x.ok && x.status === 400 && hsErrors(x).some(t => /INVALID_EMAIL|BLOCKED_EMAIL/i.test(t));
      // if this portal rejects the consent block, the form's own consent settings apply and the theme sends none
      if (!r.ok && r.status === 400 && !emailErr(r) && body.legalConsentOptions && hsErrors(r).some(t => /consent|legal/i.test(t))) {
        const b2 = Object.assign({}, body); delete b2.legalConsentOptions; r = await hsPost(guid, b2);
      }
      return Object.assign(r, {emailErr:emailErr(r)});
    }
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const field = (name, value, type) => ({objectTypeId:type || '0-1', name, value:String(value)});
    function reviewBody(form, kind) {
      const v = n => { const el = form.querySelector('[name="' + n + '"]'); return el ? String(el.value || '').trim() : ''; };
      const r = lastResult, a = answers();
      const names = v('contact[name]').split(/\s+/).filter(Boolean);
      const fields = [field('email', v('contact[email]')), field('firstname', names[0] || ''), field('lastname', names.slice(1).join(' '))];
      if (v('contact[phone]')) fields.push(field('phone', v('contact[phone]')));
      const id = r ? r.id : 'NONE';
      fields.push(field('subject', 'Quiz: ' + hsQuizLabel() + ', ' + (kind === 'report' ? 'water report' : 'specialist review') + ' (' + id + ')', '0-5'));
      const content = [v('contact[body]'), contactSummary().slice(0, 3000), 'Result: ' + id + ' ' + recommendationText(r), 'Page: ' + pageUri() + '\nMatch link: ' + shareURL()].filter(Boolean).join('\n\n');
      fields.push(field('content', content, '0-5'));
      const src = hsSource(a);
      if (src === 'well' || src === 'city') fields.push(field('city_or_well_', src === 'well' ? 'Well Water' : 'City Water', '0-5'));
      fields.push(field('water_report_', kind === 'report' ? 'Yes' : 'No', '0-5'));
      return {fields, context:hsContext(), legalConsentOptions:{legitimateInterest:{value:true, subscriptionTypeId:935551697, legalBasis:'LEAD', text:S.reviewPrivacy}}};
    }
    function matchBody(email, first, tips) {
      const r = lastResult, a = answers();
      const fields = [field('email', email)];
      if (first) fields.push(field('firstname', first));
      fields.push(field('water_quiz_recommendation', recommendationText(r)));
      const src = hsSource(a); if (src) fields.push(field('water_quiz_source', src));
      if (typeof a.zipCode === 'string' && /^\d{5}$/.test(a.zipCode)) fields.push(field('water_quiz_zip', a.zipCode));
      const needs = r && r.hubspot && r.hubspot.needsTest != null ? !!r.hubspot.needsTest : !(r && r.product);
      fields.push(field('water_quiz_needs_test', needs ? 'true' : 'false'));
      fields.push(field('water_quiz_profile', JSON.stringify({v:1, quiz:hsQuizId(), outcome:r ? r.id : null, answers:shareValues()}).slice(0, 3000)));
      fields.push(field('cq_quiz_share_link', shareURL()));
      return {fields, context:hsContext(), legalConsentOptions:{consent:{consentToProcess:true, text:S.matchPrivacy, communications:[{value:!!tips, subscriptionTypeId:935551700, text:S.matchTips}]}}};
    }
    function hsFieldError(box, msg) { if (!box) return; box.innerHTML = msg ? md(msg) : ''; box.hidden = !msg; }
    // The Shopify contact form fallback when HubSpot fails. The store's spam check prepares a contact form only after a
    // focusin or change inside it, and otherwise cancels the submit without a word (the "Email me my match" customer
    // never touched that form). Shopify.captcha.protect prepares it first, then the form submits through its own
    // handlers, exactly as a customer pressing Send would. If the page has not moved on 10 seconds later, the dialog
    // says so and gives the phone number, so a lead is never dropped silently. `note` (the "we'll email your match"
    // line shown after the reload) is written only when the page actually leaves.
    function shopifyFallback(cf, note, onStuck) {
      let left = false, stuck = false, submitted = false;
      const onHide = () => { left = true; if (note) { try { win.sessionStorage.setItem(storageKey + ':note', note); } catch (_) {} } };
      win.addEventListener('pagehide', onHide);
      win.setTimeout(() => {
        if (left) return;
        stuck = true;
        win.removeEventListener('pagehide', onHide);
        try { win.sessionStorage.removeItem(storageKey + ':note'); } catch (_) {}
        delete cf.dataset.cqqFallback;
        track('cq_quiz_hs_fallback_stuck');
        onStuck();
      }, 10000);
      const send = () => { if (stuck || submitted) return; submitted = true; cf.dataset.cqqFallback = '1'; if (typeof cf.requestSubmit === 'function') cf.requestSubmit(); else cf.submit(); };
      save();
      try {
        const cap = win.Shopify && win.Shopify.captcha;
        if (cap && typeof cap.protect === 'function') { cap.protect(cf, send); return; }
      } catch (e) { warn('captcha protect failed', e); }
      send();
    }
    async function onContactSubmit(e, d, form) {
      // a flagged submission is the Shopify fallback: let it post (and remember where the quiz was)
      if (form.dataset.cqqFallback === '1') { delete form.dataset.cqqFallback; save(); track('cq_quiz_send_report_submit', {outcome:lastResult && lastResult.id, via:'shopify'}); return; }
      const c = hs(); const kind = d.dataset.cqqKind || 'review';
      if (!c) { save(); track('cq_quiz_send_report_submit', {outcome:lastResult && lastResult.id, via:'shopify'}); return; }
      e.preventDefault(); e.stopImmediatePropagation();
      if (leadPending) return;
      const err = form.querySelector('[data-cqq-hs-error]');
      const hp = form.querySelector('[data-cqq-hp]');
      const sent = d.querySelector('[data-cqq-hs-sent]');
      const showSent = () => { form.hidden = true; if (sent) { sent.hidden = false; try { sent.focus({preventScroll:true}); } catch (_) {} } };
      if (hp && hp.value) { showSent(); return; }
      const email = String((form.querySelector('[name="contact[email]"]') || {}).value || '').trim();
      if (!EMAIL_RE.test(email)) { hsFieldError(err, S.emailInvalid); return; }
      hsFieldError(err, '');
      const btn = form.querySelector('[type=submit]'); const label = btn ? btn.innerHTML : '';
      if (btn) { if (btn.getAttribute('aria-disabled') === 'true') return; btn.setAttribute('aria-disabled', 'true'); btn.textContent = S.sending; }
      leadPending = {dialog:d, form};
      const res = await hsSubmit(c.review, reviewBody(form, kind));
      const restore = () => { leadPending = null; if (btn) { btn.removeAttribute('aria-disabled'); btn.innerHTML = label; } };
      if (res.ok) { restore(); save(); showSent(); track('cq_quiz_hs_submit', {request:kind, outcome:lastResult && lastResult.id}); return; }
      if (res.emailErr) { restore(); hsFieldError(err, S.emailInvalid); return; }
      track('cq_quiz_hs_fallback', {request:kind, reason:res.reason || 'error'});
      const subj = form.querySelector('[name="contact[subject]"]');
      if (subj && !/HubSpot unreachable/.test(subj.value)) subj.value = subj.value + ' (HubSpot unreachable)';
      // the button keeps "Sending" until the page moves on (or the watchdog gives the customer the phone number)
      shopifyFallback(form, null, () => { restore(); hsFieldError(err, S.sendFailed); });
    }

    /* ---------- "Email me my match" (form F2; only on results that recommend a system) ---------- */
    function openEmailMatch() {
      if (leadPending) { openDialog(leadPending.dialog); return; }
      if (!lastResult || !emailMatchOn(lastResult)) return;
      const d = makeDialog('cqq-match', S.matchTitle);
      const body = d.querySelector('[data-cqq-dialog-body]');
      // data-hs-do-not-collect: the site's HubSpot tracking code must not also collect this form as a "non-HubSpot
      // form" (that would send the email a second time, outside form F2's consent text)
      body.innerHTML = '<form class="cqv-form cqq-match-form" novalidate data-cqq-match-form data-hs-do-not-collect="true">' +
        '<label>' + esc(S.matchEmail) + '<input type="email" name="email" autocomplete="email" required></label>' +
        '<label>' + esc(S.matchFirst) + '<input type="text" name="firstname" autocomplete="given-name"></label>' +
        '<label class="cqq-check"><input type="checkbox" name="tips"><span>' + esc(S.matchTips) + '</span></label>' +
        '<div class="cqq-hp" aria-hidden="true"><label>Company website<input type="text" tabindex="-1" autocomplete="off" data-cqq-hp></label></div>' +
        '<p class="cqv-q-field-error" role="alert" data-cqq-hs-error hidden></p>' +
        '<button type="submit" class="cqv-btn">' + esc(S.matchSend) + '</button>' +
        '<p class="cqq-privacy">' + esc(S.matchPrivacy) + ' <a href="' + esc(S.privacyUrl) + '" target="_blank" rel="noopener">' + esc(S.privacy) + '</a></p></form>' +
        '<p class="cqv-form-status" role="status" tabindex="-1" data-cqq-hs-sent hidden>' + md(S.matchSent) + '</p>';
      const form = body.querySelector('form');
      form.addEventListener('submit', e => onMatchSubmit(e, d, form));
      openDialog(d);
      track('cq_quiz_email_match_open', {outcome:lastResult.id});
    }
    async function onMatchSubmit(e, d, form) {
      e.preventDefault();
      if (leadPending) return;
      const c = hs(); if (!c) return;
      const err = form.querySelector('[data-cqq-hs-error]'), sent = d.querySelector('[data-cqq-hs-sent]');
      const email = String(form.elements.email.value || '').trim(), first = String(form.elements.firstname.value || '').trim(), tips = !!form.elements.tips.checked;
      const showSent = html => { form.hidden = true; if (sent) { if (html) sent.innerHTML = md(html); sent.hidden = false; try { sent.focus({preventScroll:true}); } catch (_) {} } };
      const hp = form.querySelector('[data-cqq-hp]');
      if (hp && hp.value) { showSent(); return; }
      if (!EMAIL_RE.test(email)) { hsFieldError(err, S.emailInvalid); try { form.elements.email.focus(); } catch (_) {} return; }
      hsFieldError(err, '');
      const btn = form.querySelector('[type=submit]'); if (btn.getAttribute('aria-disabled') === 'true') return;
      btn.setAttribute('aria-disabled', 'true'); btn.textContent = S.sending;
      const fallback = {url:shareURL(), outcome:lastResult ? lastResult.id : '', summary:contactSummary()};
      leadPending = {dialog:d, form};
      const res = await hsSubmit(c.match, matchBody(email, first, tips));
      const restore = () => { leadPending = null; btn.removeAttribute('aria-disabled'); btn.textContent = S.matchSend; };
      if (res.ok) { restore(); showSent(); track('cq_quiz_hs_submit', {request:'match', outcome:lastResult && lastResult.id}); return; }
      if (res.emailErr) { restore(); hsFieldError(err, S.emailInvalid); return; }
      track('cq_quiz_hs_fallback', {request:'match', reason:res.reason || 'error'});
      // fallback: the Shopify contact form carries the request to the Help Desk; the page reloads into the result
      const cd = root.querySelector('dialog[data-cqq-contact]'); const cf = cd && cd.querySelector('form');
      if (!cf) { restore(); hsFieldError(err, S.sendFailed); return; }
      const set = (n, v) => { const el = cf.querySelector('[name="' + n + '"]'); if (el) el.value = v; };
      set('contact[name]', first || email.split('@')[0]); set('contact[email]', email); set('contact[phone]', '');
      set('contact[body]', S.matchFallbackBody + '\n' + fallback.url + (tips ? '\nAlso send me water tips and offers.' : ''));
      set('contact[subject]', 'Quiz: email my match (HubSpot unreachable)');
      set('contact[Quiz outcome]', fallback.outcome); set('contact[Quiz answers]', fallback.summary);
      shopifyFallback(cf, S.matchSaved, () => { restore(); hsFieldError(err, S.sendFailed); });
    }

    /* ---------- share link (brief 4.3): the answers travel in the URL fragment, never the ZIP, name, email or phone ----------
       #m=<base64url of {v:1, q:'<quiz>', a:{answers}, o:{owners that differ from the screen's own}}>. The fragment
       never reaches the server or analytics. Opening it restores the answers, re-runs the rules against live stock
       (never a stale price) and opens the result. */
    const SHARE_OMIT = /^(zipCode|zipCodeRaw|zipCity|zipCityRaw|email|phone|name|firstname|lastname)$/i;
    function shareOmit(k) { return SHARE_OMIT.test(k) || !!(def.share && arr(def.share.omit).indexOf(k) >= 0); }
    function naturalOwners() {
      const a = answers(), nat = {};
      screens.forEach(s => {
        if (s.type === 'single' || s.type === 'multi') nat[s.key || s.id] = s.id;
        if (s.type === 'input') { let fs = []; try { fs = arr(resolve(s.fields, a)); } catch (_) {} fs.forEach(f => { ['', 'Raw', 'Unit', 'Base'].forEach(x => { nat[f.key + x] = s.id; }); }); }
      });
      screens.forEach(s => {
        [s.escape, s.skip].forEach(spec => { const action = resolve(spec, a); Object.keys(action && resolve(action.set, a) || {}).forEach(k => { if (!nat[k]) nat[k] = s.id; }); });
        if (s.type === 'input' && s.lookup && fieldsOf(s).some(f => f.key === 'zipCode')) ['zipGpg', 'zipPpm', 'zipCity', 'zipMatch', 'zipMiles', 'zipConf', 'zipApprox'].forEach(k => { nat[k] = s.id; });
      });
      return nat;
    }
    function shareValues() {
      const out = {}, values = def.share && typeof def.share.normalize === 'function' ? def.share.normalize(Object.assign({}, st.values)) : st.values;
      Object.keys(values).forEach(k => { if (!shareOmit(k)) out[k] = values[k]; });
      return out;
    }
    const b64url = t => win.btoa(unescape(encodeURIComponent(t))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const unb64url = t => decodeURIComponent(escape(win.atob(t.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((t.length + 3) % 4))));
    function shareURL() {
      const loc = win.location, q = new URLSearchParams(loc.search), keep = new URLSearchParams();
      if (q.get('view')) keep.set('view', q.get('view'));
      const a = shareValues(), nat = naturalOwners(), o = {};
      Object.keys(a).forEach(k => { if (st.owner[k] && nat[k] !== st.owner[k]) o[k] = st.owner[k]; });
      const payload = {v:1, q:quiz, a};
      payload.skips = pathScreens().filter(s => s.type === 'input' && st.done[s.id] && resolve(s.skip) && fieldsOf(s).every(f => st.values[f.key] == null)).map(s => s.id);
      const selected = selections.get('primary');
      if (selected) payload.c = selectionChoices(selected);
      if (Object.keys(o).length) payload.o = o;
      const qs = keep.toString();
      return loc.origin + loc.pathname + (qs ? '?' + qs : '') + '#m=' + b64url(JSON.stringify(payload));
    }
    function readShared() {
      let raw = null;
      try { const m = /[#&]m=([A-Za-z0-9_-]+)/.exec(win.location.hash || ''); raw = m ? m[1] : new URLSearchParams(win.location.search).get('m'); } catch (_) {}
      if (!raw) return null;
      let d = null; try { d = JSON.parse(unb64url(raw)); } catch (_) { return {invalid:true}; }
      if (!d || d.v !== 1 || d.q !== quiz || !d.a || typeof d.a !== 'object') return {invalid:true};
      const okKey = k => /^[A-Za-z][A-Za-z0-9_]{0,40}$/.test(k) && !/^(__proto__|constructor|prototype)$/.test(k) && !shareOmit(k);
      const okVal = v => (typeof v === 'string' && v.length <= 200) || (typeof v === 'number' && Number.isFinite(v)) || typeof v === 'boolean' || (Array.isArray(v) && v.length <= 30 && v.every(x => typeof x === 'string' && x.length <= 80));
      const values = {}, owner = {};
      Object.keys(d.a).forEach(k => { if (okKey(k) && okVal(d.a[k])) values[k] = d.a[k]; });
      if (d.o && typeof d.o === 'object') Object.keys(d.o).forEach(k => { if (okKey(k) && typeof d.o[k] === 'string' && byId.has(d.o[k])) owner[k] = d.o[k]; });
      return {values, owner, config:d.c, skips:arr(d.skips).filter(id => typeof id === 'string' && byId.has(id) && byId.get(id).type === 'input')};
    }
    function applyShared(d) {
      let values = d.values;
      if (def.share && typeof def.share.restore === 'function') { try { values = def.share.restore(Object.assign({}, values), ctx) || values; } catch (e) { warn('share restore failed', e); } }
      else if (values.zipGpg != null && values.zipCity == null) values = Object.assign({}, values, {zipCity:S.shareArea});
      st = fresh(); st.values = values; st.started = true; invalidate();
      const questionKeys = new Set(screens.filter(isQuestion).map(s => s.key || s.id));
      screens.forEach(s => { [s.escape, s.skip].forEach(spec => {
        const action = resolve(spec), set = action && resolve(action.set);
        Object.keys(set || {}).forEach(k => { if (!questionKeys.has(k) && values[k] !== set[k]) delete values[k]; });
      }); });
      invalidate();
      screens.filter(s => s.type === 'input').forEach(s => {
        fieldsOf(s).forEach(f => {
          if (!f.units) return;
          const v = values[f.key], u = f.units.find(x => x.value === values[f.key + 'Unit']);
          if (typeof v === 'number' && Number.isFinite(v) && u) values[f.key + 'Base'] = Math.round(v * (u.factor || 1) * 1000) / 1000;
          else delete values[f.key + 'Base'];
        });
      });
      invalidate();
      const nat = naturalOwners();
      // URL owners cannot move a real answer onto an unrelated, hidden screen and silently change sizing.
      Object.keys(values).forEach(k => { if (nat[k]) st.owner[k] = nat[k]; else delete st.values[k]; });
      invalidate();
      // A question counts as answered only when its value is one its screen allows. The first question that is not
      // loses whatever it held and the quiz opens there with the answers so far (the landing when not even the first
      // question is answered), so a link with missing, renamed or wrong-type answers never opens a result built on
      // defaults (verifier VF-5, 2026-09-27).
      let gap = null;
      for (const s of pathScreens().filter(isQuestion)) {
        if (sharedAnswerOK(s, d.skips)) { st.done[s.id] = true; continue; }
        gap = s; clearOwned(s.id); delete st.values[s.key || s.id];
        break;
      }
      invalidate();
      if (gap) { const qs = pathScreens().filter(isQuestion); return qs.some(s => st.done[s.id]) ? firstGap(gap.id) : null; }
      st.config = d.config || null;
      const res = pathScreens().find(s => s.type === 'result');
      return res ? res.id : null;
    }
    function sharedAnswerOK(s0, skips) {
      const s = materialize(s0.id) || s0, vals = st.values, key = s.key || s.id;
      const actionMatches = action => {
        const a = resolve(action), set = a && resolve(a.set), keys = Object.keys(set || {});
        return keys.length > 0 && keys.every(k => vals[k] === set[k] && st.owner[k] === s.id);
      };
      if (s.type === 'single' || s.type === 'multi') {
        const v = vals[key], opts = optionsOf(s);
        if (v == null) return actionMatches(s.escape);
        if (s.type === 'single') return typeof v === 'string' && opts.some(o => String(o.value) === v);
        if (!Array.isArray(v) || new Set(v).size !== v.length) return false;
        if (s.emptyLabel != null && ((v.length === 0 && s.emptyValue == null) || (v.length === 1 && v[0] === s.emptyValue))) return true;
        if (v.length < (s.min || 1) || !v.every(x => opts.some(o => String(o.value) === x))) return false;
        return v.length === 1 || !v.some(x => opts.some(o => String(o.value) === x && o.exclusive));
      }
      if (s.type === 'input') {
        const fs = fieldsOf(s);
        // ZIP and city are intentionally redacted. Only a coherent lookup result can complete that screen.
        if (s.lookup && fs.some(f => shareOmit(f.key))) {
          const n = k => typeof vals[k] === 'number' && Number.isFinite(vals[k]);
          return n('zipPpm') && vals.zipPpm >= 0 && vals.zipPpm <= 2565 && n('zipGpg') &&
            Math.abs(vals.zipGpg - Math.round(vals.zipPpm / 17.1 * 10) / 10) < 0.001 &&
            ['exact', 'near'].includes(vals.zipMatch) && ['high', 'medium', 'low'].includes(vals.zipConf) &&
            typeof vals.zipApprox === 'boolean' && n('zipMiles') && vals.zipMiles >= 0 && vals.zipMiles <= 50 &&
            (vals.zipMatch !== 'exact' || vals.zipMiles === 0);
        }
        let filled = 0;
        for (const f of fs) {
          const v = vals[f.key];
          if (v == null) {
            if (['Raw', 'Unit', 'Base'].some(x => vals[f.key + x] != null)) return false;
            continue;
          }
          filled++;
          if (f.text || f.pattern) {
            const re = f.pattern instanceof RegExp ? f.pattern : f.pattern ? new RegExp(f.pattern) : null;
            if (typeof v !== 'string' || (re && !re.test(v))) return false;
            continue;
          }
          if (typeof v !== 'number' || !Number.isFinite(v)) return false;
          const u = f.units ? f.units.find(x => x.value === vals[f.key + 'Unit']) : null;
          if (f.units && !u) return false;
          const min = u && u.min != null ? u.min : f.min, max = u && u.max != null ? u.max : f.max;
          if ((min != null && v < min) || (max != null && v > max) || (f.integer && !Number.isInteger(v))) return false;
          if (vals[f.key + 'Raw'] != null && number(String(vals[f.key + 'Raw'])) !== v) return false;
          if (u) vals[f.key + 'Base'] = Math.round(v * (u.factor || 1) * 1000) / 1000;
        }
        if (!filled) return arr(skips).includes(s.id) && !!resolve(s.skip) && (!resolve(s.skip).set || actionMatches(s.skip));
        return (s.require || (fs.length > 1 ? 'any' : 'all')) === 'any' || filled === fs.length;
      }
      return true;
    }
    // "Share my match" and "Send to my plumber" use the device share sheet where there is one; otherwise "Share"
    // copies the link and "Send" opens the customer's own mail app. Nothing is sent to Crystal Quest.
    async function copyText(t) {
      try { if (win.navigator.clipboard && win.isSecureContext) { await win.navigator.clipboard.writeText(t); return true; } } catch (_) {}
      try { const ta = doc.createElement('textarea'); ta.value = t; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0'; root.appendChild(ta); ta.select(); const ok = doc.execCommand('copy'); ta.remove(); return !!ok; } catch (_) { return false; }
    }
    let toastTimer = 0;
    function toast(text) {
      let t = root.querySelector('.cqq-toast');
      if (!t) { t = doc.createElement('p'); t.className = 'cqq-toast'; t.setAttribute('role', 'status'); t.setAttribute('aria-live', 'polite'); root.appendChild(t); }
      t.textContent = text; t.classList.add('is-on');
      win.clearTimeout(toastTimer); toastTimer = win.setTimeout(() => t.classList.remove('is-on'), 2400);
    }
    async function share(how, el) {
      const url = shareURL(), r = lastResult;
      const status = resultMore.querySelector('[data-cqq-share-status]');
      const say = t => { if (status && how !== 'first') status.textContent = t; else toast(t); };
      const fromFirst = how === 'first';
      const canShare = typeof win.navigator.share === 'function' && (!win.navigator.canShare || (() => { try { return win.navigator.canShare({url}); } catch (_) { return false; } })());
      if ((how === 'first' || how === 'plumber') && canShare) {
        try { await win.navigator.share({title:S.shareTitle, text:how === 'plumber' ? S.shareText : S.shareMyText, url}); track('cq_quiz_share', {method:'share', from:fromFirst ? 'first-view' : 'below', outcome:r && r.id}); return; }
        catch (e) { if (e && e.name === 'AbortError') return; }
      }
      if (how === 'plumber') {
        const href = 'mailto:?subject=' + encodeURIComponent(S.shareTitle) + '&body=' + encodeURIComponent(S.shareText + '\n\n' + url);
        track('cq_quiz_share', {method:'mailto', from:'below', outcome:r && r.id});
        win.location.href = href;
        return;
      }
      const ok = await copyText(url);
      track('cq_quiz_share', {method:ok ? 'copy' : 'copy-failed', from:fromFirst ? 'first-view' : 'below', outcome:r && r.id});
      if (ok) { say(S.linkCopied); return; }
      // the link could not be copied for them: show it, ready to copy by hand
      const box = resultMore.querySelector('[data-cqq-share-link]');
      if (box) { box.hidden = false; const inp = box.querySelector('input'); inp.value = url; say(S.copyFailed); try { box.scrollIntoView({block:'center', behavior:calm() ? 'auto' : 'smooth'}); inp.focus({preventScroll:true}); inp.select(); } catch (_) {} }
    }

    /* ---------- start moment and motion preference ---------- */
    // Round D (AJ 2026-09-28, B1 to B4). The overlay is white from its first frame: never navy, never the photo. The
    // photo and its darker scrim wait, invisible, under the logo canvas and show only through the logo's opening once
    // the canvas has drawn its first (white) frame (.is-drawn); the scrim is on the photo for the whole dive, so the
    // photo never changes darkness when the title arrives. The title reveals word by word. The entrance module and its
    // six PNG layers are prefetched on the entry screen when idle and on Start intent, only when it will play.
    const ENTRANCE_KEY = 'cqq:logo-entrance:v1:' + quiz;
    const ENTRANCE_LAYERS = ['logo', 'outer', 'body', 'mask-swoosh', 'mask-script', 'mask-white'];
    let entranceWarm = null;
    const entrancePlayed = () => { try { return win.sessionStorage.getItem(ENTRANCE_KEY) === '1'; } catch (_) { return false; } };
    function startPhoto() {
      const firstQuestion = pathScreens().find(isQuestion);
      return imgRef((chapters[0] && chapters[0].image) || (firstQuestion && photoRef(firstQuestion)));
    }
    function warmEntrance() {
      if (entranceWarm || calm() || entrancePlayed()) return;
      entranceWarm = [];
      loadScript('cq-quiz-logo-entrance.js').catch(() => {});
      // the same URLs the module requests (it resolves them through asset()), kept referenced so they stay cached
      ENTRANCE_LAYERS.forEach(name => {
        try { const im = new win.Image(); im.decoding = 'async'; im.src = asset('cq-quiz-logo-' + name + '.png'); if (im.decode) im.decode().catch(() => {}); entranceWarm.push(im); } catch (_) {}
      });
      const photo = startPhoto(); if (photo) preload(photo, '100vw');
    }
    function startQuiz(opts) {
      opts = opts || {};
      const from = st.current;
      clearTimers(); clearSaved(); saved = null; selections.clear(); lastResult = null; resultViewed = false;
      st = fresh(); st.started = true; invalidate(); st.current = from || firstId();
      const firstQuestion = pathScreens().find(isQuestion);
      const opener = def.startScreen && pathScreens().find(s => s.id === def.startScreen);
      const target = opener ? opener.id : firstQuestion ? firstQuestion.id : firstId();
      track('cq_quiz_start');
      const enter = outgoing => go(target, {history:opts.history || 'push', outgoing});
      // The lazy Wizard may already be showing its entrance while this engine loads.
      if (quiz === 'wizard' && typeof root.cqwBootStart === 'function') { root.cqwBootStart(enter); return; }
      if (calm() || instantInteraction() || (!opts.forceEntrance && entrancePlayed())) { enter(); return; }
      try { win.sessionStorage.setItem(ENTRANCE_KEY, '1'); } catch (_) {}
      warmScreen(target);
      const firstPhoto = startPhoto();
      const overlay = doc.createElement('div'); overlay.className = 'cqq-start-moment';
      if (firstPhoto) {
        overlay.style.setProperty('--cqv-q-focus', firstPhoto.focus || '50% 50%');
        overlay.style.setProperty('--cqv-q-focus-lg', firstPhoto.focusDesktop || firstPhoto.focus || '50% 50%');
      }
      overlay.setAttribute('role', 'dialog'); overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'cqq-' + quiz + '-start-title'); overlay.tabIndex = -1;
      overlay.innerHTML = (firstPhoto ? imgHTML(firstPhoto, {eager:true, cls:'cqq-start-photo', sizes:'100vw'}) : '') +
        '<div class="cqq-start-logo" aria-hidden="true"></div><h1 class="cqq-start-title" id="cqq-' + esc(quiz) + '-start-title">' + md(S.startMoment) + '</h1>';
      const behind = Array.from(root.children).map(el => ({el, inert:el.inert}));
      behind.forEach(x => { x.el.inert = true; }); root.appendChild(overlay);
      let finished = false, logoPlayer = null, messageShown = false;
      const startTimers = [];
      const delay = (fn, ms) => { const id = win.setTimeout(fn, ms); startTimers.push(id); return id; };
      const detach = () => {
        startTimers.forEach(id => win.clearTimeout(id));
        if (logoPlayer) { logoPlayer.cancel(); logoPlayer = null; }
        root.removeEventListener('click', skip, true); root.removeEventListener('keydown', skip, true);
        behind.forEach(x => { x.el.inert = x.inert; });
      };
      const remove = () => { finished = true; detach(); overlay.remove(); startMoment = null; };
      const finish = () => {
        if (finished || !startMoment) return;
        finished = true;
        detach(); startMoment = null;
        // go() clears timers; detach the start controller before it, keeping the overlay as the outgoing layer.
        overlay.inert = true; overlay.setAttribute('aria-hidden', 'true'); overlay.removeAttribute('aria-modal');
        enter(overlay);
        if (calm()) { remove(); return; }
        startMoment = {element:overlay, cancel:remove, finish:remove};
      };
      const skip = e => {
        if (e.type === 'keydown' && ['Enter', ' ', 'Spacebar', 'Escape'].indexOf(e.key) < 0) { if (e.key === 'Tab') e.preventDefault(); return; }
        e.preventDefault(); e.stopImmediatePropagation(); finish();
      };
      startMoment = {element:overlay, cancel:remove, finish};
      root.addEventListener('click', skip, true); root.addEventListener('keydown', skip, true);
      try { overlay.focus({preventScroll:true}); } catch (_) { overlay.focus(); }
      const message = () => {
        if (finished || messageShown) return;
        messageShown = true; overlay.classList.add('is-message');
        const titleMs = revealWords([overlay.querySelector('.cqq-start-title')], {timer:delay});
        delay(finish, Math.max(1050, titleMs + 250));
      };
      const fallback = () => {
        if (finished || messageShown) return;
        const mount = overlay.querySelector('.cqq-start-logo');
        mount.innerHTML = '<img class="cqq-start-logo-fallback" src="' + esc(asset('cq-quiz-logo-logo.png')) + '" alt="" width="1600" decoding="async">';
        const revealMs = cssTimeMs(win.getComputedStyle(mount).getPropertyValue('--cqq-reveal-ms'), 900);
        delay(() => { if (!finished) { mount.hidden = true; message(); } }, revealMs + 100);
      };
      // Usually already prefetched on the entry screen (warmEntrance); resume and shared matches never load it.
      const loaded = Promise.race([loadScript('cq-quiz-logo-entrance.js'), new Promise((resolve, reject) => delay(() => reject(new Error('logo timeout')), 3000))]);
      const picture = overlay.querySelector('.cqq-start-photo');
      const ready = picture && picture.decode ? Promise.race([picture.decode().catch(() => {}), new Promise(resolve => delay(resolve, 3000))]) : Promise.resolve();
      Promise.all([loaded, ready]).then(() => {
        if (finished) return;
        if (!win.CQQuizLogoEntrance) { fallback(); return; }
        try { logoPlayer = win.CQQuizLogoEntrance.play({element:overlay.querySelector('.cqq-start-logo'), picture, assetBase:asset, onComplete:message, onFirstFrame:() => { if (!finished) overlay.classList.add('is-drawn'); }}); }
        catch (_) { fallback(); }
      }, fallback);
    }
    // Prefetch on the entry screen: when the page is idle after load, and at once on Start intent.
    {
      const onLanding = () => { const s = byId.get(st.current); return !!(s && s.type === 'landing'); };
      const idleWarm = () => { if (!onLanding()) return; if (win.requestIdleCallback) win.requestIdleCallback(warmEntrance, {timeout:2500}); else win.setTimeout(warmEntrance, 1200); };
      if (doc.readyState === 'complete') win.setTimeout(idleWarm, 0); else win.addEventListener('load', idleWarm, {once:true});
      const intent = e => { const t = e.target; if (t && t.closest && t.closest('[data-cqq-do="start"]')) warmEntrance(); };
      ['pointerover', 'pointerdown', 'focusin'].forEach(type => root.addEventListener(type, intent, true));
      root.addEventListener('touchstart', intent, {capture:true, passive:true});
    }
    function syncMotion() {
      invalidate();
      if (!calm()) return;
      if (finishCounts) { if (countFrame) win.cancelAnimationFrame(countFrame); finishCounts(); }
      finishWords();
      if (startMoment) startMoment.finish();
      dropGhost();
      screenEl.querySelectorAll('.is-entering').forEach(el => el.classList.remove('is-entering'));
      const s = materialize(st.current);
      if (s && s.type === 'loader') advance({history:'replace'});
    }
    if (reduceMq.addEventListener) reduceMq.addEventListener('change', syncMotion);
    const motionObserver = win.MutationObserver ? new win.MutationObserver(syncMotion) : null;
    if (motionObserver) {
      let el = root;
      while (el) { motionObserver.observe(el, {attributes:true, attributeFilter:['data-cqv-motion-paused']}); el = el.parentElement; }
    }
    (root.closest('.shopify-section') || root).addEventListener('shopify:section:unload', () => { clearTimers(); if (motionObserver) motionObserver.disconnect(); if (reduceMq.removeEventListener) reduceMq.removeEventListener('change', syncMotion); }, {once:true});

    /* ---------- loader ---------- */
    function runLoader(s, stage) {
      const result = pathScreens().find(screen => screen.type === 'result'); if (result) warmScreen(result);
      const items = [...stage.querySelectorAll('.cqv-q-steps li')], meter = stage.querySelector('.cqv-q-meter span'), live = stage.querySelector('[data-cqq-loader-live]');
      const ms = s.stepMs || 800;
      items.forEach((li, i) => later(() => {
        li.classList.add('is-done');
        if (meter) meter.style.setProperty('--cqv-q-fill', Math.round((i + 1) / items.length * 100) + '%');
        if (live) live.textContent = li.textContent;
      }, ms * (i + 1) - 200));
      later(() => advance({history:'replace'}), ms * items.length + 250);
    }

    /* ---------- events ---------- */
    let exitSent = false;
    function onClick(e) {
      const el = e.target.closest('[data-cqq-do], [data-cqq-close], .cqv-quiz-back, a[href^="tel:"], [data-cqq-track]');
      if (!el || !root.contains(el)) return;
      if (el.matches('[data-cqq-close]')) { const d = el.closest('dialog'); if (d) d.close(); return; }
      if (el.matches('.cqv-quiz-back')) { e.preventDefault(); back(); return; }
      if (el.matches('a[href^="tel:"]')) { track('cq_quiz_call_click', {outcome:lastResult && lastResult.id}); if (!el.dataset.cqqDo) return; }
      if (el.matches('[data-cqq-track]') && !el.dataset.cqqDo) { track(el.dataset.cqqTrack, {outcome:lastResult && lastResult.id}); return; }
      if (el.getAttribute('aria-disabled') === 'true' && el.dataset.cqqDo !== 'continue') { e.preventDefault(); return; }
      const act = el.dataset.cqqDo, s = materialize(st.current), stage = screenEl.querySelector('[data-cqq-stage]');
      switch (act) {
        case 'start': startQuiz(); break;
        case 'resume': if (saved) { const target = saved.current; adopt(saved); saved = null; track('cq_quiz_resume'); go(target || firstId(), {history:'push'}); } break;
        case 'next': advance(); break;
        case 'pick': pick(el, s); break;
        case 'continue': {
          if (s.type === 'input') { inputContinue(stage, s, el); break; }
          const t = typeOf(s);
          if (t && t.commit) { const v = t.commit(stage, s, tAPI); if (v !== false) { commit(s.id, v); warmAhead(s.id); track('cq_quiz_answer', {value:s.type === 'multi' ? v[s.key || s.id] : null}); advance({goto:s.goto}); } }
          else advance();
          break;
        }
        case 'escape': case 'skip': { const a = resolve(act === 'skip' ? s.skip : s.escape); if (!a) break; clearOwned(s.id); track('cq_quiz_skip', {kind:act}); runAction(a, s.id); break; }
        case 'lookup-action': { const i = Number(el.dataset.cqqIndex); const a = (st._lookupActions || [])[i]; if (a) { track('cq_quiz_skip', {kind:'lookup'}); runAction(a, s.id); } break; }
        case 'unit': unitToggle(el, s); break;
        case 'change': openAnswers(); break;
        case 'edit': edit(el.dataset.cqqEdit); break;
        case 'retake': retake(); break;
        case 'loader-skip': track('cq_quiz_matching_skip'); clearTimers(); advance({history:'replace'}); break;
        case 'add': addToCart(el.dataset.cqqWhich || 'primary', el); break;
        case 'configure': openConfigure(el.dataset.cqqWhich || 'primary'); break;
        case 'details': openDetails(el.dataset.cqqTab); break;
        case 'alternative': openDetails('other'); break;
        case 'contact': {
          const i = Number(el.dataset.cqqIndex);
          const alt = lastResult && lastResult.details && lastResult.details.alternative;
          const a = el.dataset.cqqWhich === 'alt' ? alt && alt.sizingContact
            : el.dataset.cqqTitle ? {title:el.dataset.cqqTitle} : lastResult && arr(lastResult.actions)[i];
          openContact(a || {}); break;
        }
        case 'share': share(el.dataset.cqqShare || 'first', el); break;
        case 'email-match': openEmailMatch(); break;
        case 'tab': selectTab(el.closest('dialog'), el.dataset.cqqTab); break;
        case 'test-kit': track('cq_quiz_test_kit_click'); break;
        default: {
          const t = typeOf(s); if (t && t.action) t.action(act, el, stage, s, tAPI);
        }
      }
    }
    root.addEventListener('click', onClick);
    root.addEventListener('change', e => {
      const d = e.target.closest('dialog.cqq-configure');
      if (d && e.target.matches('select, input')) { onConfigureChange(d, e.target); return; }
      if (e.target.matches('.cqv-q-options input[type=checkbox]')) multiChange(e.target);
    });
    root.addEventListener('input', e => { if (e.target.matches('[data-cqq-field]')) inputChanged(); });
    root.addEventListener('keydown', e => {
      const t = e.target;
      if (t.matches && t.matches('[data-cqq-field]') && e.key === 'Enter') { e.preventDefault(); const btn = screenEl.querySelector('[data-cqq-do="continue"]'); if (btn) btn.click(); return; }
      const inRows = t.closest && t.closest('.cqv-q-options');
      if (inRows && ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) {
        const rows = [...inRows.querySelectorAll('button.cqv-q-option, .cqv-q-option input')];
        const i = rows.indexOf(t); if (i < 0) return;
        e.preventDefault();
        const j = e.key === 'Home' ? 0 : e.key === 'End' ? rows.length - 1 : (e.key === 'ArrowDown' || e.key === 'ArrowRight') ? Math.min(rows.length - 1, i + 1) : Math.max(0, i - 1);
        rows[j].focus();
        return;
      }
      if (inRows && e.key === 'Enter' && t.matches('input[type=checkbox]')) { e.preventDefault(); t.click(); return; }
      const tab = t.closest && t.closest('[role=tablist]');
      if (tab && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) {
        const tabs = [...tab.querySelectorAll('[role=tab]')]; const i = tabs.indexOf(t); if (i < 0) return;
        e.preventDefault();
        const j = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : e.key === 'ArrowRight' ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
        selectTab(tab.closest('dialog'), tabs[j].dataset.cqqTab);
      }
    });
    win.addEventListener('pagehide', () => { save(); if (st.started && !resultViewed && !exitSent) { exitSent = true; track('cq_quiz_exit', {last:st.current}); } });
    if (reduceMq.addEventListener) reduceMq.addEventListener('change', () => invalidate());

    /* ---------- single ---------- */
    function pick(el, s) {
      if (!s || s.type !== 'single') return;
      const value = el.dataset.cqqValue;
      screenEl.querySelectorAll('button.cqv-q-option').forEach(b => b.setAttribute('aria-pressed', String(b === el)));
      const key = s.key || s.id;
      commit(s.id, {[key]:value});
      warmAhead(s.id);
      track('cq_quiz_answer', {value});
      clearTimers();
      // Use the row-fill pause to decode the next screen, then prepare and crossfade it once.
      later(() => advance(), calm() ? 0 : 140);
    }
    /* ---------- multi ---------- */
    function multiState(stage, s) {
      const boxes = [...stage.querySelectorAll('.cqv-q-options input[type=checkbox]')];
      return boxes.filter(b => b.checked).map(b => b.value);
    }
    function multiChange(input) {
      const s = materialize(st.current); if (!s || s.type !== 'multi') return;
      const stage = screenEl.querySelector('[data-cqq-stage]');
      const boxes = [...stage.querySelectorAll('.cqv-q-options input[type=checkbox]')];
      if (input.checked) {
        const ex = input.hasAttribute('data-cqv-q-exclusive');
        boxes.forEach(b => { if (b !== input && (ex || b.hasAttribute('data-cqv-q-exclusive'))) b.checked = false; });
      }
      syncMulti(stage, s);
      const draft = derive(Object.assign({}, answers(), {[s.key || s.id]:multiState(stage, s)}));
      warmAhead(s.id, 1, draft);
    }
    function syncMulti(stage, s) {
      const btn = stage.querySelector('[data-cqq-do="continue"]'); if (!btn) return;
      const n = multiState(stage, s).length;
      const allowEmpty = s.emptyLabel != null;
      const ready = allowEmpty || n >= (s.min || 1);
      btn.setAttribute('aria-disabled', String(!ready));
      btn.innerHTML = md(n === 0 && allowEmpty ? resolve(s.emptyLabel) : resolve(s.cta) || S.continue);
      const err = stage.querySelector('[data-cqq-error]'); if (err && ready) err.hidden = true;
    }
    /* ---------- input ---------- */
    function fieldsOf(s) { return arr(resolve(s.fields)); }
    function unitOf(f, stage) { if (!f.units) return null; const el = stage.querySelector('[data-cqq-units="' + f.key + '"] [aria-pressed="true"]'); const v = el ? el.dataset.cqqUnit : f.units[0].value; return f.units.find(u => u.value === v) || f.units[0]; }
    function readField(f, stage) {
      const el = stage.querySelector('[data-cqq-field="' + f.key + '"]'); const raw = el ? el.value.trim() : '';
      const u = unitOf(f, stage);
      const min = u && u.min != null ? u.min : f.min, max = u && u.max != null ? u.max : f.max;
      if (raw === '') return {raw, empty:true, valid:false, u, min, max};
      if (f.text || f.pattern) {
        const re = f.pattern instanceof RegExp ? f.pattern : f.pattern ? new RegExp(f.pattern) : null;
        return {raw, value:raw, valid:re ? re.test(raw) : true, u, min, max};
      }
      const n = number(raw);
      const ok = n != null && (min == null || n >= min) && (max == null || n <= max) && (!f.integer || Number.isInteger(n));
      return {raw, value:n, valid:ok, u, min, max};
    }
    function inputReady(stage, s) {
      const fs = fieldsOf(s), reads = fs.map(f => readField(f, stage));
      const need = s.require || (fs.length > 1 ? 'any' : 'all');
      const filled = reads.filter(r => !r.empty);
      const bad = filled.filter(r => !r.valid);
      const ready = bad.length === 0 && (need === 'all' ? reads.every(r => r.valid) : reads.some(r => r.valid));
      return {fs, reads, ready, bad};
    }
    function inputChanged() {
      const s = materialize(st.current); if (!s || s.type !== 'input') return;
      const stage = screenEl.querySelector('[data-cqq-stage]'); const {ready} = inputReady(stage, s);
      const btn = stage.querySelector('[data-cqq-do="continue"]'); if (btn) btn.setAttribute('aria-disabled', String(!ready));
      stage.querySelectorAll('.cqv-q-field.is-invalid').forEach(f => { f.classList.remove('is-invalid'); const inp = f.querySelector('input'); if (inp) inp.removeAttribute('aria-invalid'); });
      const err = stage.querySelector('[data-cqq-error]'); if (err) err.hidden = true;
      const look = stage.querySelector('[data-cqq-lookup]'); if (look) { look.hidden = true; look.innerHTML = ''; }
    }
    function unitToggle(el, s) {
      const group = el.closest('[data-cqq-units]'); if (!group) return;
      group.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', String(b === el)));
      const f = fieldsOf(s).find(x => x.key === group.dataset.cqqUnits);
      const box = screenEl.querySelector('[data-cqq-unit-label="' + (f && f.key) + '"]'); const u = f && f.units.find(x => x.value === el.dataset.cqqUnit);
      if (box && u) box.textContent = u.label;
      inputChanged();
    }
    function inputError(stage, s, reads) {
      const firstBad = reads.findIndex(r => !r.empty && !r.valid);
      const fi = firstBad >= 0 ? firstBad : 0;
      const f = fieldsOf(s)[fi], r = reads[fi];
      let msg = resolve(typeof s.error === 'function' ? s.error(f, answers()) : s.error);
      if (!msg) msg = S.invalidDefault.replace('{min}', r && r.min != null ? r.min : 0).replace('{max}', r && r.max != null ? r.max : '');
      const err = stage.querySelector('[data-cqq-error]');
      if (err) { err.innerHTML = md(msg); err.hidden = false; }
      const box = stage.querySelectorAll('.cqv-q-field')[fi] || stage.querySelector('.cqv-q-field');
      if (box) { box.classList.add('is-invalid'); const inp = box.querySelector('input'); if (inp) { inp.setAttribute('aria-invalid', 'true'); inp.focus(); } }
      track('cq_quiz_input_invalid');
    }
    async function inputContinue(stage, s, btn) {
      const {fs, reads, ready} = inputReady(stage, s);
      if (!ready) { inputError(stage, s, reads); return; }
      const vals = {};
      fs.forEach((f, i) => {
        const r = reads[i];
        if (r.empty || !r.valid) { vals[f.key] = null; vals[f.key + 'Raw'] = null; vals[f.key + 'Unit'] = null; vals[f.key + 'Base'] = null; return; }
        vals[f.key] = r.value; vals[f.key + 'Raw'] = r.raw;
        if (f.units) { vals[f.key + 'Unit'] = r.u.value; vals[f.key + 'Base'] = typeof r.value === 'number' ? Math.round(r.value * (r.u.factor || 1) * 1000) / 1000 : null; }
      });
      if (typeof s.lookup === 'function') {
        const label = btn.innerHTML; btn.setAttribute('aria-disabled', 'true'); btn.textContent = S.lookingUp;
        let res = null;
        try { res = await s.lookup(vals, answers(), ctx); } catch (e) { warn('lookup failed', e); res = {ok:false, message:resolve(s.lookupError) || S.cartError}; }
        btn.removeAttribute('aria-disabled'); btn.innerHTML = label;
        if (st.current !== s.id) return;
        track('cq_quiz_zip_lookup', {match:res && res.match ? res.match : (res && res.ok ? 'ok' : 'miss')});
        if (!res || !res.ok) {
          const box = stage.querySelector('[data-cqq-lookup]');
          st._lookupActions = arr(res && res.actions);
          if (box) { box.innerHTML = (res && res.message ? '<p class="cqv-q-field-error" role="alert">' + md(res.message) + '</p>' : '') + st._lookupActions.map((a, i) => '<button type="button" class="cqv-text-btn" data-cqq-do="lookup-action" data-cqq-index="' + i + '">' + md(resolve(a.label)) + '</button>').join(''); box.hidden = false; }
          const field = stage.querySelector('.cqv-q-field'); if (field) field.classList.add('is-invalid');
          return;
        }
        commit(s.id, vals, res.set || null);
      } else commit(s.id, vals);
      warmAhead(s.id);
      const band = typeof s.band === 'function' ? s.band(answers(), ctx) : null;
      track('cq_quiz_answer', {entered:true, band:band || null});
      advance({goto:s.goto});
    }

    /* ---------- controller ---------- */
    const controller = {
      root, def, quiz,
      go: (id, o) => go(id, Object.assign({history:'push'}, o || {})),
      answers: () => answers(), path: () => pathIds(), state: () => JSON.parse(JSON.stringify(st)),
      result: () => lastResult, reset: () => retake(), overflow: () => lastOverflow, events: () => root.__cqqEvents || [],
      ctx, commit: (id, v) => commit(id, v), inputContinue
    };

    /* ---------- start ---------- */
    saved = load();
    const navType = (() => { try { const n = win.performance.getEntriesByType('navigation')[0]; return n && n.type; } catch (_) { return ''; } })();
    const contactState = root.querySelector('[data-cqq-contact-state]');
    const first = firstId();
    let startId = first, restored = false;
    // a shared match link (#m=...) opens the result for those answers; an unreadable one opens the landing
    const shared = contactState ? null : readShared();
    if (shared) {
      try { win.history.replaceState(win.history.state, '', win.location.pathname + win.location.search); } catch (_) {}
      if (!shared.invalid) {
        const target = applyShared(shared);
        saved = null;
        if (target) { saved = null; restored = true; startId = target; save(); track('cq_quiz_share_open'); }
        else st = fresh();
      }
    }
    if (!restored && saved && saved.current && saved.current !== first && (contactState || navType === 'reload' || navType === 'back_forward')) {
      const target = saved.current; adopt(saved); saved = null; restored = true;
      startId = target;
    }
    if (!restored && saved && !(saved.started && Object.keys(saved.values || {}).length)) saved = null;
    st.current = null;
    const deepStart = !shared && !contactState && !restored && (win.location.hash === '#start' || new URLSearchParams(win.location.search).get('start') === '1');
    if (deepStart) startQuiz({history:'replace', forceEntrance:true});
    else { go(startId, {history:'replace', focus:false, instant:true}); try { adoptServerMedia(); } catch (e) { warn('entry picture adopt failed', e); } }
    if (contactState) {
      const d = root.querySelector('dialog[data-cqq-contact]');
      if (d) {
        // after an "Email me my match" fallback, the note says the match is on its way
        let note = null; try { note = win.sessionStorage.getItem(storageKey + ':note'); win.sessionStorage.removeItem(storageKey + ':note'); } catch (_) {}
        const nb = d.querySelector('[data-cqq-contact-note]'); if (note && nb && contactState.dataset.cqqContactState === 'sent') { nb.innerHTML = md(note); nb.hidden = false; }
        bindContact(d); openDialog(d);
      }
    } else { const d = root.querySelector('dialog[data-cqq-contact]'); if (d) bindContact(d); }
    Promise.resolve().then(revealFirstView);
    instances.push(controller);
    return controller;
  }

  /* ============================== built-in screen types ============================== */
  // AJ 2026-10-11: on the landing the eyebrow is the page's h1 (it names the page) and the display headline is a styled
  // p.cqv-q-title that looks exactly as the old h1 did (assets/cq-well-v3-quiz.css, "Eyebrow h1"). Without an eyebrow the
  // headline stays the h1. The sections print the same markup for the first view.
  function landingHead(s, api) {
    if (!s.eyebrow) return api.heading(s);
    return '<h1 class="cqv-eyebrow" id="' + api.id('title') + '" tabindex="-1">' + api.text(s.eyebrow) + '</h1>' +
      '<p class="cqv-q-title">' + api.text(s.title) + '</p>';
  }
  defineType('landing', {
    screenClass:'cqv-q-landing', progress:false,
    render(s, api) {
      if (s.editorial === true) {
        const secondary = s.secondary || {label:s.secondaryCta, href:s.secondaryHref};
        const link = secondary.label && secondary.href ? '<a class="cqv-btn cqq-editorial-secondary" href="' + api.esc(secondary.href) + '">' + api.text(secondary.label) + '</a>' : '';
        return '<div class="cqq-editorial-landing">' + api.photo(s, {eager:true, cls:'cqq-editorial-media', sizes:'100vw'}) +
          '<div class="cqq-editorial-copy cqv-q-col">' + landingHead(s, api) +
          (s.lead ? '<p class="cqv-q-lead">' + api.text(s.lead) + '</p>' : '') +
          '<div class="cqq-editorial-actions">' + api.button(s.cta || api.S.start, 'start') + link + '</div>' +
          (s.note ? '<p class="cqv-q-note">' + api.text(s.note) + '</p>' : '') +
          '<button type="button" class="cqv-text-btn cqq-resume" data-cqq-do="resume" hidden>' + api.text(s.resumeLabel || api.S.resume) + '</button>' +
          (s.trust ? '<p class="cqv-q-trust">' + api.text(s.trust) + '</p>' : '') + '</div></div>';
      }
      const plan = s.plan || {};
      const items = arr(typeof plan.items === 'function' ? plan.items(api.answers) : plan.items);
      const list = (items.length ? items : api.chapters.map(c => ({name:c.name, line:c.line}))).map(i => '<li>' + api.esc(api.plain(i.name)) + '<span>' + api.esc(api.plain(i.line || '')) + '</span></li>').join('');
      const card = list ? '<aside class="cqv-q-profile' + (plan.position === 'top' ? ' is-top' : '') + '" aria-label="' + api.esc(api.plain(plan.kicker || 'Your plan')) + '"><p class="cqv-q-kicker">' + api.text(plan.kicker || 'Your plan, in four parts') + '</p><ol>' + list + '</ol></aside>' : '';
      const checks = arr(typeof s.checks === 'function' ? s.checks(api.answers) : s.checks);
      return api.photo(s, {eager:true, inner:card, sizes:'(min-width: 821px) and (orientation: landscape) 50vw, 100vw', fit:'wide'}) +
        '<div class="cqv-q-col">' + landingHead(s, api) + (s.lead ? '<p class="cqv-q-lead">' + api.text(s.lead) + '</p>' : '') +
        (checks.length ? '<ul class="cqv-checks">' + checks.map(c => '<li>' + api.text(c) + '</li>').join('') + '</ul>' : '') +
        api.foot('<button type="button" class="cqv-text-btn cqq-resume" data-cqq-do="resume" hidden>' + api.text(s.resumeLabel || api.S.resume) + '</button>' + api.button(s.cta || api.S.start, 'start') + (s.note ? '<p class="cqv-q-note">' + api.text(s.note) + '</p>' : '')) +
        (s.trust ? '<p class="cqv-q-trust">' + api.text(s.trust) + '</p>' : '') + '</div>';
    },
    bind(stage, s, api) {
      const btn = stage.querySelector('.cqq-resume');
      if (btn && api.hasResume()) btn.hidden = false;
    }
  });
  defineType('chapter', {
    screenClass:'cqv-q-chapter', photo:true,
    render(s, api) {
      const n = api.chapters.length, c = s.chapter || 1;
      const rail = api.chapters.map((ch, i) => {
        const k = i + 1;
        const state = k < c ? 'is-done' : k === c ? 'is-current' : '';
        const meta = k < c ? (api.chapterSkipped(k) ? api.S.notNeeded : api.S.done) : k === c ? api.S.now : '';
        return '<li' + (state ? ' class="' + state + '"' : '') + (k === c ? ' aria-current="step"' : '') + '>' + api.esc(api.plain(ch.name)) + (meta ? '<span class="cqv-q-rail-meta">' + api.esc(meta) + '</span>' : '') + '</li>';
      }).join('');
      // D2. compare:{items, source} swaps the chapter picture for the compare card (softener chapter 03, decision 10)
      const cmp = typeof s.compare === 'function' ? s.compare(api.answers) : s.compare;
      if (cmp && arr(cmp.items).length) {
        const tiles = arr(cmp.items).map(it => { const r = it.image ? api.imgRef(typeof it.image === 'string' ? {src:it.image} : it.image) : null;
          const wall = r && /^#[a-f\d]{6}$/i.test(r.wall) ? r.wall : '#e8ecfb';
          return '<div class="cqq-vs-tile" style="--cqq-compare-wall:' + wall + '">' + (r && r.url ? '<figure>' + api.img(r, {alt:r.alt || '', sizes:'(min-width: 821px) 300px, 45vw', now:true, priority:'high'}) + '</figure>' : '') + '<p class="cqq-vs-name">' + api.text(it.name) + '</p><ul>' + arr(it.points).map(x => '<li>' + api.text(x) + '</li>').join('') + '</ul></div>'; }).join('');
        return '<div class="cqv-q-col cqq-card-col"><div class="cqv-q-moment">' + api.chapterKicker(s) + '<div class="cqq-vs">' + api.heading(s, 'cqq-vs-title') + '<div class="cqq-vs-tiles">' + tiles + '</div></div>' +
          (cmp.source ? '<p class="cqv-q-source cqq-card-source">' + api.text(cmp.source) + '</p>' : '') + '</div>' + api.foot(api.button(s.cta || api.S.continue, 'next')) + '</div>';
      }
      return api.photo(s) + '<div class="cqv-q-col">' + api.kicker(s) + api.heading(s) + (s.lead ? '<p class="cqv-q-lead">' + api.text(s.lead) + '</p>' : '') +
        (n ? '<ol class="cqv-q-rail" aria-label="' + api.esc(api.S.railLabel) + '">' + rail + '</ol>' : '') + api.foot(api.button(s.cta || api.S.continue, 'next')) + '</div>';
    }
  });
  defineType('single', {
    question:true, photo:true,
    render(s, api) {
      const key = s.key || s.id, cur = api.value(key);
      const opts = optionsFor(s, api);
      const rows = opts.map(o => '<button type="button" class="cqv-q-option" aria-pressed="' + (cur === o.value) + '" data-cqq-do="pick" data-cqq-value="' + api.esc(o.value) + '"><span class="cqv-q-option-text"><span>' + api.text(o.label) + '</span>' + (o.help ? '<span class="cqv-q-option-help">' + api.text(o.help) + '</span>' : '') + '</span><span class="cqv-q-mark" aria-hidden="true"></span></button>').join('');
      const esc = s.escape ? api.button(typeof s.escape === 'function' ? s.escape(api.answers).label : s.escape.label, 'escape', 'cqv-text-btn') : '';
      return api.photo(s) + '<div class="cqv-q-col">' + api.kicker(s) + api.heading(s) + api.lead(s) +
        '<div class="cqv-q-options" role="group" aria-labelledby="' + api.id('title') + '"' + (s.help ? ' aria-describedby="' + api.id('help') + '"' : '') + ' data-cqv-q-single>' + rows + '</div>' + api.foot(esc) + '</div>';
    }
  });
  defineType('multi', {
    question:true, photo:true,
    render(s, api) {
      const key = s.key || s.id, cur = arr(api.value(key));
      const opts = optionsFor(s, api);
      const rows = opts.map(o => '<label class="cqv-q-option"><input type="checkbox" name="' + api.esc(key) + '" value="' + api.esc(o.value) + '"' + (cur.indexOf(o.value) >= 0 ? ' checked' : '') + (o.exclusive ? ' data-cqv-q-exclusive' : '') + '><span class="cqv-q-option-text"><span>' + api.text(o.label) + '</span>' + (o.help ? '<span class="cqv-q-option-help">' + api.text(o.help) + '</span>' : '') + '</span><span class="cqv-q-mark cqv-q-mark-box" aria-hidden="true"></span></label>').join('');
      const ticked = cur.filter(v => opts.some(o => o.value === v)).length;
      const allowEmpty = s.emptyLabel != null;
      const ready = allowEmpty || ticked >= (s.min || 1);
      const label = ticked === 0 && allowEmpty ? s.emptyLabel : (s.cta || api.S.continue);
      const compact = s.compactHelp != null ? s.compactHelp : (opts.length >= 6 || (opts.length >= 5 && s.escape));
      const escBtn = s.escape ? api.button(typeof s.escape === 'function' ? s.escape(api.answers).label : s.escape.label, 'escape', 'cqv-text-btn') : '';
      return api.photo(s) + '<div class="cqv-q-col">' + api.kicker(s) + api.heading(s) + api.lead(s, compact ? 'cqq-lead-short-hide' : '') +
        '<fieldset class="cqv-q-options" aria-labelledby="' + api.id('title') + '"' + (s.help ? ' aria-describedby="' + api.id('help') + '"' : '') + ' data-cqv-q-multi>' + rows + '</fieldset>' +
        '<p class="cqv-q-field-error" role="alert" data-cqq-error hidden></p>' +
        api.foot(escBtn + api.button(typeof label === 'function' ? label(api.answers) : label, 'continue', 'cqv-btn', ' aria-disabled="' + (!ready) + '"')) + '</div>';
    },
    commit(stage, s, api) {
      const boxes = [...stage.querySelectorAll('.cqv-q-options input[type=checkbox]')];
      const vals = boxes.filter(b => b.checked).map(b => b.value);
      if (!vals.length && s.emptyLabel == null && (s.min || 1) > 0) {
        const btn = stage.querySelector('[data-cqq-do="continue"]');
        if (btn) { btn.classList.remove('cqq-nudge'); void btn.offsetWidth; btn.classList.add('cqq-nudge'); }
        const err = stage.querySelector('[data-cqq-error]'); if (err) { err.textContent = s.required || (api && api.S.required) || 'Choose an answer to continue.'; err.hidden = false; }
        return false;
      }
      const key = s.key || s.id;
      const out = {[key]:vals.length ? vals : (s.emptyValue != null ? [s.emptyValue] : [])};
      if (typeof s.onCommit === 'function') Object.assign(out, s.onCommit(out[key]) || {});
      return out;
    }
  });
  defineType('input', {
    screenClass:'cqv-q-entry', question:true, photo:true,
    render(s, api) {
      const fs = arr(typeof s.fields === 'function' ? s.fields(api.answers) : s.fields);
      const pair = fs.length > 1;
      const field = f => {
        const id = api.id('f-' + f.key);
        const raw = api.value(f.key + 'Raw');
        const unitVal = api.value(f.key + 'Unit');
        const units = f.units ? '<div class="cqv-q-units" role="group" aria-label="Unit" data-cqq-units="' + api.esc(f.key) + '">' + f.units.map((u, i) => '<button type="button" data-cqq-do="unit" data-cqq-unit="' + api.esc(u.value) + '" aria-pressed="' + (unitVal ? unitVal === u.value : i === 0) + '">' + api.esc(u.label) + '</button>').join('') + '</div>' : '';
        const curUnit = f.units ? (f.units.find(u => u.value === unitVal) || f.units[0]).label : f.unit;
        const attrs = ' inputmode="' + api.esc(f.inputmode || (f.text ? 'text' : 'decimal')) + '" autocomplete="' + api.esc(f.autocomplete || 'off') + '"' + (f.maxlength ? ' maxlength="' + Number(f.maxlength) + '"' : '') + (f.placeholder ? ' placeholder="' + api.esc(f.placeholder) + '"' : '');
        return '<div class="cqv-q-field"><label class="cqv-q-field-label" for="' + id + '">' + api.text(f.label) + '</label><div class="cqv-q-field-row"><div class="cqv-q-field-box"><input type="text" id="' + id + '" data-cqq-field="' + api.esc(f.key) + '" value="' + api.esc(raw == null ? '' : raw) + '"' + attrs + ' aria-describedby="' + api.id('error') + (s.fact ? ' ' + api.id('fact') : '') + '">' + (curUnit && (!f.units || pair) ? '<span class="cqv-q-unit" aria-hidden="true" data-cqq-unit-label="' + api.esc(f.key) + '">' + api.esc(curUnit) + '</span>' : '') + '</div>' + (pair ? '' : units) + '</div></div>';
      };
      const fact = s.fact ? '<p class="cqv-q-fact" id="' + api.id('fact') + '">' + api.text(s.fact.text) + (s.fact.source ? '<span class="cqv-q-source">' + api.text(s.fact.source) + '</span>' : '') + '</p>' : '';
      const skip = s.skip ? api.button(typeof s.skip === 'function' ? s.skip(api.answers).label : s.skip.label, 'skip', 'cqv-text-btn') : '';
      return api.photo(s) + '<div class="cqv-q-col">' + api.kicker(s) + api.heading(s) + api.lead(s) + fact +
        '<div class="' + (pair ? 'cqq-fields' : 'cqq-fields cqq-fields-one') + '">' + fs.map(field).join('') + '</div>' +
        '<p class="cqv-q-field-error" id="' + api.id('error') + '" role="alert" data-cqq-error hidden></p><div class="cqq-lookup" data-cqq-lookup hidden></div>' +
        api.foot(skip + api.button(s.cta || api.S.continue, 'continue', 'cqv-btn', ' aria-disabled="true"')) + '</div>';
    },
    bind(stage, s) {
      const ev = new (stage.ownerDocument.defaultView.Event)('input', {bubbles:true});
      const f = stage.querySelector('[data-cqq-field]'); if (f) f.dispatchEvent(ev);
    }
  });
  function optionsFor(s, api) {
    const list = typeof s.options === 'function' ? s.options(api.answers) : s.options;
    return arr(list).map(o => typeof o === 'string' ? {value:o, label:o} : o).filter(o => o && o.value != null);
  }
  function sourceHTML(s, api) { return s.source ? '<p class="cqv-q-source">' + api.text(s.source) + '</p>' : ''; }
  function statFoot(s, api, cls) { return api.foot(api.button(s.cta || api.S.continue, 'next', cls || 'cqv-btn cqv-btn-light')); }
  /* Round B in-between cards (brief 2.1 to 2.7). A screen (or variant) picks one with `look`; without it the screen
     keeps today's typographic layout, now on its background plate. Copy fields are the same text-or-function fields
     as everywhere else. */
  const pad2 = x => (x < 10 ? '0' : '') + x;
  function lookSource(s, api) { return s.source ? '<p class="cqv-q-source cqq-card-source">' + api.text(s.source) + '</p>' : ''; }
  function lookPivot(s, api) { return s.pivot ? '<p class="cqq-card-pivot">' + api.text(s.pivot) + '</p>' : ''; }
  function lookPhoto(s, api, cls, sizes) {
    const ph = typeof s.photo === 'function' ? s.photo(api.answers) : s.photo;
    if (!ph) return '';
    const r = api.imgRef(typeof ph === 'string' ? {src:ph} : ph); if (!r || !r.url) return '';
    // the photo is on screen the moment its card renders (the engine warms it during the screen before), so it loads now
    return '<figure class="' + cls + '"' + api.focusStyle(r) + '>' + api.img(r, {alt:r.alt || '', sizes, now:true}) + '</figure>';
  }
  // USGS bands 0 to 3.5, 3.5 to 7, 7 to 10.5 and 10.5 to 21 gpg on four equal segments; above 21 pins at the end.
  // The segment follows USGS's own inclusive mg/L cuts (up to 60 soft, up to 120 moderately hard, up to 180 hard, at
  // 17.1 mg/L per gpg), so 3.5 gpg or 60 mg/L lights "Soft", the same band the words and the hardness map give.
  const USGS_GPG = [60 / 17.1, 120 / 17.1, 180 / 17.1];
  function scalePos(v) {
    const edges = [0, 3.5, 7, 10.5, 21];
    const x = Math.max(0, Number(v) || 0);
    const i = x <= USGS_GPG[0] + 1e-9 ? 0 : x <= USGS_GPG[1] + 1e-9 ? 1 : x <= USGS_GPG[2] + 1e-9 ? 2 : 3;
    const f = Math.min(1, (x - edges[i]) / (edges[i + 1] - edges[i]));
    return {band:i, pct:Math.round((i + f) * 25 * 10) / 10};
  }
  function scaleHTML(sc, api) {
    if (!sc || sc.value == null) return '';
    const S = api.S, v = Number(sc.value), p = scalePos(v), shown = api.plain(String(sc.display != null ? sc.display : (Math.round(v * 10) / 10)));
    const label = sc.label != null ? api.text(sc.label) : api.esc((sc.assumed ? S.scaleAssumed : S.scaleYou).replace('{v}', shown));
    const aria = api.esc(S.scaleAria.replace('{v}', shown).replace('{band}', S.scaleBands[p.band]));
    const names = S.scaleNames.map((n, i) => '<span' + (i === p.band ? ' class="is-on"' : '') + '><span class="cqq-narrow">' + api.esc(n) + '</span><span class="cqq-wide">' + api.esc(S.scaleWide[i] || n) + '</span></span>').join('');
    return '<div class="cqq-scale' + (sc.assumed ? ' is-assumed' : '') + '" role="img" aria-label="' + aria + '" data-x="' + p.pct + '" style="--x:' + p.pct + '%">' +
      '<div class="cqq-scale-bar" aria-hidden="true"><i></i><i></i><i></i><i></i><span class="cqq-scale-chip">' + label + '</span><span class="cqq-scale-dot"></span></div>' +
      '<div class="cqq-scale-names" aria-hidden="true">' + names + '</div><div class="cqq-scale-ticks" aria-hidden="true">' + S.scaleTicks.map(t => '<span>' + api.esc(t) + '</span>').join('') + '</div></div>';
  }
  const fmtNum = n => Number(n).toLocaleString('en-US', {maximumFractionDigits:1});
  function mathHTML(m, api) {
    if (!m) return '';
    if (typeof m === 'string') return '<p class="cqq-mine-math">' + api.text(m) + '</p>';
    const tiles = arr(m.tiles);
    const eq = tiles.length ? '<div class="cqq-eq' + (tiles.length > 4 ? ' is-long' : '') + '" role="group" aria-label="' + api.esc(tiles.map((t, i) => (i ? (t.op || '×') + ' ' : '') + api.plain(String(t.value)) + ' ' + api.plain(String(t.label || ''))).join(' ')) + '">' +
      tiles.map((t, i) => (i ? '<span class="cqq-eq-op" aria-hidden="true">' + api.esc(t.op || '×') + '</span>' : '') + '<span class="cqq-eq-tile' + (t.assumed ? ' is-assumed' : '') + '" aria-hidden="true"><b>' + api.text(String(t.value)) + '</b><span>' + api.text(String(t.label || '')) + '</span></span>').join('') + '</div>' : '';
    const c = m.capacity;
    let cap = '';
    if (c && Number(c.total) > 0) {
      const unit = c.unit || 'grains', pct = Math.max(2, Math.min(100, Math.round(Number(c.used) / Number(c.total) * 1000) / 10));
      cap = '<div class="cqq-cap"><p class="cqq-cap-row"><span>' + api.text(c.label || 'Your week') + '</span><span><b>' + api.esc(fmtNum(c.used)) + '</b> of ' + api.esc(fmtNum(c.total)) + ' ' + api.esc(unit) + '</span></p>' +
        '<div class="cqq-cap-track" aria-hidden="true"><i class="cqq-cap-fill" style="width:' + pct + '%"></i></div>' +
        (c.size ? '<p class="cqq-cap-row is-sub"><span>' + api.text(c.size) + '</span><span>' + api.esc(fmtNum(c.total)) + ' ' + api.esc(unit) + '</span></p>' : '') + '</div>';
    }
    return eq + cap;
  }
  const figHTML = (s, api, min) => '<span class="cqq-fig" data-cqq-fitline="' + (min || 34) + '">' + api.text(s.figure) + (s.unit ? '<small>' + api.text(s.unit) + '</small>' : '') + '</span>';
  const LOOKS = {
    // A. Your number: the royal card with the customer's own figure, the USGS scale or the grain math
    mine(s, api) {
      const tag = s.tag ? ' <span class="cqq-tag">' + api.text(s.tag) + '</span>' : '';
      const math = typeof s.math === 'function' ? s.math(api.answers, api) : s.math;
      const scale = typeof s.scale === 'function' ? s.scale(api.answers) : s.scale;
      return '<div class="cqv-q-col cqq-card-col"><div class="cqv-q-moment">' + api.chapterKicker(s) +
        '<div class="cqq-mine' + (math ? ' is-math' : '') + '">' + (s.kicker ? '<p class="cqq-card-kicker">' + api.text(s.kicker) + tag + '</p>' : '') +
        (s.lead ? '<p class="cqq-card-lead">' + api.text(s.lead) + '</p>' : '') +
        '<h1 class="cqq-card-head" id="' + api.id('title') + '" tabindex="-1">' + figHTML(s, api, 36) + (s.caption ? '<span class="cqq-mine-caption">' + api.text(s.caption) + '</span>' : '') + '</h1>' +
        scaleHTML(scale, api) + mathHTML(math, api) + (s.pivot ? '<p class="cqq-mine-pivot">' + api.text(s.pivot) + '</p>' : '') + '</div>' +
        lookSource(s, api) + '</div>' + api.foot(api.button(s.cta || api.S.continue, 'next', 'cqv-btn cqv-btn-light')) + '</div>';
    },
    // B. National stat: a problem photo above a navy panel (no text on the photo)
    photo(s, api) {
      // the figure may step down to 26 px, so a long figure ("Fewer than 35 percent") stays on one line on a 360 px phone
      const head = s.figure ? figHTML(s, api, 26) + (s.caption ? '<span class="cqq-pstat-caption">' + api.text(s.caption) + '</span>' : '') : '<span class="cqq-pstat-title">' + api.text(s.title) + '</span>';
      return '<div class="cqv-q-col cqq-card-col is-wide"><div class="cqv-q-moment"><div class="cqq-pstat">' + lookPhoto(s, api, 'cqq-pstat-photo', '(min-width: 821px) 500px, calc(100vw - 32px)') +
        '<div class="cqq-pstat-panel">' + (s.kicker ? '<p class="cqq-card-kicker">' + api.text(s.kicker) + '</p>' : '') + '<h1 class="cqq-card-head" id="' + api.id('title') + '" tabindex="-1">' + head + '</h1>' + (s.pivotInside ? lookPivot(s, api) : '') + '</div></div>' +
        (s.pivotInside ? '' : lookPivot(s, api)) + lookSource(s, api) + '</div>' + api.foot(api.button(s.cta || api.S.continue, 'next', 'cqv-btn cqv-btn-light')) + '</div>';
    },
    // D1. Good to know: a warm paper card with a real problem photo
    fact(s, api) {
      return '<div class="cqv-q-col cqq-card-col"><div class="cqv-q-moment"><div class="cqq-factcard"><div class="cqq-factcard-copy">' + (s.kicker ? '<p class="cqq-card-kicker">' + api.text(s.kicker) + '</p>' : '') +
        '<h1 class="cqq-factcard-title" id="' + api.id('title') + '" tabindex="-1">' + api.text(s.title) + '</h1>' + (s.pivotInside ? lookPivot(s, api) : '') + '</div>' + lookPhoto(s, api, 'cqq-factcard-photo', '(min-width: 821px) 568px, calc(100vw - 72px)') + '</div>' +
        (s.pivotInside ? '' : lookPivot(s, api)) + lookSource(s, api) + '</div>' + api.foot(api.button(s.cta || api.S.continue, 'next', 'cqv-btn cqv-btn-light')) + '</div>';
    }
  };
  const lookOf = (s, api) => (s.look && LOOKS[s.look] ? LOOKS[s.look](s, api) : null);
  defineType('stat', {
    screenClass:'cqv-q-stat',
    render(s, api) {
      return lookOf(s, api) || '<div class="cqv-q-col"><div class="cqv-q-moment">' + api.kicker(s) + '<h1 class="cqv-q-statline" id="' + api.id('title') + '" tabindex="-1"><strong class="cqv-q-figure" data-cqq-fitline="36">' + api.text(s.figure) + '</strong> <span class="cqv-q-caption">' + api.text(s.caption) + '</span></h1>' +
        (s.pivot ? '<p class="cqv-q-lead">' + api.text(s.pivot) + '</p>' : '') + sourceHTML(s, api) + '</div>' + statFoot(s, api) + '</div>';
    }
  });
  defineType('reaction', {
    screenClass:'cqv-q-stat cqq-reaction',
    render(s, api) {
      return lookOf(s, api) || '<div class="cqv-q-col"><div class="cqv-q-moment">' + api.kicker(s) + '<h1 class="cqv-q-statline" id="' + api.id('title') + '" tabindex="-1"><strong class="cqv-q-figure" data-cqq-fitline="36">' + api.text(s.figure) + '</strong> <span class="cqv-q-caption">' + api.text(s.caption) + '</span></h1>' +
        (s.math && typeof s.math !== 'object' ? '<p class="cqq-math">' + api.text(s.math) + '</p>' : '') + (s.pivot ? '<p class="cqv-q-lead">' + api.text(s.pivot) + '</p>' : '') + sourceHTML(s, api) + '</div>' + statFoot(s, api) + '</div>';
    }
  });
  defineType('fact', {
    screenClass:'cqv-q-stat cqq-fact',
    render(s, api) {
      return lookOf(s, api) || '<div class="cqv-q-col"><div class="cqv-q-moment">' + api.kicker(s) + api.heading(s) + (s.pivot ? '<p class="cqv-q-lead">' + api.text(s.pivot) + '</p>' : '') + sourceHTML(s, api) + '</div>' + statFoot(s, api) + '</div>';
    }
  });
  // C. A customer review: the white card on the light blue plate (AJ 2026-09-25: light blue replaces sage)
  defineType('testimonial', {
    screenClass:'cqv-q-quote',
    render(s, api) {
      return '<div class="cqv-q-col cqq-card-col"><div class="cqv-q-moment"><h1 class="cqv-q-kicker" id="' + api.id('title') + '" tabindex="-1">' + api.text(s.kicker || api.S.reviewKicker) + '</h1>' +
        api.review(s) + '</div>' + api.foot(api.button(s.cta || api.S.continue, 'next', 'cqv-btn')) + '</div>';
    }
  });
  defineType('playback', {
    screenClass:'cqv-q-playback',
    render(s, api) {
      const full = api.text(s.reflect), short = s.reflectShort ? api.text(s.reflectShort) : '';
      return '<div class="cqv-q-col">' + api.kicker(s) + api.heading(s) +
        '<p class="cqv-q-reflect' + (short ? ' cqq-reflect-full' : '') + '">' + full + '</p>' + (short ? '<p class="cqv-q-reflect cqq-reflect-short" aria-hidden="true">' + short + '</p>' : '') +
        (s.because ? '<p class="cqv-q-because"><strong>' + api.text(s.becauseLabel || api.S.because) + '</strong>' + api.text(s.because) + '</p>' : '') +
        api.foot(api.button(s.changeLabel || api.S.change, 'change', 'cqv-text-btn') + api.button(s.cta || api.S.playbackCta, 'next')) + '</div>';
    }
  });
  defineType('loader', {
    screenClass:'cqv-q-loader', progress:false,
    render(s, api) {
      const steps = arr(typeof s.steps === 'function' ? s.steps(api.answers) : s.steps);
      // E. "Matched for" chips, in the customer's words (at most five on phones, seven from 601 px; not links)
      const chips = arr(typeof s.chips === 'function' ? s.chips(api.answers) : s.chips).filter(Boolean).slice(0, 7);
      const chipRow = chips.length ? '<p class="cqq-chips"><span class="cqq-chips-label">' + api.text(s.chipsLabel || api.S.matchedFor) + '</span>' + chips.map(c => '<span class="cqq-chip">' + api.text(c) + '</span>').join('') + '</p>' : '';
      return '<div class="cqv-q-col"><div class="cqv-q-moment">' + api.kicker(s) + api.heading(s) + chipRow +
        '<ol class="cqv-q-steps">' + steps.map(t => '<li>' + api.text(t) + '</li>').join('') + '</ol><div class="cqv-q-meter" aria-hidden="true"><span></span></div>' +
        (s.proof ? '<p class="cqv-q-source cqv-q-loader-proof">' + api.text(s.proof) + '</p>' : '') + '<p class="cqv-sr-only" aria-live="polite" data-cqq-loader-live></p></div>' +
        api.foot(api.button(s.skipLabel || api.S.loaderSkip, 'loader-skip', 'cqv-text-btn')) + '</div>';
    }
  });
  defineType('result', {
    screenClass:'cqv-q-result', progress:false,
    render(s, api) { return api.resultHTML(s); },
    bind(stage, s, api) { api.resultBind(); }
  });

  /* The section's <noscript> copy (compare link and phone) doubles as the message when a definition cannot load. */
  function showFallback(root) {
    root.classList.remove('cqq-fonts-pending');   // the section prints it hidden (round D, D2); a failed mount shows it
    const screen = root.querySelector('[data-cqq-screen]'), ns = screen && screen.querySelector('noscript');
    if (!screen || screen.querySelector(':scope > .cqq-noscript')) return;
    const html = ns ? ns.textContent : '<div class="cqq-noscript"><p>The quiz could not load. Call a specialist at <a href="tel:18009340051">1-800-934-0051</a>.</p></div>';
    const holder = root.ownerDocument.createElement('div');
    holder.innerHTML = html.replace(/needs JavaScript/, 'could not load');
    screen.appendChild(holder.firstElementChild || holder);
  }

  /* ============================== init ============================== */
  function register(key, def) { definitions.set(key, def); init(typeof document !== 'undefined' ? document : null); }
  function init(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll('[data-cqq-quiz]').forEach(root => {
      const def = definitions.get(root.dataset.cqqQuiz);
      if (def && !mounted.has(root)) { mounted.add(root); try { mount(root, def); } catch (e) { if (typeof console !== 'undefined') console.error('[cqq] mount failed', e); showFallback(root); } }
    });
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('shopify:section:load', e => init(e.target));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  }
  return {version:VERSION, register, defineType, init, helpers, instances, types};
});
